import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, DragEvent, FormEvent, MouseEvent, ReactNode } from "react";
import type { User as FirebaseUser } from "firebase/auth";
import {
  ApiError,
  createScreenshotComment,
  cancelStripeSubscription,
  changeStripeSubscriptionPlan,
  clearAdminStripeSubscriptionState,
  createApp,
  createReviewLink,
  createStripeCheckoutSession,
  createStripeCustomerPortalSession,
  deleteApp,
  deleteReviewLink,
  deleteUploadedStoreScreenshot,
  deleteScreenshotComment,
  fetchAdminPlans,
  fetchAdminStripeConfig,
  fetchAdminStripePrices,
  fetchAdminUsers,
  getApp,
  getBillingAccount,
  getMe,
  isPaymentRequiredError,
  listBillingPlans,
  listApps,
  listReviewLinks,
  listScreenshotComments,
  listStoreScreenshots,
  mergeAnonymousReviewer,
  refreshStoreScreenshots,
  removeSavedApp,
  removeStoreScreenshot,
  resolveReviewLink,
  resolveScreenshotComment,
  saveApp,
  searchAppStore,
  searchGooglePlay,
  stackStoreScreenshot,
  syncAdminStripePrices,
  unresolveScreenshotComment,
  unstackStoreScreenshot,
  updateAdminPlanStripeProduct,
  updateAdminUserPlan,
  updateCommentSettings,
  updateAppStoreMetadata,
  updateMyCommentIdentity,
  updateMyLanguages,
  updateStoreScreenshotDescription,
  uploadStoreScreenshots,
  type UploadStoreScreenshotAssignment,
  type UploadStoreScreenshotProgress
} from "./lib/api";
import {
  clearFirebaseAuthActionFromUrl,
  completeFirebaseEmailSignInLink,
  completeFirebasePasswordReset,
  createEmailPasswordAccount,
  getFirebasePasswordResetEmail,
  getStoredFirebaseEmailLinkEmail,
  isFirebaseConfigured,
  isFirebaseEmailSignInLink,
  isFirebasePasswordResetLink,
  sendFirebaseEmailSignInLink,
  sendFirebasePasswordReset,
  signInAnonymouslyForReview,
  signInWithEmailPassword,
  signOutOfFirebase,
  subscribeToFirebaseUser
} from "./lib/firebase";
import type { AdminPlan, AdminStripeConfig, AdminStripePrice, AdminUserSummary, AppSummary, AssetVisibilityPolicy, BillingAccount, BillingPlan, BillingPlanPrice, BillingSubscription, CommentAuthorPolicy, CommentVisibilityPolicy, Platform, ReviewLinkSummary, SessionState, StoreScreenshotComment, StoreScreenshotCommentType, StoreScreenshotRefreshSummary, UserLanguage, UserProfile } from "./types";
import type { CreateAppRequest, CreateStoreScreenshotCommentRequest, StoreAppSearchResult, StoreScreenshot, UpdateAppStoreMetadataRequest } from "./types";

const storeTileSizeStorageKey = "screenshot-review.store-tile-size";
const storeGroupingModeStorageKey = "screenshot-review.store-grouping-mode.v3";
const reviewLatestOnlyStorageKey = "screenshot-review.review-latest-only";
const reviewerIntroCollapsedStorageKeyPrefix = "screenshot-review.reviewer-intro-collapsed";
const anonymousReviewerNameStorageKey = "screenshot-review.anonymous-reviewer-name";
const authDeliveryStorageKey = "screenshot-review.auth-delivery";
const commentDraftsStorageKey = "screenshot-review.comment-drafts.v1";
const commentDraftAuthHandoffStorageKey = "screenshot-review.comment-draft-auth-handoff.v1";
const commentDraftGuestIdStorageKey = "screenshot-review.comment-draft-guest-id";
const commentDraftTtlMs = 24 * 60 * 60 * 1000;
const commentDraftAutosaveDelayMs = 300;
const maxStoredCommentDrafts = 100;
const storeLocalesSearchParam = "locales";
const legacyStoreLocalesSearchParam = "l";
const storeScreenshotFilterHistoryStateKey = "screenshotReviewStoreFilters";
const reviewLinkPayloadSearchParam = "r";
const reviewLinkMessageMaxLength = 180;
const googlePlayRegularTileWidth = 900;
const googlePlaySmallTileWidth = 640;
const googlePlayDisplayWidth = 1440;
type StoreTileSize = "regular" | "small";
type StoreGroupingMode = "position" | "locale";
type StorePlatformFilter = "ALL" | "APP_STORE" | "PLAY_STORE";
type CommentDraftKind = "comment" | "needsWork" | "reply";
type LegacyReviewLinkCropFocus = "top" | "center" | "bottom";
type ReviewLinkCropFocus = number;
type AuthMode = "signin" | "signup";
type AuthDelivery = "password" | "link";
type FloatingNotification = {
  id: number;
  text: string;
};
type FloatingNotificationDispatcher = (message: string) => void;
type ImageDimensions = {
  width: number;
  height: number;
};
type ImageDimensionsById = Record<string, ImageDimensions>;
interface ReviewLinkConfig {
  locales: string[];
  cropFocus: ReviewLinkCropFocus;
  message: string;
  welcomePosition: number | null;
  welcomeGroupKey: string | null;
  welcomeLocale: string | null;
}
interface ReviewLinkPayload {
  v: 1;
  ls: string[];
  f?: ReviewLinkCropFocus | LegacyReviewLinkCropFocus;
  m?: string | null;
  p?: number | null;
  g?: string | null;
  wl?: string | null;
}
interface StoredCommentDraft {
  identityScope: string;
  appId: string;
  screenshotId: string;
  kind: CommentDraftKind;
  parentCommentId?: string | null;
  body: string;
  updatedAt: number;
  expiresAt: number;
}
interface StoredCommentDraftAuthHandoff {
  sourceIdentityScope: string;
  appId: string;
  screenshotId: string;
  kind: CommentDraftKind;
  parentCommentId?: string | null;
  createdAt: number;
  expiresAt: number;
}
interface StoreScreenshotFilterHistoryState {
  appId: string;
  selectedLocales: string[];
  platformFilter: StorePlatformFilter;
  commentedOnly: boolean;
  needsWorkOnly: boolean;
  unresolvedOnly: boolean;
  updatedSince: string;
}
type DropdownPortalPlacement = "top" | "bottom";
type DropdownPortalAlign = "start" | "end" | "center";
type DropdownPortalPosition = {
  left: number;
  top: number;
  width?: number;
  maxHeight: number;
  placement: DropdownPortalPlacement;
};

const FLOATING_NOTIFICATION_AUTO_DISMISS_MS = 6_000;
const MAX_FLOATING_NOTIFICATIONS = 5;
const FloatingNotificationContext = createContext<FloatingNotificationDispatcher>(() => undefined);
const defaultReviewLinkCropFocus: ReviewLinkCropFocus = 0;
const publicApiContext = {
  getIdToken: async () => null
};
const phoneAutoFocusMaxWidth = 620;
let modalScrollLockCount = 0;
let modalScrollLockSnapshot: {
  bodyOverflow: string;
  bodyPaddingRight: string;
  bodyPosition: string;
  bodyTop: string;
  bodyWidth: string;
  htmlOverflow: string;
  scrollY: number;
} | null = null;

function useModalScrollLock() {
  useLayoutEffect(() => {
    lockModalPageScroll();
    return unlockModalPageScroll;
  }, []);
}

function ModalScrollLock() {
  useModalScrollLock();
  return null;
}

function useFloatingNotification(): FloatingNotificationDispatcher {
  return useContext(FloatingNotificationContext);
}

function shouldAutoFocusDropdownSearch(): boolean {
  return window.innerWidth > phoneAutoFocusMaxWidth;
}

function focusDropdownSearchInput(input: HTMLInputElement | null) {
  if (!input || !shouldAutoFocusDropdownSearch()) {
    return;
  }
  window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
}

function lockModalPageScroll() {
  if (modalScrollLockCount === 0) {
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY;
    const scrollbarGap = Math.max(0, window.innerWidth - html.clientWidth);
    modalScrollLockSnapshot = {
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
      scrollY
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    if (scrollbarGap > 0) {
      body.style.paddingRight = `${scrollbarGap}px`;
    }
  }
  modalScrollLockCount += 1;
}

function unlockModalPageScroll() {
  modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);
  if (modalScrollLockCount > 0 || !modalScrollLockSnapshot) {
    return;
  }

  const snapshot = modalScrollLockSnapshot;
  modalScrollLockSnapshot = null;
  const body = document.body;
  const html = document.documentElement;
  html.style.overflow = snapshot.htmlOverflow;
  body.style.overflow = snapshot.bodyOverflow;
  body.style.paddingRight = snapshot.bodyPaddingRight;
  body.style.position = snapshot.bodyPosition;
  body.style.top = snapshot.bodyTop;
  body.style.width = snapshot.bodyWidth;
  window.scrollTo(0, snapshot.scrollY);
}

function App() {
  const [session, setSession] = useState<SessionState | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(readAppIdFromUrl);
  const [removingSavedAppId, setRemovingSavedAppId] = useState<string | null>(null);
  const [deletingAppId, setDeletingAppId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [localeSelectionResetAppId, setLocaleSelectionResetAppId] = useState<string | null>(null);
  const notify = useCallback((nextMessage: string) => setMessage(nextMessage), []);
  const dismissNotificationMessage = useCallback(() => setMessage(null), []);
  const renderWithNotifications = (content: ReactNode) => (
    <FloatingNotificationContext.Provider value={notify}>
      {content}
      <FloatingNotifications message={message} onDismiss={dismissNotificationMessage} />
    </FloatingNotificationContext.Provider>
  );

  useEffect(() => {
    if (!isFirebaseConfigured()) {
      setAuthReady(true);
      return;
    }

    return subscribeToFirebaseUser((firebaseUser) => {
      if (!firebaseUser) {
        setSession(null);
        setUser(null);
        setApps([]);
        setSelectedAppId(null);
        setLocaleSelectionResetAppId(null);
        setWorkspaceReady(false);
        setAuthReady(true);
        return;
      }

      setWorkspaceReady(false);
      setSession(createFirebaseSession(firebaseUser));
      setAuthReady(true);
    });
  }, []);

  useEffect(() => {
    if (!session) {
      setWorkspaceReady(false);
      return;
    }

    let cancelled = false;
    const requestedSharedAppId = readAppIdFromUrl();
    setWorkspaceReady(false);
    setLoading(true);
    if (session.isAnonymous) {
      if (!requestedSharedAppId) {
        setUser(null);
        setApps([]);
        setSelectedAppId(null);
        setWorkspaceReady(true);
        setLoading(false);
        return () => {
          cancelled = true;
        };
      }
      getMe(session)
        .then((profile) => {
          if (!cancelled) {
            setUser(profile);
            setApps([]);
            setSelectedAppId(null);
          }
        })
        .catch((error: Error) => {
          if (!cancelled) {
            setMessage(error.message);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setWorkspaceReady(true);
            setLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }

    Promise.all([getMe(session), listApps(session)])
      .then(async ([profile, appList]) => {
        const requested = readAppIdFromUrl();
        let nextApps = appList;
        if (requested && !appList.some((app) => app.id === requested)) {
          try {
            const savedSharedApp = await saveApp(requested, session);
            nextApps = upsertAppSummary(appList, savedSharedApp);
          } catch (sharedError) {
            if (!cancelled) {
              setMessage(sharedError instanceof Error ? sharedError.message : "Failed to load shared workspace.");
            }
          }
        }
        if (cancelled) {
          return;
        }
        setUser(profile);
        setApps(nextApps);
        setSelectedAppId((current) => {
          const selected = current ?? requested;
          return selected && nextApps.some((app) => app.id === selected) ? selected : nextApps[0]?.id ?? null;
        });
      })
      .catch((error: Error) => setMessage(error.message))
      .finally(() => {
        if (!cancelled) {
          setWorkspaceReady(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const selectedApp = useMemo(
    () => apps.find((app) => app.id === selectedAppId) ?? apps[0] ?? null,
    [apps, selectedAppId]
  );

  useEffect(() => {
    function onPopState() {
      setSelectedAppId(readAppIdFromUrl());
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (selectedApp) {
      writeAppIdToUrl(selectedApp.id);
    }
  }, [selectedApp?.id]);

  async function handleCreateApp(request: CreateAppRequest) {
    if (!session) {
      return;
    }

    setLoading(true);
    try {
      const created = await createApp(request, session);
      const nextApps = await listApps(session);
      setApps(nextApps);
      setSelectedAppId(created.id);
      setLocaleSelectionResetAppId(created.id);
      setMessage(`Created ${created.name}.`);
    } catch (error) {
      if (isPaymentRequiredError(error)) {
        setShowSubscription(true);
      }
      setMessage(error instanceof Error ? error.message : "Failed to create app.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveSavedApp(appId: string) {
    if (!session) {
      return;
    }

    const removedApp = apps.find((app) => app.id === appId);
    setRemovingSavedAppId(appId);
    try {
      await removeSavedApp(appId, session);
      const nextApps = apps.filter((app) => app.id !== appId);
      const nextSelectedAppId = selectedAppId === appId ? nextApps[0]?.id ?? null : selectedAppId;
      setApps(nextApps);
      setSelectedAppId(nextSelectedAppId);
      if (!nextSelectedAppId) {
        writeAppIdToUrl(null);
      }
      setMessage(`Removed ${removedApp?.name ?? "app"} from review queue.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to remove app from review queue.");
    } finally {
      setRemovingSavedAppId(null);
    }
  }

  async function handleDeleteApp(appId: string, confirmationName: string) {
    if (!session) {
      return;
    }

    const deletedApp = apps.find((app) => app.id === appId);
    setDeletingAppId(appId);
    try {
      await deleteApp(appId, confirmationName, session);
      const nextApps = apps.filter((app) => app.id !== appId);
      const nextSelectedAppId = selectedAppId === appId ? nextApps[0]?.id ?? null : selectedAppId;
      setApps(nextApps);
      setSelectedAppId(nextSelectedAppId);
      if (!nextSelectedAppId) {
        writeAppIdToUrl(null);
      }
      setMessage(`Deleted ${deletedApp?.name ?? "app"}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to delete app.");
      throw error;
    } finally {
      setDeletingAppId(null);
    }
  }

  async function handleUpdateStoreMetadata(appId: string, request: UpdateAppStoreMetadataRequest): Promise<AppSummary> {
    if (!session) {
      throw new Error("Not signed in.");
    }

    const updated = await updateAppStoreMetadata(appId, request, session);
    setApps((current) => current.map((app) => (app.id === updated.id ? updated : app)));
    setMessage("Saved app details.");
    return updated;
  }

  async function handleUpdateCommentSettings(appId: string, request: {
    commentAuthorPolicy: AppSummary["commentAuthorPolicy"];
    commentVisibilityPolicy: AppSummary["commentVisibilityPolicy"];
    commentReplyVisibilityPolicy: AppSummary["commentReplyVisibilityPolicy"];
  }): Promise<AppSummary> {
    if (!session) {
      throw new Error("Not signed in.");
    }

    const updated = await updateCommentSettings(appId, request, session);
    setApps((current) => current.map((app) => (app.id === updated.id ? updated : app)));
    setMessage("Saved comment settings.");
    return updated;
  }

  async function handleUpdateProfile(
    displayName: string | null,
    shareEmailWithAppOwner: boolean,
    nativeLanguages: string[],
    proficientLanguages: string[]
  ): Promise<UserProfile> {
    if (!session) {
      throw new Error("Not signed in.");
    }

    const normalizedDisplayName = displayName?.trim() || null;
    let identityProfile: UserProfile | null = null;
    if ((user?.displayName ?? null) !== normalizedDisplayName || (user?.shareEmailWithAppOwner ?? false) !== shareEmailWithAppOwner) {
      identityProfile = await updateMyCommentIdentity(
        {
          displayName: normalizedDisplayName,
          shareEmailWithAppOwner
        },
        session
      );
    }

    const updated = await updateMyLanguages({ nativeLanguages, proficientLanguages }, session);
    const merged = {
      ...updated,
      displayName: identityProfile ? identityProfile.displayName : updated.displayName,
      reviewerIdentityConfigured: identityProfile ? identityProfile.reviewerIdentityConfigured : updated.reviewerIdentityConfigured,
      shareEmailWithAppOwner: identityProfile ? identityProfile.shareEmailWithAppOwner : updated.shareEmailWithAppOwner
    };
    setUser(merged);
    setMessage("Saved profile.");
    return merged;
  }

  if (isLocalOgPreviewRoute()) {
    return <LocalOgImagePreview />;
  }

  if (!authReady) {
    return renderWithNotifications(<SplashScreen />);
  }

  const shortReviewLinkCode = readShortReviewLinkCodeFromUrl();
  if (shortReviewLinkCode) {
    return renderWithNotifications(<ShortReviewLinkRedirect code={shortReviewLinkCode} />);
  }

  const sharedAppId = readAppIdFromUrl();
  if (!session || session.isAnonymous) {
    if (sharedAppId) {
      return renderWithNotifications(
        <PublicSharedWorkspace
          appId={sharedAppId}
          session={session ?? undefined}
          currentUser={session?.isAnonymous ? user : null}
          onSessionUpdated={setSession}
          onUserUpdated={setUser}
        />
      );
    }
    return renderWithNotifications(<AuthScreen anonymousMigrationAvailable={session?.isAnonymous ?? false} />);
  }

  return renderWithNotifications(
    <div className="page-shell">
      <TopBar
        apps={apps}
        removingSavedAppId={removingSavedAppId}
        deletingAppId={deletingAppId}
        selectedAppId={selectedApp?.id ?? null}
        session={session}
        userEmail={user?.email ?? session.email ?? "Account"}
        user={user}
        loading={loading}
        onSelectApp={setSelectedAppId}
        onCreateApp={handleCreateApp}
        onRemoveSavedApp={handleRemoveSavedApp}
        onDeleteApp={handleDeleteApp}
        onUpdateApp={handleUpdateStoreMetadata}
        onUpdateProfile={handleUpdateProfile}
        onOpenCreateApp={() => setShowCreateAppModal(true)}
        onOpenSubscription={() => setShowSubscription(true)}
        onSignOut={async () => {
          await session.signOut();
          clearWorkspaceUrlState();
          setSession(null);
          setUser(null);
          setApps([]);
          setSelectedAppId(null);
          setLocaleSelectionResetAppId(null);
          setWorkspaceReady(false);
          setShowSubscription(false);
          setShowCreateAppModal(false);
        }}
      />

      <main className="workspace-shell">
        {!workspaceReady ? (
          <SplashScreen />
        ) : selectedApp ? (
          selectedApp.relationship === "OWNER" ? (
            <OwnedAppWorkspace
              key={selectedApp.id}
              app={selectedApp}
              session={session}
              user={user}
              onUserUpdated={setUser}
              localeSelectionResetAppId={localeSelectionResetAppId}
              onLocaleSelectionReset={() => setLocaleSelectionResetAppId(null)}
              onOpenSubscription={() => setShowSubscription(true)}
            />
          ) : (
            <SavedAppWorkspace
              key={selectedApp.id}
              app={selectedApp}
              session={session}
              user={user}
              onUserUpdated={setUser}
            />
          )
        ) : (
          <EmptyWorkspace onCreateApp={() => setShowCreateAppModal(true)} />
        )}
      </main>
      {showCreateAppModal ? (
        <AppDetailsModal
          app={null}
          context={session}
          loading={loading}
          mode="add"
          onClose={() => setShowCreateAppModal(false)}
          onCreate={handleCreateApp}
          onUpdate={async () => undefined}
        />
      ) : null}
      {showSubscription ? (
        <SubscriptionModal
          context={session}
          onClose={() => setShowSubscription(false)}
        />
      ) : null}
    </div>
  );
}

function readAppIdFromUrl(): string | null {
  const value = new URLSearchParams(window.location.search).get("appId")?.trim();
  return value && isUuid(value) ? value : null;
}

function readShortReviewLinkCodeFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/~([0-9A-Za-z]{6,})\/?$/);
  return match?.[1] ?? null;
}

function isLocalOgPreviewRoute(): boolean {
  const path = window.location.pathname.replace(/\/+$/, "");
  return path === "/local/og-preview" && isLocalPreviewHost(window.location.hostname);
}

function isLocalPreviewHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname === "";
}

function readReviewScreenshotIdFromUrl(): string | null {
  const value = new URLSearchParams(window.location.search).get("screenshotId")?.trim();
  return value && isUuid(value) ? value : null;
}

function writeAppIdToUrl(appId: string | null) {
  const url = new URL(window.location.href);
  if (appId) {
    if (url.searchParams.get("appId") === appId) {
      return;
    }
    url.searchParams.set("appId", appId);
  } else {
    if (!url.searchParams.has("appId")) {
      return;
    }
    url.searchParams.delete("appId");
  }
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function upsertAppSummary(apps: AppSummary[], app: AppSummary): AppSummary[] {
  return apps.some((candidate) => candidate.id === app.id)
    ? apps.map((candidate) => (candidate.id === app.id ? app : candidate))
    : [app, ...apps];
}

function writeReviewScreenshotIdToUrl(screenshotId: string | null, options: { replace?: boolean } = {}) {
  const url = new URL(window.location.href);
  if (screenshotId) {
    if (url.searchParams.get("screenshotId") === screenshotId) {
      return;
    }
    url.searchParams.set("screenshotId", screenshotId);
  } else {
    if (!url.searchParams.has("screenshotId")) {
      return;
    }
    url.searchParams.delete("screenshotId");
  }
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  if (options.replace === false) {
    window.history.pushState(window.history.state, "", nextUrl);
  } else {
    window.history.replaceState(window.history.state, "", nextUrl);
  }
}

function clearWorkspaceUrlState() {
  window.history.replaceState(window.history.state, "", "/");
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function createPublicSession(): SessionState {
  return {
    mode: "public",
    email: null,
    isAnonymous: false,
    getIdToken: async () => null,
    signOut: async () => undefined
  };
}

function createFirebaseSession(firebaseUser: FirebaseUser): SessionState {
  return {
    mode: "firebase",
    email: firebaseUser.email,
    isAnonymous: firebaseUser.isAnonymous,
    getIdToken: () => firebaseUser.getIdToken(),
    signOut: signOutOfFirebase
  };
}

function DropdownPortal(props: {
  anchorRef: { current: HTMLElement | null };
  open: boolean;
  children: ReactNode;
  className?: string;
  align?: DropdownPortalAlign;
  matchAnchorWidth?: boolean;
  minWidth?: number;
  onDismiss?: () => void;
  onMouseLeave?: () => void;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  role?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<DropdownPortalPosition | null>(null);
  const align = props.align ?? "end";

  const updatePosition = useCallback(() => {
    const anchor = props.anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 8;
    const minimumPanelHeight = 48;
    const visualViewport = window.visualViewport;
    const viewportLeft = visualViewport?.offsetLeft ?? 0;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportWidth = visualViewport?.width ?? window.innerWidth;
    const viewportHeight = visualViewport?.height ?? window.innerHeight;
    const viewportRight = viewportLeft + viewportWidth;
    const viewportBottom = viewportTop + viewportHeight;
    const visibleLeft = viewportLeft + viewportPadding;
    const visibleTop = viewportTop + viewportPadding;
    const visibleRight = viewportRight - viewportPadding;
    const visibleBottom = viewportBottom - viewportPadding;
    const visibleWidth = Math.max(160, visibleRight - visibleLeft);
    const visibleHeight = Math.max(minimumPanelHeight, visibleBottom - visibleTop);
    const availableWidth = Math.max(160, viewportWidth - viewportPadding * 2);
    const naturalWidth = props.matchAnchorWidth
      ? Math.max(anchorRect.width, props.minWidth ?? 0)
      : Math.max(panelRect.width, props.minWidth ?? 0);
    const width = Math.min(naturalWidth, availableWidth, visibleWidth);
    const availableBelow = Math.max(0, visibleBottom - anchorRect.bottom - gap);
    const availableAbove = Math.max(0, anchorRect.top - visibleTop - gap);
    const openAbove = availableAbove > availableBelow;
    const selectedAvailableHeight = openAbove ? availableAbove : availableBelow;
    const availableHeight = Math.max(
      minimumPanelHeight,
      Math.min(visibleHeight, Math.floor(selectedAvailableHeight || visibleHeight))
    );
    const renderedHeight = Math.min(panelRect.height, availableHeight);

    let left = anchorRect.right - width;
    if (align === "start") {
      left = anchorRect.left;
    } else if (align === "center") {
      left = anchorRect.left + anchorRect.width / 2 - width / 2;
    }
    left = Math.max(visibleLeft, Math.min(left, visibleRight - width));

    const preferredTop = openAbove
      ? anchorRect.top - gap - renderedHeight
      : anchorRect.bottom + gap;
    const top = Math.max(visibleTop, Math.min(preferredTop, visibleBottom - renderedHeight));

    setPosition({
      left,
      top,
      width: props.matchAnchorWidth || width < naturalWidth ? width : undefined,
      maxHeight: availableHeight,
      placement: openAbove ? "top" : "bottom"
    });
  }, [align, props.anchorRef, props.matchAnchorWidth, props.minWidth]);

  useLayoutEffect(() => {
    if (!props.open) {
      setPosition(null);
      return;
    }

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frame);
  }, [props.open, props.children, updatePosition]);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        props.onDismiss?.();
      }
    }

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    document.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [props.open, props.onDismiss, updatePosition]);

  if (!props.open || typeof document === "undefined") {
    return null;
  }

  const panelStyle: CSSProperties = position
    ? {
        left: position.left,
        top: position.top,
        right: "auto",
        bottom: "auto",
        width: position.width,
        maxHeight: position.maxHeight
      }
    : {
        left: -10_000,
        top: -10_000,
        right: "auto",
        bottom: "auto",
        visibility: "hidden"
      };

  return createPortal(
    <>
      <div
        className="menu-portal-backdrop"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onDismiss?.();
        }}
        onTouchStart={(event) => {
          event.preventDefault();
          event.stopPropagation();
          props.onDismiss?.();
        }}
      />
      <div
        ref={panelRef}
        className={`menu-panel menu-portal-panel ${position?.placement === "top" ? "menu-panel-top" : ""} ${props.className ?? ""}`.trim()}
        role={props.role}
        style={panelStyle}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onMouseLeave={props.onMouseLeave}
        onPointerDown={(event) => event.stopPropagation()}
        onScroll={props.onScroll}
        onTouchStart={(event) => event.stopPropagation()}
      >
        {props.children}
      </div>
    </>,
    document.body
  );
}

function FloatingNotifications(props: { message: string | null; onDismiss: () => void }) {
  const [notifications, setNotifications] = useState<FloatingNotification[]>([]);
  const [paused, setPaused] = useState(false);
  const previousMessageRef = useRef<string | null>(null);
  const nextNotificationIdRef = useRef(1);

  useEffect(() => {
    if (!props.message) {
      previousMessageRef.current = null;
      return;
    }
    if (previousMessageRef.current === props.message) {
      return;
    }

    const text: string = props.message;
    previousMessageRef.current = text;
    setNotifications((currentNotifications) => [
      { id: nextNotificationIdRef.current++, text },
      ...currentNotifications
    ].slice(0, MAX_FLOATING_NOTIFICATIONS));
    props.onDismiss();
  }, [props.message, props.onDismiss]);

  function dismissNotification(notificationId: number) {
    setNotifications((currentNotifications) =>
      currentNotifications.filter((notification) => notification.id !== notificationId)
    );
  }

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div
      className="floating-notification-stack"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {notifications.map((notification) => (
        <FloatingNotificationBanner
          key={notification.id}
          notification={notification}
          paused={paused}
          onDismiss={() => dismissNotification(notification.id)}
        />
      ))}
    </div>
  );
}

function FloatingNotificationBanner(props: {
  notification: FloatingNotification;
  paused: boolean;
  onDismiss: () => void;
}) {
  const [progress, setProgress] = useState(0);
  const [autoDismissCancelled, setAutoDismissCancelled] = useState(false);
  const remainingMsRef = useRef(FLOATING_NOTIFICATION_AUTO_DISMISS_MS);
  const dismissRef = useRef(props.onDismiss);

  useEffect(() => {
    dismissRef.current = props.onDismiss;
  }, [props.onDismiss]);

  useEffect(() => {
    remainingMsRef.current = FLOATING_NOTIFICATION_AUTO_DISMISS_MS;
    setAutoDismissCancelled(false);
    setProgress(0);
  }, [props.notification.id]);

  useEffect(() => {
    if (props.paused || autoDismissCancelled) {
      return;
    }

    const startedAt = Date.now();
    const startingRemainingMs = remainingMsRef.current;
    const updateProgress = () => {
      const elapsedMs = Date.now() - startedAt;
      const nextRemainingMs = Math.max(startingRemainingMs - elapsedMs, 0);
      setProgress(1 - nextRemainingMs / FLOATING_NOTIFICATION_AUTO_DISMISS_MS);
    };
    const progressInterval = window.setInterval(updateProgress, 80);
    const dismissTimeout = window.setTimeout(() => {
      remainingMsRef.current = 0;
      setProgress(1);
      dismissRef.current();
    }, startingRemainingMs);

    updateProgress();

    return () => {
      window.clearInterval(progressInterval);
      window.clearTimeout(dismissTimeout);
      const elapsedMs = Date.now() - startedAt;
      remainingMsRef.current = Math.max(startingRemainingMs - elapsedMs, 0);
    };
  }, [autoDismissCancelled, props.paused, props.notification.id]);

  return (
    <div className="message-banner floating-notification" onClick={() => setAutoDismissCancelled(true)}>
      <span className="message-banner-text">{props.notification.text}</span>
      <button
        aria-label="Dismiss notification"
        className="message-banner-dismiss notification-dismiss-progress"
        type="button"
        style={{ "--notification-progress": `${progress * 360}deg` } as CSSProperties}
        onClick={(event) => {
          event.stopPropagation();
          props.onDismiss();
        }}
      >
        <XIcon />
      </button>
    </div>
  );
}

function SplashScreen() {
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <p className="eyebrow">Screenshot Review</p>
        <h1>Preparing workspace</h1>
      </section>
    </main>
  );
}

function LocalOgImagePreview() {
  return (
    <main className="local-og-preview-page" aria-label="Local Open Graph image preview">
      <section className="local-og-preview-canvas">
        <div className="local-og-preview-brand">
          <span className="local-og-preview-brand-mark"><ScreenshotReviewLogo /></span>
          <strong>SCREENSHOT.REVIEW</strong>
        </div>
        <div className="local-og-preview-copy">
          <h1>
            <span>Launch app <span className="local-og-preview-title-screenshots">screenshots</span></span>
            <span>with <span className="local-og-preview-title-native">native-speaker</span> feedback</span>
          </h1>
        </div>
        <div className="local-og-preview-visual" aria-hidden="true">
          <div className="local-og-preview-shot local-og-preview-review-shot">
            <div className="local-og-preview-shot-label">Under review</div>
            <div className="local-og-preview-phone">
              <div className="local-og-preview-shot-title">
                Reisen
                <span>Gespeicherte Plätze</span>
              </div>
              <div className="local-og-preview-device" />
            </div>
            <div className="local-og-preview-feedback">
              <span>Needs work</span>
              <strong>“Gespeicherte Plätze” sounds unnatural. Try “Gespeicherte Orte” instead.</strong>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function AuthScreen(props: {
  anonymousMigrationAvailable?: boolean;
}) {
  const firebaseConfigured = isFirebaseConfigured();
  const notify = useFloatingNotification();
  const supportEmail = "support@screenshot.review";
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [authModalInitialMode, setAuthModalInitialMode] = useState<AuthMode>("signup");
  const [supportEmailCopied, setSupportEmailCopied] = useState(false);
  const supportEmailCopiedTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (firebaseConfigured && (isFirebaseEmailSignInLink() || isFirebasePasswordResetLink())) {
      setAuthModalInitialMode("signin");
      setShowAuthModal(true);
    }
  }, [firebaseConfigured]);

  useEffect(() => () => {
    if (supportEmailCopiedTimeoutRef.current !== null) {
      window.clearTimeout(supportEmailCopiedTimeoutRef.current);
    }
  }, []);

  const setupSteps = [
    {
      title: "Add app by name",
      copy: "Find your App Store or Google Play listing."
    },
    {
      title: "Sync already published artwork",
      note: "or upload new candidates",
      copy: "Pull store screenshots into one workspace, organized by position and locale."
    },
    {
      title: "Collect native speaker feedback",
      copy: "Share personalized links with native speakers or post them for wider audience."
    }
  ];

  function scrollToLandingSection(event: MouseEvent<HTMLAnchorElement>, sectionId: string) {
    event.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }

  function openAuthModal(initialMode: AuthMode) {
    setAuthModalInitialMode(initialMode);
    setShowAuthModal(true);
  }

  async function copySupportEmail() {
    try {
      await copyTextToClipboard(supportEmail);
      setSupportEmailCopied(true);
      if (supportEmailCopiedTimeoutRef.current !== null) {
        window.clearTimeout(supportEmailCopiedTimeoutRef.current);
      }
      supportEmailCopiedTimeoutRef.current = window.setTimeout(() => {
        setSupportEmailCopied(false);
        supportEmailCopiedTimeoutRef.current = null;
      }, 2_000);
    } catch {
      notify("Failed to copy support email.");
    }
  }

  return (
    <main className="owner-landing-shell">
      <header className="owner-landing-top-bar">
        <div className="brand-lockup">
          <span className="brand-mark"><ScreenshotReviewLogo /></span>
          <strong>SCREENSHOT.REVIEW</strong>
        </div>
        <div className="owner-landing-top-actions">
          <a className="text-button owner-landing-nav-link" href="#how-it-works" onClick={(event) => scrollToLandingSection(event, "how-it-works")}>
            Setup
          </a>
          <a className="text-button owner-landing-nav-link" href="#reviewer-experience" onClick={(event) => scrollToLandingSection(event, "reviewer-experience")}>
            Reviewer experience
          </a>
          <button className="text-button owner-landing-nav-link owner-landing-pricing" type="button" onClick={() => setShowPricingModal(true)}>
            Pricing
          </button>
          <a className="text-button owner-landing-nav-link" href="#contact" onClick={(event) => scrollToLandingSection(event, "contact")}>
            Contact
          </a>
        </div>
        <button className="primary-button owner-landing-sign-in" type="button" onClick={() => openAuthModal("signin")}>
          Sign in
        </button>
      </header>
      <section className="owner-landing-hero" aria-labelledby="owner-landing-title">
        <div className="owner-landing-hero-content">
          <div className="owner-landing-hero-body">
            <div className="owner-landing-hero-actions">
              <h1 id="owner-landing-title">
                <span className="owner-landing-title-line">
                  Launch <span className="owner-landing-title-nowrap">app <span className="owner-landing-title-screenshots">screenshots</span></span>
                </span>
                <br />
                <span className="owner-landing-title-line">
                  with <span className="owner-landing-title-native">native-speaker</span>
                </span>
                {" "}
                <span className="owner-landing-title-feedback">feedback</span>
              </h1>
              <ul className="owner-landing-check-list">
                <li>
                  <span className="owner-landing-check-box" aria-hidden="true" />
                  Catch awkward AI-translated wording
                </li>
                <li>
                  <span className="owner-landing-check-box" aria-hidden="true" />
                  Publish perfectly localized artwork
                </li>
                <li>
                  <span className="owner-landing-check-box" aria-hidden="true" />
                  Increase conversion rates
                </li>
              </ul>
              <div className="owner-landing-actions">
                <button className="primary-button owner-landing-primary owner-landing-hero-primary" type="button" onClick={() => openAuthModal("signup")}>
                  Start now
                </button>
              </div>
            </div>
            <div className="owner-landing-visual" aria-hidden="true">
              <div className="owner-landing-shot-stack owner-landing-reference-stack">
                <div className="owner-landing-shot-label">Reference</div>
                <div className="owner-landing-phone">
                  <div className="owner-landing-shot-title">
                    Trips
                    <span>Saved places</span>
                  </div>
                  <div className="owner-landing-device" />
                </div>
              </div>
              <div className="owner-landing-visual-arrow">
                <ChevronRightIcon />
              </div>
              <div className="owner-landing-shot-stack owner-landing-review-stack">
                <div className="owner-landing-shot-label">Under review</div>
                <div className="owner-landing-phone">
                  <div className="owner-landing-shot-title">
                    Reisen
                    <span>Gespeicherte Plätze</span>
                  </div>
                  <div className="owner-landing-device" />
                </div>
                <div className="owner-landing-feedback-card">
                  <span>Needs work</span>
                  <strong>“Gespeicherte Plätze” sounds unnatural. Try “Gespeicherte Orte” instead.</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="owner-landing-section owner-landing-steps" aria-labelledby="setup-title">
        <div className="owner-landing-section-header">
          {/*<h2 id="setup-title">3 steps. 1 minute.</h2>*/}
          <h2 id="setup-title">Set up in 1 minute</h2>
        </div>
        <div className="owner-landing-step-grid">
          {setupSteps.map((step, index) => (
            <article className="owner-landing-step" key={step.title}>
              <span className="owner-landing-step-number">{index + 1}</span>
              <h3>
                {step.title}
                {"note" in step ? <span className="owner-landing-step-title-note">{step.note}</span> : null}
              </h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="reviewer-experience" className="owner-landing-section owner-landing-reviewer-experience" aria-labelledby="reviewer-experience-title">
        <div>
          <h2 id="reviewer-experience-title">Reviewer experience</h2>
        </div>
        <div className="owner-landing-reviewer-copy">
          <strong>One click starts the review</strong>
          <p>
            Reviewers open a shared link, land on the first screenshot, and can mark what needs work right away.
            Asking for feedback feels like a quick pass, not another tool they have to learn.
          </p>
        </div>
      </section>

      <section className="owner-landing-section owner-landing-benefits" aria-labelledby="benefits-title">
        <div>
          <h2 id="benefits-title">
            Catch awkward
            <br />
            wording in context
          </h2>
        </div>
        <div className="owner-landing-benefit-list">
          <p>Every comment stays pinned to the exact screenshot, locale, and spot.</p>
          <p>Native speakers can review without access to your internal tools.</p>
          <p>Share private links for trusted reviews, or public links for wider feedback.</p>
        </div>
      </section>

      <section id="create-account" className="owner-landing-auth-section" aria-labelledby="account-title">
        <div className="owner-landing-auth-copy">
          <h2 id="account-title">
            Get native-speaker
            <br />
            feedback
          </h2>
          <p>
            Sign in, find your app, choose locales, sync screenshots, and share a review link with native speakers.
          </p>
        </div>
        <div className="owner-landing-auth-actions">
          <button className="primary-button owner-landing-primary" type="button" onClick={() => openAuthModal("signup")}>
            Create review link
          </button>
          <span>Reviewers don’t need internal tool access.</span>
        </div>
      </section>

      <section id="contact" className="owner-landing-section owner-landing-contact" aria-labelledby="contact-title">
        <div className="owner-landing-contact-copy">
          <h2 id="contact-title">Need help?</h2>
          <p>Use email for support, product questions, or business inquiries.</p>
          <div className="owner-landing-contact-action">
            <button
              className={`owner-landing-contact-link ${supportEmailCopied ? "copied" : ""}`}
              type="button"
              aria-label={supportEmailCopied ? "Support email copied" : "Copy support email"}
              onClick={() => void copySupportEmail()}
            >
              <span>{supportEmail}</span>
              {supportEmailCopied ? <CheckIcon /> : <CopyIcon />}
            </button>
            <span className={`owner-landing-contact-copied ${supportEmailCopied ? "visible" : ""}`} aria-live="polite">
              {supportEmailCopied ? "Copied!" : ""}
            </span>
          </div>
        </div>
      </section>

      {showAuthModal ? (
        <>
          <ModalScrollLock />
          <div className="modal-backdrop auth-modal-backdrop" role="presentation" onMouseDown={() => setShowAuthModal(false)}>
            <section className="modal-panel auth-modal-panel" role="dialog" aria-modal="true" aria-labelledby="owner-auth-title" onMouseDown={(event) => event.stopPropagation()}>
              <h2 id="owner-auth-title" className="auth-modal-title">
                {isFirebasePasswordResetLink() ? "Reset password" : "Sign in or create account"}
              </h2>
              {firebaseConfigured ? (
                <EmailPasswordAuthForm
                  initialMode={authModalInitialMode}
                  anonymousMigrationAvailable={props.anonymousMigrationAvailable}
                  onSignedIn={() => setShowAuthModal(false)}
                />
              ) : (
                <p className="muted-copy">Firebase frontend config is not set yet.</p>
              )}
            </section>
          </div>
        </>
      ) : null}
      {showPricingModal ? (
        <SubscriptionModal
          context={null}
          onClose={() => setShowPricingModal(false)}
          onRequireSignIn={() => {
            setShowPricingModal(false);
            openAuthModal("signin");
          }}
        />
      ) : null}
    </main>
  );
}

function EmailPasswordAuthForm(props: {
  initialMode?: AuthMode;
  initialDelivery?: AuthDelivery;
  onSignedIn?: (user: FirebaseUser) => void;
  onUserUpdated?: (user: UserProfile) => void;
  anonymousMigrationAvailable?: boolean;
}) {
  const [mode, setMode] = useState<AuthMode>(props.initialMode ?? "signin");
  const [delivery, setDelivery] = useState<AuthDelivery>(() =>
    props.initialDelivery ?? readStoredAuthDelivery()
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"error" | "success">("error");
  const [emailLinkPending, setEmailLinkPending] = useState(() => isFirebaseEmailSignInLink());
  const [passwordResetPending, setPasswordResetPending] = useState(() => isFirebasePasswordResetLink());
  const [passwordResetVerified, setPasswordResetVerified] = useState(false);
  const [passwordResetPassword, setPasswordResetPassword] = useState("");
  const [passwordResetConfirmation, setPasswordResetConfirmation] = useState("");
  const [pendingAnonymousTransfer, setPendingAnonymousTransfer] = useState<{
    anonymousIdToken: string;
    signedInUser: FirebaseUser;
  } | null>(null);
  const emailLinkHandledRef = useRef(false);
  const passwordResetHandledRef = useRef(false);
  const notify = useFloatingNotification();

  useEffect(() => {
    if (!passwordResetHandledRef.current && isFirebasePasswordResetLink()) {
      passwordResetHandledRef.current = true;
      setPasswordResetPending(true);
      setPasswordResetVerified(false);
      setEmailLinkPending(false);
      setDelivery("password");
      setMode("signin");
      clearAuthMessage();
      setBusy(true);
      getFirebasePasswordResetEmail()
        .then((resetEmail) => {
          setEmail(resetEmail);
          setPasswordResetVerified(true);
        })
        .catch((error) => {
          showAuthError(error instanceof Error ? error.message : "Failed to verify password reset link.");
        })
        .finally(() => {
          setBusy(false);
        });
      return;
    }

    if (emailLinkHandledRef.current || !isFirebaseEmailSignInLink()) {
      return;
    }

    emailLinkHandledRef.current = true;
    setDelivery("link");
    setMode("signin");
    setEmailLinkPending(true);

    const storedEmail = getStoredFirebaseEmailLinkEmail();
    if (!storedEmail) {
      showAuthError("Enter your email to complete sign in.");
      notify("Enter your email to complete sign in.");
      return;
    }

    setEmail(storedEmail);
    notify("Completing email link sign in...");
    setBusy(true);
    completeFirebaseEmailSignInLink(storedEmail)
      .then(finishAuthentication)
      .catch((error) => {
        showAuthError(error instanceof Error ? error.message : "Failed to complete email link sign in.");
      })
      .finally(() => {
        setBusy(false);
      });
  }, [notify, props.onSignedIn]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clearAuthMessage();

    if (passwordResetPending) {
      await completePasswordReset();
      return;
    }

    const nextEmail = email.trim();

    if (!nextEmail) {
      showAuthError("Enter an email address.");
      return;
    }

    if (delivery === "link") {
      setBusy(true);
      try {
        if (emailLinkPending || isFirebaseEmailSignInLink()) {
          const result = await completeFirebaseEmailSignInLink(nextEmail);
          finishAuthentication(result);
        } else {
          await sendFirebaseEmailSignInLink(nextEmail);
          showAuthSuccess("Email sign-in link sent. Check your inbox.");
          notify("Email sign-in link sent. Check your inbox.");
        }
      } catch (error) {
        showAuthError(error instanceof Error ? error.message : "Authentication failed.");
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!password) {
      showAuthError("Enter a password.");
      return;
    }
    if (password.length < 6) {
      showAuthError("Use at least 6 characters for the password.");
      return;
    }

    setBusy(true);
    try {
      if (mode === "signin") {
        finishAuthentication(await signInWithEmailPassword(nextEmail, password));
      } else {
        finishAuthentication(await createEmailPasswordAccount(nextEmail, password));
      }
    } catch (error) {
      showAuthError(error instanceof Error ? error.message : "Authentication failed.");
    } finally {
      setBusy(false);
    }
  }

  async function completePasswordReset() {
    if (!passwordResetVerified) {
      showAuthError("This password reset link is not ready yet.");
      return;
    }
    if (passwordResetPassword.length < 6) {
      showAuthError("Use at least 6 characters for the password.");
      return;
    }
    if (passwordResetPassword !== passwordResetConfirmation) {
      showAuthError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await completeFirebasePasswordReset(passwordResetPassword);
      setPasswordResetPending(false);
      setPasswordResetVerified(false);
      setPasswordResetPassword("");
      setPasswordResetConfirmation("");
      setPassword("");
      setDelivery("password");
      setMode("signin");
      showAuthSuccess("Password updated. Sign in with your new password.");
      notify("Password updated. Sign in with your new password.");
    } catch (error) {
      showAuthError(error instanceof Error ? error.message : "Failed to reset password.");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    const nextEmail = email.trim();
    clearAuthMessage();
    if (!nextEmail) {
      showAuthError("Enter your email before requesting a password reset.");
      return;
    }
    setBusy(true);
    try {
      await sendFirebasePasswordReset(nextEmail);
      showAuthSuccess("Password reset email sent. Check your inbox.");
      notify("Password reset email sent.");
    } catch (error) {
      showAuthError(error instanceof Error ? error.message : "Failed to send password reset email.");
    } finally {
      setBusy(false);
    }
  }

  function selectDelivery(nextDelivery: AuthDelivery) {
    setDelivery(nextDelivery);
    writeStoredAuthDelivery(nextDelivery);
    clearAuthMessage();
    if (nextDelivery === "link") {
      const hasEmailLink = isFirebaseEmailSignInLink();
      setEmailLinkPending(hasEmailLink);
      const storedEmail = getStoredFirebaseEmailLinkEmail();
      if (hasEmailLink && storedEmail && !email.trim()) {
        setEmail(storedEmail);
      }
    } else {
      setEmailLinkPending(false);
    }
  }

  function leavePasswordReset() {
    clearFirebaseAuthActionFromUrl();
    setPasswordResetPending(false);
    setPasswordResetVerified(false);
    setPasswordResetPassword("");
    setPasswordResetConfirmation("");
    setDelivery("password");
    setMode("signin");
    clearAuthMessage();
  }

  function clearAuthMessage() {
    setMessage(null);
    setMessageTone("error");
  }

  function showAuthError(nextMessage: string) {
    setMessageTone("error");
    setMessage(nextMessage);
  }

  function showAuthSuccess(nextMessage: string) {
    setMessageTone("success");
    setMessage(nextMessage);
  }

  function finishAuthentication(result: { user: FirebaseUser; previousAnonymousIdToken?: string | null }) {
    if (result.previousAnonymousIdToken) {
      setPendingAnonymousTransfer({
        anonymousIdToken: result.previousAnonymousIdToken,
        signedInUser: result.user
      });
      setMessage(null);
      return;
    }
    if (!result.user.isAnonymous) {
      clearAnonymousReviewerName();
    }
    props.onSignedIn?.(result.user);
  }

  async function transferAnonymousComments() {
    if (!pendingAnonymousTransfer) {
      return;
    }
    const transfer = pendingAnonymousTransfer;
    setBusy(true);
    clearAuthMessage();
    try {
      const updatedUser = await mergeAnonymousReviewer(
        { anonymousIdToken: transfer.anonymousIdToken },
        { getIdToken: () => transfer.signedInUser.getIdToken() }
      );
      props.onUserUpdated?.(updatedUser);
      notify("Anonymous comments moved to this account.");
      setPendingAnonymousTransfer(null);
      clearAnonymousReviewerName();
      props.onSignedIn?.(transfer.signedInUser);
    } catch (error) {
      showAuthError(error instanceof Error ? error.message : "Failed to move anonymous comments.");
    } finally {
      setBusy(false);
    }
  }

  function keepAnonymousCommentsSeparate() {
    const transfer = pendingAnonymousTransfer;
    setPendingAnonymousTransfer(null);
    if (transfer) {
      clearAnonymousReviewerName();
      props.onSignedIn?.(transfer.signedInUser);
    }
  }

  if (pendingAnonymousTransfer) {
    return (
      <form className="auth-form" noValidate onSubmit={(event) => {
        event.preventDefault();
        void transferAnonymousComments();
      }}>
        <div className="auth-transfer-card">
          <h3>Move your anonymous comments?</h3>
          <p>Attach comments from this browser to the account you just signed into.</p>
        </div>
        <div className="auth-transfer-actions">
          <button className="text-button" type="button" disabled={busy} onClick={keepAnonymousCommentsSeparate}>
            Keep separate
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? "Moving..." : "Move comments"}
          </button>
        </div>
        {message ? <p className={`auth-message ${messageTone}`}>{message}</p> : null}
      </form>
    );
  }

  if (passwordResetPending) {
    return (
      <form className="auth-form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        {passwordResetVerified ? (
          <label className="auth-field">
            <span>Email</span>
            <input type="email" value={email} disabled readOnly autoComplete="email" />
          </label>
        ) : (
          <p className="muted-copy">Checking password reset link...</p>
        )}
        <label className="auth-field">
          <span>New password</span>
          <input
            type="password"
            value={passwordResetPassword}
            onChange={(event) => setPasswordResetPassword(event.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            disabled={!passwordResetVerified}
          />
        </label>
        <label className="auth-field">
          <span>Confirm password</span>
          <input
            type="password"
            value={passwordResetConfirmation}
            onChange={(event) => setPasswordResetConfirmation(event.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            disabled={!passwordResetVerified}
          />
        </label>
        <div className="auth-delivery-actions single">
          <button className="text-button" type="button" disabled={busy} onClick={leavePasswordReset}>
            Back to sign in
          </button>
        </div>
        <button className="primary-button" type="submit" disabled={busy || !passwordResetVerified}>
          {busy ? "Working..." : "Update password"}
        </button>
        {message ? <p className={`auth-message ${messageTone}`}>{message}</p> : null}
      </form>
    );
  }

  const submitLabel = busy
    ? "Working..."
    : delivery === "link"
      ? emailLinkPending ? "Complete sign in" : "Send email link"
      : mode === "signin" ? "Sign in" : "Create account";

  return (
    <form className="auth-form" noValidate onSubmit={(event) => void handleSubmit(event)}>
      <div className={`auth-mode-tabs ${mode === "signup" ? "select-second" : "select-first"}`} role="tablist" aria-label="Authentication mode">
        <button
          className={`auth-mode-button ${mode === "signin" ? "active" : ""}`}
          type="button"
          onClick={() => {
            setMode("signin");
            clearAuthMessage();
          }}
        >
          Sign in
        </button>
        <button
          className={`auth-mode-button ${mode === "signup" ? "active" : ""}`}
          type="button"
          onClick={() => {
            setMode("signup");
            clearAuthMessage();
          }}
        >
          Create account
        </button>
      </div>
      <label className="auth-field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoComplete="email"
        />
      </label>
      {delivery === "password" ? (
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={6}
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
          />
        </label>
      ) : null}
      <div className={`auth-delivery-actions ${mode === "signin" && delivery === "password" ? "" : "single"}`}>
        {mode === "signin" && delivery === "password" ? (
          <button className="text-button" type="button" disabled={busy} onClick={() => void resetPassword()}>
            Forgot password?
          </button>
        ) : null}
        <button
          className="text-button"
          type="button"
          disabled={busy}
          onClick={() => selectDelivery(delivery === "password" ? "link" : "password")}
        >
          {delivery === "password" ? "Use email link" : "Use password"}
        </button>
      </div>
      {props.anonymousMigrationAvailable && delivery === "link" ? (
        <p className="auth-note">Open the link in this same browser to keep your anonymous comments.</p>
      ) : null}
      <button className="primary-button" type="submit" disabled={busy}>
        {submitLabel}
      </button>
      {message ? <p className={`auth-message ${messageTone}`}>{message}</p> : null}
    </form>
  );
}

function TopBar(props: {
  apps: AppSummary[];
  removingSavedAppId: string | null;
  deletingAppId: string | null;
  selectedAppId: string | null;
  session: SessionState;
  user: UserProfile | null;
  userEmail: string;
  loading: boolean;
  onSelectApp: (id: string) => void;
  onCreateApp: (request: CreateAppRequest) => Promise<void>;
  onRemoveSavedApp: (id: string) => void;
  onDeleteApp: (id: string, confirmationName: string) => Promise<void>;
  onUpdateApp: (appId: string, request: UpdateAppStoreMetadataRequest) => Promise<AppSummary>;
  onUpdateProfile: (displayName: string | null, shareEmailWithAppOwner: boolean, nativeLanguages: string[], proficientLanguages: string[]) => Promise<UserProfile>;
  onOpenCreateApp: () => void;
  onOpenSubscription: () => void;
  onSignOut: () => Promise<void>;
}) {
  const [showAppMenu, setShowAppMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [editingApp, setEditingApp] = useState<AppSummary | null>(null);
  const [deleteTargetApp, setDeleteTargetApp] = useState<AppSummary | null>(null);
  const [query, setQuery] = useState("");
  const appMenuRef = useRef<HTMLDivElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const appSearchInputRef = useRef<HTMLInputElement | null>(null);

  const selectedApp = props.apps.find((app) => app.id === props.selectedAppId) ?? props.apps[0] ?? null;
  const showAppSearch = props.apps.length > 8;
  const activeQuery = showAppSearch ? query.toLowerCase().trim() : "";
  const filteredApps = props.apps.filter((app) => {
    const haystack = `${app.name} ${app.storeIdentifier ?? ""} ${app.iosAppStoreId ?? ""} ${app.androidPackageName ?? ""}`.toLowerCase();
    return haystack.includes(activeQuery);
  });
  const ownedApps = filteredApps.filter((app) => app.relationship === "OWNER");
  const savedApps = filteredApps.filter((app) => app.relationship !== "OWNER");

  function openEditModal(app: AppSummary) {
    setEditingApp(app);
    setModalMode("edit");
    setShowAppMenu(false);
  }

  useEffect(() => {
    if (showAppMenu && showAppSearch) {
      focusDropdownSearchInput(appSearchInputRef.current);
    }
  }, [showAppMenu, showAppSearch]);

  return (
    <>
      <header className="top-bar">
        <div className="brand-lockup">
          <span className="brand-mark"><ScreenshotReviewLogo /></span>
          <strong>SCREENSHOT.REVIEW</strong>
        </div>

        <div className="top-controls">
          <div className="menu-shell app-picker-shell" ref={appMenuRef}>
            <button
              className={`ghost-button menu-trigger ${showAppMenu ? "open" : ""}`}
              type="button"
              onClick={() => {
                setShowAppMenu((current) => !current);
                setShowAccountMenu(false);
              }}
            >
              <DropdownCopy eyebrow="Active app" value={selectedApp?.name ?? "Select app"} />
              <ChevronDownIcon />
            </button>

            {showAppMenu ? (
              <DropdownPortal
                anchorRef={appMenuRef}
                className="app-menu-panel"
                onDismiss={() => setShowAppMenu(false)}
                open={showAppMenu}
              >
                {showAppSearch ? (
                  <label className="menu-search-shell">
                    <span>Search apps</span>
                    <input
                      className="menu-search-input"
                      ref={appSearchInputRef}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Name or store ID"
                    />
                  </label>
                ) : null}

                <AppMenuSection
                  title="Owned apps"
                  apps={ownedApps}
                  selectedAppId={props.selectedAppId}
                  deletingAppId={props.deletingAppId}
                  onEdit={openEditModal}
                  onDelete={(app) => {
                    setDeleteTargetApp(app);
                    setShowAppMenu(false);
                  }}
                  onSelect={(id) => {
                    props.onSelectApp(id);
                    setShowAppMenu(false);
                  }}
                />
                <AppMenuSection
                  title="Review queue"
                  apps={savedApps}
                  selectedAppId={props.selectedAppId}
                  emptyText="No apps in review queue yet."
                  removingAppId={props.removingSavedAppId}
                  onRemove={props.onRemoveSavedApp}
                  onSelect={(id) => {
                    props.onSelectApp(id);
                    setShowAppMenu(false);
                  }}
                />

                <button
                  className="primary-button compact add-app-button"
                  type="button"
                  onClick={() => {
                    props.onOpenCreateApp();
                    setShowAppMenu(false);
                  }}
                >
                  Add app
                </button>
              </DropdownPortal>
            ) : null}
          </div>

          <div className="menu-shell" ref={accountMenuRef}>
            <button
              className={`ghost-button menu-trigger account-trigger ${showAccountMenu ? "open" : ""}`}
              type="button"
              onClick={() => {
                setShowAccountMenu((current) => !current);
                setShowAppMenu(false);
              }}
            >
              <DropdownCopy eyebrow="Account" value={props.userEmail} />
              <ChevronDownIcon />
            </button>
            {showAccountMenu ? (
              <DropdownPortal
                anchorRef={accountMenuRef}
                className="account-menu-panel"
                onDismiss={() => setShowAccountMenu(false)}
                open={showAccountMenu}
              >
                {props.user?.admin ? (
                  <button
                    className="menu-option"
                    type="button"
                    onClick={() => {
                      setShowAdminPanel(true);
                      setShowAccountMenu(false);
                    }}
                  >
                    <span className="menu-option-primary">Admin panel</span>
                    <span className="menu-option-secondary">Users, plans, Stripe sync.</span>
                  </button>
                ) : null}
                <button
                  className="menu-option"
                  type="button"
                  onClick={() => {
                    setShowProfile(true);
                    setShowAccountMenu(false);
                  }}
                >
                  <span className="menu-option-primary">Profile</span>
                  <span className="menu-option-secondary">Display name and languages.</span>
                </button>
                <button
                  className="menu-option"
                  type="button"
                  onClick={() => {
                    props.onOpenSubscription();
                    setShowAccountMenu(false);
                  }}
                >
                  <span className="menu-option-primary">Subscription</span>
                  <span className="menu-option-secondary">Plan, usage, invoices.</span>
                </button>
                <button className="menu-option danger" type="button" onClick={props.onSignOut}>
                  <span className="menu-option-primary">Sign out</span>
                  <span className="menu-option-secondary">Leave this browser session.</span>
                </button>
              </DropdownPortal>
            ) : null}
          </div>
        </div>
      </header>

      {modalMode ? (
        <AppDetailsModal
          app={modalMode === "edit" ? editingApp : null}
          context={props.session}
          loading={props.loading}
          mode={modalMode}
          onClose={() => {
            setModalMode(null);
            setEditingApp(null);
          }}
          onCreate={props.onCreateApp}
          onUpdate={async (appId, request) => {
            await props.onUpdateApp(appId, request);
          }}
        />
      ) : null}
      {showProfile ? (
        <ProfileModal
          user={props.user}
          onClose={() => setShowProfile(false)}
          onSave={props.onUpdateProfile}
        />
      ) : null}
      {showAdminPanel ? (
        <AdminPanel
          context={props.session}
          onClose={() => setShowAdminPanel(false)}
        />
      ) : null}
      {deleteTargetApp ? (
        <DeleteAppConfirmationModal
          app={deleteTargetApp}
          deleting={props.deletingAppId === deleteTargetApp.id}
          onClose={() => setDeleteTargetApp(null)}
          onDelete={async (confirmationName) => {
            await props.onDeleteApp(deleteTargetApp.id, confirmationName);
            setDeleteTargetApp(null);
          }}
        />
      ) : null}
    </>
  );
}

function AppMenuSection(props: {
  title: string;
  apps: AppSummary[];
  selectedAppId: string | null;
  emptyText?: string;
  removingAppId?: string | null;
  deletingAppId?: string | null;
  onEdit?: (app: AppSummary) => void;
  onDelete?: (app: AppSummary) => void;
  onRemove?: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="app-menu-section">
      <p>{props.title}</p>
      {props.apps.length ? (
        props.apps.map((app) => (
          <div className={`app-menu-row ${app.id === props.selectedAppId ? "active" : ""}`} key={app.id}>
            <button className="menu-option app-menu-select" type="button" onClick={() => props.onSelect(app.id)}>
              <span className="menu-option-primary">{app.name}</span>
              <span className="menu-option-secondary">{formatStoreIdentifiers(app)}</span>
            </button>
            {props.onEdit ? (
              <div className="app-menu-actions">
                <button className="edit-app-button" type="button" onClick={() => props.onEdit?.(app)} aria-label={`Edit ${app.name}`} title="Edit app">
                  <EditIcon />
                </button>
                {props.onDelete ? (
                  <button
                    className="edit-app-button danger"
                    type="button"
                    disabled={props.deletingAppId === app.id}
                    onClick={() => props.onDelete?.(app)}
                    aria-label={`Delete ${app.name}`}
                    title="Delete app"
                  >
                    <TrashIcon />
                  </button>
                ) : null}
              </div>
            ) : null}
            {props.onRemove ? (
              <div className="app-menu-actions">
                <button
                  className="edit-app-button danger"
                  type="button"
                  disabled={props.removingAppId === app.id}
                  onClick={() => props.onRemove?.(app.id)}
                  aria-label={`Remove ${app.name} from review queue`}
                  title="Remove from review queue"
                >
                  <TrashIcon />
                </button>
              </div>
            ) : null}
          </div>
        ))
      ) : (
        <div className="menu-empty">{props.emptyText ?? "No apps yet."}</div>
      )}
    </section>
  );
}

function formatStoreIdentifiers(app: AppSummary): string {
  return [app.iosAppStoreId, app.androidPackageName].filter(Boolean).join(" / ") || "No store IDs";
}

function DeleteAppConfirmationModal(props: {
  app: AppSummary;
  deleting: boolean;
  onClose: () => void;
  onDelete: (confirmationName: string) => Promise<void>;
}) {
  useModalScrollLock();
  const [confirmationName, setConfirmationName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const matchesAppName = confirmationName === props.app.name;

  useEffect(() => {
    setConfirmationName("");
    setError(null);
  }, [props.app.id]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!matchesAppName) {
      setError("Type the app name exactly to confirm deletion.");
      return;
    }

    try {
      await props.onDelete(confirmationName);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Failed to delete app.");
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel delete-app-modal" role="dialog" aria-modal="true" aria-labelledby="delete-app-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Delete app</p>
            <h2 id="delete-app-title">Delete {props.app.name}?</h2>
          </div>
        </div>
        <form className="delete-app-form" noValidate onSubmit={(event) => void submit(event)}>
          <p>This permanently removes the app workspace, screenshots, comments, saved links, and uploaded assets.</p>
          <label>
            <span>Type app name to confirm</span>
            <input
              value={confirmationName}
              onChange={(event) => {
                setConfirmationName(event.target.value);
                setError(null);
              }}
              placeholder={props.app.name}
              autoFocus
            />
          </label>
          {error ? <p className="error-copy">{error}</p> : null}
          <div className="modal-actions">
            <button className="ghost-button" type="button" disabled={props.deleting} onClick={props.onClose}>
              Cancel
            </button>
            <button className="primary-button danger" type="submit" disabled={props.deleting || !matchesAppName}>
              {props.deleting ? "Deleting..." : "Delete app"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const commentVisibilityOptions = [
  { label: "Everyone", value: "EVERYONE" },
  { label: "Signed in users", value: "SIGNED_IN" },
  { label: "Comment authors", value: "AUTHOR_OR_OWNER" }
];

const assetVisibilityOptions = [
  { label: "Everyone", value: "EVERYONE" },
  { label: "Signed in users", value: "SIGNED_IN" }
];

function AppDetailsModal(props: {
  app: AppSummary | null;
  context: SessionState;
  loading: boolean;
  mode: "add" | "edit";
  onClose: () => void;
  onCreate: (request: CreateAppRequest) => Promise<void>;
  onUpdate: (appId: string, request: UpdateAppStoreMetadataRequest) => Promise<void>;
}) {
  useModalScrollLock();
  const [name, setName] = useState(props.app?.name ?? "");
  const [iosAppStoreId, setIosAppStoreId] = useState(props.app?.iosAppStoreId ?? "");
  const [androidPackageName, setAndroidPackageName] = useState(props.app?.androidPackageName ?? "");
  const [assetVisibilityPolicy, setAssetVisibilityPolicy] = useState<AssetVisibilityPolicy>(props.app?.assetVisibilityPolicy ?? "EVERYONE");
  const [commentAuthorPolicy, setCommentAuthorPolicy] = useState<CommentAuthorPolicy>(props.app?.commentAuthorPolicy ?? "SIGNED_IN");
  const [commentVisibilityPolicy, setCommentVisibilityPolicy] = useState<CommentVisibilityPolicy>(props.app?.commentVisibilityPolicy ?? "SIGNED_IN");
  const [commentReplyVisibilityPolicy, setCommentReplyVisibilityPolicy] = useState<CommentVisibilityPolicy>(props.app?.commentReplyVisibilityPolicy ?? "SIGNED_IN");
  const [referenceLocale, setReferenceLocale] = useState(props.app?.referenceLocale ?? "");
  const [referenceLocaleWarning, setReferenceLocaleWarning] = useState<string | null>(null);
  const [confirmStoreIdentifierChange, setConfirmStoreIdentifierChange] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(props.app?.name ?? "");
    setIosAppStoreId(props.app?.iosAppStoreId ?? "");
    setAndroidPackageName(props.app?.androidPackageName ?? "");
    setAssetVisibilityPolicy(props.app?.assetVisibilityPolicy ?? "EVERYONE");
    setCommentAuthorPolicy(props.app?.commentAuthorPolicy ?? "SIGNED_IN");
    setCommentVisibilityPolicy(props.app?.commentVisibilityPolicy ?? "SIGNED_IN");
    setCommentReplyVisibilityPolicy(props.app?.commentReplyVisibilityPolicy ?? "SIGNED_IN");
    setReferenceLocale(props.app?.referenceLocale ?? "");
    setReferenceLocaleWarning(null);
    setConfirmStoreIdentifierChange(false);
    setError(null);
  }, [props.app, props.mode]);

  const storeIdentifierChanged = props.mode === "edit" && props.app
    ? (props.app.iosAppStoreId ?? "") !== iosAppStoreId || (props.app.androidPackageName ?? "") !== androidPackageName
    : false;
  const normalizedReferenceLocale = normalizeLocaleInput(referenceLocale);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    if (!normalizedReferenceLocale) {
      setReferenceLocaleWarning(referenceLocale.trim() ? "Use a valid reference locale, such as en-US." : "Reference locale is required.");
      setError(null);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (props.mode === "edit" && props.app) {
        if (storeIdentifierChanged && !confirmStoreIdentifierChange) {
          setError("Confirm the store identifier change before saving.");
          return;
        }
        await props.onUpdate(props.app.id, {
          name: name.trim(),
          iosAppStoreId: iosAppStoreId || undefined,
          androidPackageName: androidPackageName || undefined,
          assetVisibilityPolicy,
          commentAuthorPolicy,
          commentVisibilityPolicy,
          commentReplyVisibilityPolicy,
          referenceLocale: normalizedReferenceLocale
        });
      } else {
        await props.onCreate({
          name: name.trim(),
          storeIdentifier: iosAppStoreId || androidPackageName || undefined,
          iosAppStoreId: iosAppStoreId || undefined,
          androidPackageName: androidPackageName || undefined,
          primaryPlatform: derivePrimaryPlatform(iosAppStoreId, androidPackageName),
          assetVisibilityPolicy,
          commentAuthorPolicy,
          commentVisibilityPolicy,
          commentReplyVisibilityPolicy,
          referenceLocale: normalizedReferenceLocale
        });
      }
      props.onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Failed to save app.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel app-details-modal" role="dialog" aria-modal="true" aria-labelledby="app-details-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">{props.mode === "edit" ? "Edit app" : "Add app"}</p>
            <h2 id="app-details-title">{props.mode === "edit" ? "Update app details" : "Create app workspace"}</h2>
          </div>
        </div>
        <form className="app-details-form" noValidate onSubmit={submit}>
          <label>
            <span>App name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="App name" />
          </label>
          <StoreLookupField
            context={props.context}
            platform="IOS"
            label="App Store app ID"
            placeholder="Search App Store or paste app ID"
            value={iosAppStoreId}
            onValueChange={setIosAppStoreId}
            onSelect={(result) => {
              setIosAppStoreId(result.identifier);
              setName((current) => current || result.name);
            }}
          />
          <StoreLookupField
            context={props.context}
            platform="ANDROID"
            label="Android package"
            placeholder="Search Google Play or paste package"
            value={androidPackageName}
            onValueChange={setAndroidPackageName}
            onSelect={(result) => {
              setAndroidPackageName(result.identifier);
              setName((current) => current || result.name);
            }}
          />
          {storeIdentifierChanged ? (
            <label className="store-change-warning">
              <input
                type="checkbox"
                checked={confirmStoreIdentifierChange}
                onChange={(event) => setConfirmStoreIdentifierChange(event.target.checked)}
              />
              <span>Changing store identifiers can disconnect future refreshes from the screenshots already reviewed.</span>
            </label>
          ) : null}
          <SingleLocaleField
            label="Reference locale"
            value={referenceLocale}
            onChange={(value) => {
              setReferenceLocale(value);
              setReferenceLocaleWarning(null);
            }}
            required={true}
            warning={referenceLocaleWarning}
          />
          <div className="app-settings-grid">
            <StoreControlDropdown
              eyebrow="Who can see assets?"
              options={assetVisibilityOptions}
              value={assetVisibilityPolicy}
              onSelect={(value) => setAssetVisibilityPolicy(value as AssetVisibilityPolicy)}
            />
            <StoreControlDropdown
              eyebrow="Who can comment?"
              options={[
                { label: "Everyone", value: "ANYONE" },
                { label: "Signed in users", value: "SIGNED_IN" }
              ]}
              value={commentAuthorPolicy}
              onSelect={(value) => setCommentAuthorPolicy(value as CommentAuthorPolicy)}
            />
            <StoreControlDropdown
              eyebrow="Who can see comments?"
              options={commentVisibilityOptions}
              value={commentVisibilityPolicy}
              onSelect={(value) => setCommentVisibilityPolicy(value as CommentVisibilityPolicy)}
            />
            <StoreControlDropdown
              eyebrow="Who can see replies?"
              options={commentVisibilityOptions}
              value={commentReplyVisibilityPolicy}
              onSelect={(value) => setCommentReplyVisibilityPolicy(value as CommentVisibilityPolicy)}
            />
          </div>
          {error ? <p className="error-copy">{error}</p> : null}
          <div className="modal-actions">
            <button className="ghost-button" type="button" onClick={props.onClose}>
              Cancel
            </button>
            <button className="primary-button" type="submit" disabled={props.loading || submitting || !name.trim() || (storeIdentifierChanged && !confirmStoreIdentifierChange)}>
              {submitting ? "Saving..." : props.mode === "edit" ? "Save changes" : "Create app"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ProfileModal(props: {
  user: UserProfile | null;
  onClose: () => void;
  onSave: (displayName: string | null, shareEmailWithAppOwner: boolean, nativeLanguages: string[], proficientLanguages: string[]) => Promise<UserProfile>;
}) {
  useModalScrollLock();
  const [displayName, setDisplayName] = useState(props.user?.displayName ?? "");
  const [shareEmailWithAppOwner, setShareEmailWithAppOwner] = useState(props.user?.shareEmailWithAppOwner ?? false);
  const [nativeLanguages, setNativeLanguages] = useState<string[]>(() =>
    props.user?.languages.filter((language) => language.proficiency === "NATIVE").map((language) => language.languageTag) ?? []
  );
  const [proficientLanguages, setProficientLanguages] = useState<string[]>(() =>
    props.user?.languages.filter((language) => language.proficiency === "PROFICIENT").map((language) => language.languageTag) ?? []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await props.onSave(
        displayName.trim() || null,
        shareEmailWithAppOwner,
        nativeLanguages,
        proficientLanguages.filter((locale) => !nativeLanguages.includes(locale))
      );
      props.onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 id="profile-title">Profile</h2>
          </div>
        </div>
        <label className="profile-display-field">
          <span>Display name</span>
          <input
            type="text"
            maxLength={80}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Name shown on comments"
          />
        </label>
        <label className="profile-email-check">
          <input
            type="checkbox"
            checked={shareEmailWithAppOwner}
            onChange={(event) => setShareEmailWithAppOwner(event.target.checked)}
          />
          <span>
            <strong>Show email to app owners</strong>
            <small>Applies to all comments by this profile.</small>
          </span>
        </label>
        <div className="profile-language-grid">
          <LocaleTagPicker
            label="Native"
            values={nativeLanguages}
            excludedValues={proficientLanguages}
            onChange={(nextValues) => {
              setNativeLanguages(nextValues);
              setProficientLanguages((current) => current.filter((locale) => !nextValues.includes(locale)));
            }}
          />
          <LocaleTagPicker
            label="Proficient"
            values={proficientLanguages}
            excludedValues={nativeLanguages}
            onChange={setProficientLanguages}
          />
        </div>
        {error ? <p className="error-copy">{error}</p> : null}
        <div className="modal-actions">
          <button className="ghost-button" type="button" onClick={props.onClose}>
            Cancel
          </button>
          <button className="primary-button" type="button" disabled={saving} onClick={save}>
            {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </section>
    </div>
  );
}

function SubscriptionModal(props: {
  context: SessionState | null;
  onClose: () => void;
  onRequireSignIn?: () => void;
}) {
  useModalScrollLock();
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [actionKey, setActionKey] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const billingPlanListRef = useRef<HTMLDivElement | null>(null);
  const [activeBillingPlanIndex, setActiveBillingPlanIndex] = useState(0);
  const notify = useFloatingNotification();

  async function loadBilling() {
    setLoading(true);
    setMessage(null);
    try {
      if (props.context) {
        const [nextAccount, planResponse] = await Promise.all([
          getBillingAccount(props.context),
          listBillingPlans(props.context)
        ]);
        setAccount(nextAccount);
        setPlans(planResponse.plans);
      } else {
        const planResponse = await listBillingPlans(publicApiContext);
        setAccount(null);
        setPlans(planResponse.plans);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to load subscription details.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBilling();
  }, []);

  async function beginCheckout(price: BillingPlanPrice) {
    if (!props.context) {
      if (props.onRequireSignIn) {
        props.onRequireSignIn();
      } else {
        setMessage("Sign in to choose a plan.");
      }
      return;
    }

    setActionKey(`checkout:${price.stripePriceId}`);
    setMessage(null);
    try {
      const response = await createStripeCheckoutSession({
        stripePriceId: price.stripePriceId,
        successUrl: window.location.href,
        cancelUrl: window.location.href
      }, props.context);
      window.location.assign(response.url);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to start checkout.");
      setActionKey(null);
    }
  }

  async function openPortal() {
    if (!props.context) {
      return;
    }

    setActionKey("portal");
    setMessage(null);
    try {
      const response = await createStripeCustomerPortalSession({ returnUrl: window.location.href }, props.context);
      window.location.assign(response.url);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to open customer portal.");
      setActionKey(null);
    }
  }

  async function changePlan(price: BillingPlanPrice) {
    if (!props.context) {
      return;
    }

    setActionKey(`change:${price.stripePriceId}`);
    setMessage(null);
    try {
      const response = await changeStripeSubscriptionPlan({ stripePriceId: price.stripePriceId }, props.context);
      notify(response.message);
      await loadBilling();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to change subscription.");
    } finally {
      setActionKey(null);
    }
  }

  async function cancelSubscription() {
    if (!props.context) {
      return;
    }

    setActionKey("cancel");
    setMessage(null);
    try {
      const response = await cancelStripeSubscription(props.context);
      notify(response.message);
      await loadBilling();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to cancel subscription.");
    } finally {
      setActionKey(null);
    }
  }

  const activePriceId = account?.subscription?.stripePriceId ?? null;
  const hasSubscription = Boolean(account?.subscription);
  const availableCurrencies = useMemo(() => collectAvailableBillingCurrencies(plans), [plans]);
  const subscriptionCurrency = useMemo(
    () => normalizeBillingCurrency(account?.subscription?.currency) || billingSubscriptionCatalogCurrency(plans, account?.subscription ?? null),
    [account?.subscription, plans]
  );
  const currencyOptions = subscriptionCurrency ? [subscriptionCurrency] : availableCurrencies;
  const effectiveSelectedCurrency = subscriptionCurrency || selectedCurrency || preferredBillingCurrency(availableCurrencies);

  useEffect(() => {
    setSelectedCurrency((current) => {
      if (subscriptionCurrency) {
        return subscriptionCurrency;
      }
      if (current && availableCurrencies.includes(current)) {
        return current;
      }
      return preferredBillingCurrency(availableCurrencies);
    });
  }, [availableCurrencies, subscriptionCurrency]);

  const updateBillingPlanPager = useCallback(() => {
    const planList = billingPlanListRef.current;
    if (!planList) {
      return;
    }

    const cards = Array.from(planList.querySelectorAll<HTMLElement>(".billing-plan-card"));
    if (!cards.length) {
      setActiveBillingPlanIndex(0);
      return;
    }

    const listRect = planList.getBoundingClientRect();
    const listCenter = listRect.left + planList.clientWidth / 2;
    let nextIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - listCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    setActiveBillingPlanIndex((current) => current === nextIndex ? current : nextIndex);
  }, []);

  useEffect(() => {
    setActiveBillingPlanIndex(0);
    const frame = window.requestAnimationFrame(updateBillingPlanPager);
    return () => window.cancelAnimationFrame(frame);
  }, [plans.length, effectiveSelectedCurrency, updateBillingPlanPager]);

  function scrollToBillingPlan(index: number) {
    setActiveBillingPlanIndex(index);
    const planList = billingPlanListRef.current;
    const card = planList?.querySelectorAll<HTMLElement>(".billing-plan-card")[index];
    if (!planList || !card) {
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const targetLeft = cardRect.left - planList.getBoundingClientRect().left + planList.scrollLeft - (planList.clientWidth - cardRect.width) / 2;
    planList.scrollTo({ left: targetLeft, behavior: "smooth" });
  }

  function billingPriceActionLabel(plan: BillingPlan, active: boolean, busy: boolean): string {
    if (active) {
      return "Active";
    }

    if (!hasSubscription) {
      return busy ? "Opening..." : `Start ${plan.name}`;
    }

    if (busy) {
      return "Switching...";
    }

    const currentPlanCode = account?.subscription?.planCode ?? account?.subscriptionPlanCode ?? null;
    const currentPlanIndex = plans.findIndex((candidate) => candidate.code === currentPlanCode);
    const targetPlanIndex = plans.findIndex((candidate) => candidate.code === plan.code);
    if (currentPlanIndex === -1 || targetPlanIndex === -1 || currentPlanIndex === targetPlanIndex) {
      return "Switch";
    }

    return targetPlanIndex > currentPlanIndex ? "Upgrade" : "Downgrade";
  }

  return (
    <div className="modal-backdrop billing-modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel subscription-modal" role="dialog" aria-modal="true" aria-labelledby="subscription-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 id="subscription-title">Plans and billing</h2>
          </div>
          <div className="subscription-header-actions">
            {!hasSubscription && currencyOptions.length > 1 ? (
              <div className="billing-currency-options" role="group" aria-label="Billing currency">
                {currencyOptions.map((currency) => (
                  <button
                    className={`ghost-button billing-currency-option ${effectiveSelectedCurrency === currency ? "active" : ""}`}
                    key={currency}
                    type="button"
                    aria-pressed={effectiveSelectedCurrency === currency}
                    onClick={() => setSelectedCurrency(currency)}
                  >
                    {billingCurrencyLabel(currency)}
                  </button>
                ))}
              </div>
            ) : null}
            {account && shouldShowBillingStorageUsage(account) ? (
              <div className="billing-storage-badge" aria-label="Storage usage">
                <span>Storage</span>
                <strong>{formatStorageUsage(account)}</strong>
              </div>
            ) : null}
          </div>
        </div>

        {loading ? <p className="muted-copy">Loading subscription details.</p> : null}

        <div className="billing-plan-list" ref={billingPlanListRef} onScroll={updateBillingPlanPager}>
          {plans.length ? (
            <>
              <aside className="billing-feature-column" aria-label="Plan features">
                <div className="billing-feature-column-header">Plan</div>
                <div className="billing-feature-column-spacer" />
                <div className="billing-feature-list">
                  {billingPlanMetricLabels.map((label) => (
                    <div className="billing-feature-label" key={label}>{label}</div>
                  ))}
                </div>
                <div className="billing-feature-column-footer" />
              </aside>
              {plans.map((plan) => {
                const planPrices = effectiveSelectedCurrency
                  ? plan.prices.filter((price) => normalizeBillingCurrency(price.currency) === effectiveSelectedCurrency)
                  : plan.prices;
                const currentPlan = account?.planCode === plan.code;

                return (
                  <section className={`billing-plan-card ${currentPlan ? "current" : ""}`} key={plan.code}>
                    <div className="billing-plan-card-header">
                      <h3>{plan.name}</h3>
                      {currentPlan ? <span>Current</span> : null}
                    </div>
                    <p className="billing-plan-description" aria-hidden={plan.description ? undefined : true}>
                      {plan.description ?? ""}
                    </p>
                    <div className="billing-plan-metric-list">
                      {billingPlanMetrics(plan).map((metric) => (
                        <div className={`billing-plan-metric ${metric.muted ? "muted" : ""}`} key={metric.label}>
                          <span className="billing-plan-mobile-label">{metric.label}</span>
                          {metric.hideValue ? (
                            <span className="billing-plan-metric-placeholder" aria-hidden="true" />
                          ) : (
                            <strong>
                              <span className="billing-plan-metric-value">{metric.value}</span>
                              {metric.unit ? <span className="billing-plan-metric-unit">{metric.unit}</span> : null}
                            </strong>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={`billing-price-list ${planPrices.length === 0 && plan.code === "free" ? "empty" : ""}`}>
                      {planPrices.length ? planPrices.map((price) => {
                        const active = activePriceId === price.stripePriceId;
                        const key = `${plan.code}:${price.stripePriceId}`;
                        const busy = actionKey === `change:${price.stripePriceId}` || actionKey === `checkout:${price.stripePriceId}`;
                        const actionLabel = billingPriceActionLabel(plan, active, busy);
                        return (
                          <button
                            className={`primary-button billing-price-button ${active ? "active" : ""}`}
                            key={key}
                            type="button"
                            disabled={Boolean(actionKey) || active}
                            onClick={() => hasSubscription ? changePlan(price) : beginCheckout(price)}
                          >
                            <span className="billing-price-main">
                              <span className="billing-price-amount">{formatPlanPrice(price)}</span>
                              <small>{billingTaxLabel(price.currency)}</small>
                              <span className="billing-price-action">{actionLabel}</span>
                            </span>
                          </button>
                        );
                      }) : plan.code === "free" ? null : (
                        <span className="billing-price-missing">
                          {effectiveSelectedCurrency
                            ? `No ${billingCurrencyLabel(effectiveSelectedCurrency)} price available.`
                            : "Price not configured."}
                        </span>
                      )}
                    </div>
                  </section>
                );
              })}
            </>
          ) : !loading ? <p className="muted-copy">No subscription plans are configured.</p> : null}
        </div>

        {plans.length > 1 ? (
          <div className="billing-plan-pager-dots" role="group" aria-label="Billing plan position">
            {plans.map((plan, index) => {
              const active = activeBillingPlanIndex === index;
              return (
                <button
                  className={`billing-plan-pager-dot ${active ? "active" : ""}`}
                  key={plan.code}
                  type="button"
                  aria-label={`Show ${plan.name} plan`}
                  aria-current={active ? "true" : undefined}
                  onClick={() => scrollToBillingPlan(index)}
                />
              );
            })}
          </div>
        ) : null}

        {message ? <p className="error-copy">{message}</p> : null}

        <div className="modal-actions">
          {hasSubscription ? (
            <>
              <button className="ghost-button" type="button" disabled={Boolean(actionKey)} onClick={openPortal}>
                {actionKey === "portal" ? "Opening..." : "Manage billing"}
              </button>
              <button className="ghost-button danger" type="button" disabled={Boolean(actionKey) || account?.subscription?.cancelAtPeriodEnd} onClick={cancelSubscription}>
                {actionKey === "cancel" ? "Cancelling..." : account?.subscription?.cancelAtPeriodEnd ? "Cancellation scheduled" : "Cancel at period end"}
              </button>
            </>
          ) : null}
          <button className="ghost-button" type="button" onClick={props.onClose}>
            Close
          </button>
        </div>
      </section>
    </div>
  );
}

function AdminPanel(props: {
  context: SessionState;
  onClose: () => void;
}) {
  useModalScrollLock();
  const [activeTab, setActiveTab] = useState<"users" | "plans" | "stripe">("users");
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [stripeConfig, setStripeConfig] = useState<AdminStripeConfig | null>(null);
  const [stripePrices, setStripePrices] = useState<AdminStripePrice[]>([]);
  const [planProductDrafts, setPlanProductDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmClearStripeState, setConfirmClearStripeState] = useState(false);
  const notify = useFloatingNotification();

  async function loadAdminData() {
    setLoading(true);
    setMessage(null);
    try {
      const [nextUsers, nextPlans, nextConfig, nextPrices] = await Promise.all([
        fetchAdminUsers(props.context),
        fetchAdminPlans(props.context),
        fetchAdminStripeConfig(props.context),
        fetchAdminStripePrices(props.context)
      ]);
      setUsers(nextUsers);
      setPlans(nextPlans);
      setStripeConfig(nextConfig);
      setStripePrices(nextPrices);
      setPlanProductDrafts(Object.fromEntries(nextPlans.map((plan) => [plan.code, plan.stripeProductId ?? ""])));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAdminData();
  }, []);

  async function saveUserPlan(userId: string, planCode: string | null) {
    setSavingKey(`user:${userId}`);
    setMessage(null);
    try {
      const updated = await updateAdminUserPlan(userId, { planCode }, props.context);
      setUsers((current) => current.map((user) => (user.userId === userId ? updated : user)));
      notify("User plan updated.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to update user plan.");
    } finally {
      setSavingKey(null);
    }
  }

  async function savePlanStripeProduct(planCode: string) {
    const stripeProductId = planProductDrafts[planCode]?.trim() || null;
    setSavingKey(`plan:${planCode}`);
    setMessage(null);
    try {
      const updated = await updateAdminPlanStripeProduct(planCode, { stripeProductId }, props.context);
      setPlans((current) => current.map((plan) => (plan.code === updated.code ? updated : plan)));
      setPlanProductDrafts((current) => ({ ...current, [updated.code]: updated.stripeProductId ?? "" }));
      notify("Plan Stripe product updated.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to update Stripe product.");
    } finally {
      setSavingKey(null);
    }
  }

  async function syncPrices() {
    setSavingKey("stripe:sync");
    setMessage(null);
    try {
      const result = await syncAdminStripePrices(props.context);
      const nextPrices = await fetchAdminStripePrices(props.context);
      setStripePrices(nextPrices);
      notify(`Synced ${result.syncedPrices} prices. Ignored ${result.ignoredPrices}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to sync Stripe prices.");
    } finally {
      setSavingKey(null);
    }
  }

  async function clearStripeSubscriptionState() {
    if (!confirmClearStripeState) {
      setConfirmClearStripeState(true);
      return;
    }

    setSavingKey("stripe:clear-state");
    setMessage(null);
    try {
      const result = await clearAdminStripeSubscriptionState(props.context);
      const [nextUsers, nextPrices] = await Promise.all([
        fetchAdminUsers(props.context),
        fetchAdminStripePrices(props.context)
      ]);
      setUsers(nextUsers);
      setStripePrices(nextPrices);
      setConfirmClearStripeState(false);
      notify(
        `Cleared ${result.deletedSubscriptions} ${result.mode} subscriptions, ${result.deletedCustomers} ${result.mode} customers, ${result.deletedPrices} ${result.mode} prices, and ${result.deletedWebhookEvents} ${result.mode} webhook events.`
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Failed to clear Stripe subscription state.");
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel admin-modal" role="dialog" aria-modal="true" aria-labelledby="admin-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Admin</p>
            <h2 id="admin-title">Account controls</h2>
          </div>
          <button className="ghost-button icon-button" type="button" onClick={props.onClose} aria-label="Close">
            <XIcon />
          </button>
        </div>

        <div className="admin-tabs" role="tablist" aria-label="Admin sections">
          <button className={activeTab === "users" ? "active" : ""} type="button" onClick={() => setActiveTab("users")}>Users</button>
          <button className={activeTab === "plans" ? "active" : ""} type="button" onClick={() => setActiveTab("plans")}>Plans</button>
          <button className={activeTab === "stripe" ? "active" : ""} type="button" onClick={() => setActiveTab("stripe")}>Stripe</button>
        </div>

        {loading ? <p className="muted-copy">Loading admin data.</p> : null}
        {message ? <p className="error-copy">{message}</p> : null}

        {!loading && activeTab === "users" ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Plan</th>
                  <th>Usage</th>
                  <th>Stripe</th>
                  <th>Manual plan</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td>
                      <strong>{user.email ?? "No email"}</strong>
                      <span>{user.firebaseUid}</span>
                    </td>
                    <td>{user.role}</td>
                    <td>
                      <strong>{user.effectivePlanName}</strong>
                      <span>{user.subscriptionPlanCode ? `Stripe ${user.subscriptionPlanCode}` : "No Stripe plan"}</span>
                    </td>
                    <td>
                      <span>{user.ownedAppCount}/{formatPlanAppLimit(user.appLimit)} apps</span>
                      <span>{formatBytes(user.storageUsedBytes)} / {formatStorageLimit(user.storageLimitMb)}</span>
                    </td>
                    <td>
                      <span>{user.stripeSubscriptionStatus ?? "None"}</span>
                      {user.stripeCurrentPeriodEnd ? <span>{formatDate(user.stripeCurrentPeriodEnd)}</span> : null}
                    </td>
                    <td>
                      <ManualPlanDropdown
                        plans={plans}
                        value={user.manualPlanCode ?? ""}
                        disabled={savingKey === `user:${user.userId}`}
                        onSelect={(planCode) => void saveUserPlan(user.userId, planCode || null)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loading && activeTab === "plans" ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Limits</th>
                  <th>Purchasable</th>
                  <th>Stripe product</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.code}>
                    <td>
                      <strong>{plan.name}</strong>
                      <span>{plan.code}</span>
                    </td>
                    <td>
                      <span>{formatPlanAppLimit(plan.appLimit)} apps</span>
                      <span>{plan.uploadEnabled ? `${formatStorageLimit(plan.storageLimitMb)} storage` : "Uploads disabled"}</span>
                    </td>
                    <td>{plan.purchasable ? "Yes" : "No"}</td>
                    <td>
                      <input
                        value={planProductDrafts[plan.code] ?? ""}
                        onChange={(event) => setPlanProductDrafts((current) => ({ ...current, [plan.code]: event.target.value }))}
                        placeholder={plan.purchasable ? "prod_..." : "Not required"}
                      />
                    </td>
                    <td>
                      <button
                        className="ghost-button compact"
                        type="button"
                        disabled={savingKey === `plan:${plan.code}`}
                        onClick={() => void savePlanStripeProduct(plan.code)}
                      >
                        {savingKey === `plan:${plan.code}` ? "Saving..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loading && activeTab === "stripe" ? (
          <div className="admin-stripe-panel">
            {stripeConfig ? (
              <dl className="admin-config-grid">
                <div>
                  <dt>Stripe</dt>
                  <dd>{stripeConfig.enabled ? "Enabled" : "Disabled"}</dd>
                </div>
                <div>
                  <dt>Secret key</dt>
                  <dd>{stripeConfig.secretKeyConfigured ? stripeConfig.secretKeyPrefix ?? "Configured" : "Missing"}</dd>
                </div>
                <div>
                  <dt>Mode</dt>
                  <dd>{stripeConfig.secretKeyLivemode === null || stripeConfig.secretKeyLivemode === undefined ? "Unknown" : stripeConfig.secretKeyLivemode ? "Live" : "Test"}</dd>
                </div>
                <div>
                  <dt>Webhook secret</dt>
                  <dd>{stripeConfig.webhookSigningSecretConfigured ? stripeConfig.webhookSigningSecretPrefix ?? "Configured" : "Missing"}</dd>
                </div>
                <div>
                  <dt>App URL</dt>
                  <dd>{stripeConfig.appBaseUrl}</dd>
                </div>
              </dl>
            ) : null}
            <div className="admin-toolbar">
              <button className="primary-button compact" type="button" disabled={Boolean(savingKey)} onClick={() => void syncPrices()}>
                {savingKey === "stripe:sync" ? "Syncing..." : "Sync Stripe prices"}
              </button>
            </div>
            <section className="admin-danger-panel">
              <div>
                <strong>Clear test subscription state</strong>
                <span>Removes only local Stripe test-mode customers, subscriptions, synced prices, and webhook event history. Live-mode rows, plans, and Stripe product mappings stay intact.</span>
              </div>
              <div className="admin-danger-actions">
                {confirmClearStripeState ? <span>Click again to clear test data.</span> : null}
                <button
                  className="ghost-button danger"
                  type="button"
                  disabled={Boolean(savingKey)}
                  onClick={() => void clearStripeSubscriptionState()}
                >
                  {savingKey === "stripe:clear-state" ? "Clearing..." : confirmClearStripeState ? "Confirm test clear" : "Clear test state"}
                </button>
                {confirmClearStripeState ? (
                  <button className="comment-reply-button" type="button" disabled={savingKey === "stripe:clear-state"} onClick={() => setConfirmClearStripeState(false)}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </section>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Stripe IDs</th>
                    <th>Synced</th>
                  </tr>
                </thead>
                <tbody>
                  {stripePrices.length ? stripePrices.map((price) => (
                    <tr key={price.stripePriceId}>
                      <td>
                        <strong>{price.planName}</strong>
                        <span>{price.planCode}</span>
                      </td>
                      <td>{formatAdminStripePrice(price)}</td>
                      <td>{price.active ? `Active ${price.mode}` : `Inactive ${price.mode}`}</td>
                      <td>
                        <span>{price.stripePriceId}</span>
                        <span>{price.stripeProductId}</span>
                      </td>
                      <td>{formatDate(price.syncedAt)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5}>No synced Stripe prices.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ManualPlanDropdown(props: {
  plans: AdminPlan[];
  value: string;
  disabled: boolean;
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const options = [
    { label: "Auto", value: "" },
    ...props.plans.map((plan) => ({ label: plan.name, value: plan.code }))
  ];
  const selected = options.find((option) => option.value === props.value) ?? options[0];

  return (
    <div className="menu-shell manual-plan-dropdown" ref={ref}>
      <button
        className={`ghost-button menu-trigger manual-plan-trigger ${open ? "open" : ""}`}
        type="button"
        disabled={props.disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected.label}</span>
        <ChevronDownIcon />
      </button>
      {open ? (
        <DropdownPortal
          anchorRef={ref}
          className="store-control-panel manual-plan-panel"
          minWidth={180}
          onDismiss={() => setOpen(false)}
          open={open}
        >
          {options.map((option) => (
            <button
              className={`menu-option ${option.value === props.value ? "active" : ""}`}
              key={option.value || "auto"}
              type="button"
              onClick={() => {
                props.onSelect(option.value);
                setOpen(false);
              }}
            >
              <span className="menu-option-primary">{option.label}</span>
            </button>
          ))}
        </DropdownPortal>
      ) : null}
    </div>
  );
}

function LocaleTagPicker(props: {
  label: string;
  values: string[];
  excludedValues: string[];
  onChange: (values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = normalizeLocaleInput(query);
  const trimmedQuery = query.toLowerCase().trim();
  const suggestions = trimmedQuery
    ? localeSuggestions
        .filter((locale) => !props.values.includes(locale) && !props.excludedValues.includes(locale))
        .filter((locale) => locale !== normalizedQuery)
        .filter((locale) => locale.toLowerCase().includes(trimmedQuery))
        .slice(0, 16)
    : [];

  function add(locale: string) {
    if (!props.values.includes(locale)) {
      props.onChange([...props.values, locale].sort());
    }
    setQuery("");
  }

  return (
    <section className="locale-tag-picker">
      <h3>{props.label}</h3>
      <div className="locale-chip-list">
        {props.values.length ? props.values.map((locale) => (
          <button type="button" key={locale} onClick={() => props.onChange(props.values.filter((candidate) => candidate !== locale))}>
            {locale}
            <span>x</span>
          </button>
        )) : <span>No locales selected.</span>}
      </div>
      <label>
        <span>Add locale</span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="en-US" />
      </label>
      {trimmedQuery ? (
        <div className="locale-suggestion-list">
          {normalizedQuery && !props.values.includes(normalizedQuery) && !props.excludedValues.includes(normalizedQuery) ? (
            <button type="button" onClick={() => add(normalizedQuery)}>
              {normalizedQuery}
            </button>
          ) : null}
          {suggestions.map((locale) => (
            <button type="button" key={locale} onClick={() => add(locale)}>
              {locale}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function SingleLocaleField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  warning?: string | null;
}) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLLabelElement | null>(null);
  const normalizedValue = normalizeLocaleInput(props.value);
  const trimmedQuery = props.value.toLowerCase().trim();
  const invalidValue = Boolean(trimmedQuery && !normalizedValue);
  const suggestions = focused && trimmedQuery
    ? localeSuggestions
        .filter((locale) => locale !== normalizedValue)
        .filter((locale) => locale.toLowerCase().includes(trimmedQuery))
        .slice(0, 16)
    : [];

  function select(locale: string) {
    props.onChange(locale);
    setFocused(false);
  }

  return (
    <label className="single-locale-field" ref={ref}>
      <span>{props.label}</span>
      <input
        value={props.value}
        onBlur={() => window.setTimeout(() => setFocused(false), 120)}
        onChange={(event) => props.onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        placeholder="en-US"
        aria-required={props.required || undefined}
        aria-invalid={invalidValue || undefined}
      />
      {props.warning ? <p className="field-warning">{props.warning}</p> : null}
      {focused && trimmedQuery ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="single-locale-suggestions"
          matchAnchorWidth={true}
          onDismiss={() => setFocused(false)}
          open={focused && Boolean(trimmedQuery)}
        >
          {normalizedValue ? (
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => select(normalizedValue)}>
              {normalizedValue}
            </button>
          ) : null}
          {suggestions.map((locale) => (
            <button type="button" key={locale} onMouseDown={(event) => event.preventDefault()} onClick={() => select(locale)}>
              {locale}
            </button>
          ))}
        </DropdownPortal>
      ) : null}
    </label>
  );
}

function derivePrimaryPlatform(iosAppStoreId: string, androidPackageName: string): Platform {
  return androidPackageName.trim() && !iosAppStoreId.trim() ? "ANDROID" : "IOS";
}

function StoreLookupField(props: {
  context: SessionState;
  platform: Platform;
  label: string;
  placeholder: string;
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (result: StoreAppSearchResult) => void;
}) {
  const [results, setResults] = useState<StoreAppSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLLabelElement | null>(null);
  const query = props.value.trim();

  useEffect(() => {
    if (query.length < 2 || (props.platform === "ANDROID" && isLikelyAndroidPackageName(query))) {
      setResults([]);
      setSearching(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setSearching(true);
    setError(null);

    const timeout = window.setTimeout(() => {
      const search = props.platform === "IOS" ? searchAppStore : searchGooglePlay;
      search(query, props.context, controller.signal)
        .then((nextResults) => {
          if (!cancelled) {
            setResults(nextResults);
          }
        })
        .catch((searchError: unknown) => {
          if (isAbortError(searchError)) {
            return;
          }
          if (!cancelled) {
            setResults([]);
            setError(searchError instanceof Error ? searchError.message : "Store lookup failed.");
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, 260);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [props.context, props.platform, query]);

  const showResults = focused && (results.length > 0 || searching || Boolean(error));

  return (
    <label className="store-lookup-field" ref={ref}>
      <span>{props.label}</span>
      <input
        value={props.value}
        onBlur={() => window.setTimeout(() => setFocused(false), 140)}
        onChange={(event) => props.onValueChange(event.target.value)}
        onFocus={() => setFocused(true)}
        placeholder={props.placeholder}
      />
      {showResults ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="store-lookup-results"
          matchAnchorWidth={true}
          onDismiss={() => setFocused(false)}
          open={showResults}
        >
          {searching ? <div className="store-lookup-status">Searching...</div> : null}
          {error ? <div className="store-lookup-status danger">{error}</div> : null}
          {results.map((result) => (
            <button
              className="store-lookup-result"
              key={`${result.platform}:${result.identifier}`}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                props.onSelect(result);
                setFocused(false);
              }}
            >
              {result.iconUrl ? <img alt="" src={result.iconUrl} referrerPolicy="no-referrer" /> : <span />}
              <span>
                <strong>{result.name}</strong>
                <small>
                  {result.developerName ? `${result.developerName} / ` : ""}
                  {result.identifier}
                </small>
              </span>
            </button>
          ))}
        </DropdownPortal>
      ) : null}
    </label>
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function isLikelyAndroidPackageName(value: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(value.trim());
}

function ShortReviewLinkRedirect(props: {
  code: string;
}) {
  const session = useMemo(createPublicSession, []);
  const [message, setMessage] = useState("Opening review link...");

  useEffect(() => {
    let cancelled = false;
    resolveReviewLink(props.code, session)
      .then((resolved) => {
        if (!cancelled) {
          window.location.replace(createReviewShareUrlFromPayload(resolved.appId, resolved.payload));
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setMessage(error.message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.code, session]);

  return (
    <div className="page-shell">
      <header className="top-bar">
        <div className="brand-lockup">
          <span className="brand-mark"><ScreenshotReviewLogo /></span>
          <strong>SCREENSHOT.REVIEW</strong>
        </div>
      </header>
      <main className="workspace-shell">
        <div className="store-screenshot-empty">{message}</div>
      </main>
    </div>
  );
}

function PublicSharedWorkspace(props: {
  appId: string;
  session?: SessionState;
  currentUser?: UserProfile | null;
  onSessionUpdated?: (session: SessionState) => void;
  onUserUpdated?: (user: UserProfile) => void;
}) {
  const publicSession = useMemo(createPublicSession, []);
  const [localSession, setLocalSession] = useState<SessionState | null>(null);
  const [localUser, setLocalUser] = useState<UserProfile | null>(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const session = localSession && !localSession.isAnonymous ? localSession : props.session ?? localSession ?? publicSession;
  const currentUser = localSession && !localSession.isAnonymous ? localUser ?? props.currentUser : props.currentUser ?? localUser;
  const reviewLinkConfig = useMemo(readReviewLinkConfigFromUrl, []);
  const [app, setApp] = useState<AppSummary | null>(null);
  const [screenshots, setScreenshots] = useState<StoreScreenshot[]>([]);
  const [storeLocales, setStoreLocales] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showSignIn, setShowSignIn] = useState(() => isFirebaseEmailSignInLink() || isFirebasePasswordResetLink());
  const signedInEmail = session.mode === "firebase" && !session.isAnonymous
    ? currentUser?.email ?? session.email ?? "Account"
    : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage(null);
    Promise.all([getApp(props.appId, session), listStoreScreenshots(props.appId, session)])
      .then(([nextApp, nextScreenshots]) => {
        if (!cancelled) {
          setApp(nextApp);
          setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setMessage(error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.appId, session]);

  async function refreshSignedScreenshotUrls(): Promise<StoreScreenshot[]> {
    const nextScreenshots = await listStoreScreenshots(props.appId, session);
    const mergedScreenshots = preserveReusableScreenshotImageUrls(screenshots, nextScreenshots);
    setScreenshots(mergedScreenshots);
    return mergedScreenshots;
  }

  function updateCommentCounts(screenshotId: string, comments: StoreScreenshotComment[]) {
    const commentCounts = storeScreenshotCommentCountPatch(comments);
    setScreenshots((current) =>
      current.map((screenshot) => screenshot.id === screenshotId ? { ...screenshot, ...commentCounts } : screenshot)
    );
  }

  function handleSharedSignedIn(firebaseUser: FirebaseUser) {
    const nextSession = createFirebaseSession(firebaseUser);
    setLocalSession(nextSession);
    props.onSessionUpdated?.(nextSession);
    setShowSignIn(false);
    if (!nextSession.isAnonymous) {
      getMe(nextSession)
        .then((profile) => {
          setLocalUser(profile);
          props.onUserUpdated?.(profile);
        })
        .catch((error: Error) => setMessage(error.message));
    }
  }

  return (
    <div className="page-shell">
      <header className="top-bar">
        <div className="brand-lockup">
          <span className="brand-mark"><ScreenshotReviewLogo /></span>
          <strong>SCREENSHOT.REVIEW</strong>
        </div>
        <div className="top-controls">
          {signedInEmail ? (
            <div className="menu-shell" ref={accountMenuRef}>
              <button
                className={`ghost-button menu-trigger account-trigger ${showAccountMenu ? "open" : ""}`}
                type="button"
                onClick={() => setShowAccountMenu((current) => !current)}
              >
                <DropdownCopy eyebrow="Account" value={signedInEmail} />
                <ChevronDownIcon />
              </button>
              {showAccountMenu ? (
                <DropdownPortal
                  anchorRef={accountMenuRef}
                  className="account-menu-panel"
                  onDismiss={() => setShowAccountMenu(false)}
                  open={showAccountMenu}
                >
                  <button
                    className="menu-option danger"
                    type="button"
                    onClick={() => {
                      setShowAccountMenu(false);
                      setLocalSession(null);
                      setLocalUser(null);
                      void signOutOfFirebase();
                    }}
                  >
                    <span className="menu-option-primary">Sign out</span>
                    <span className="menu-option-secondary">Leave this browser session.</span>
                  </button>
                </DropdownPortal>
              ) : null}
            </div>
          ) : isFirebaseConfigured() ? (
            <button className="ghost-button" type="button" onClick={() => setShowSignIn(true)}>
              Sign in
            </button>
          ) : null}
        </div>
      </header>

      {showSignIn ? (
        <>
          <ModalScrollLock />
          <div className="modal-backdrop auth-modal-backdrop" role="presentation" onMouseDown={() => setShowSignIn(false)}>
            <section className="modal-panel auth-modal-panel" role="dialog" aria-modal="true" aria-labelledby="shared-auth-title" onMouseDown={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <p className="eyebrow">Screenshot Review</p>
                  <h2 id="shared-auth-title">{isFirebasePasswordResetLink() ? "Reset password" : "Sign in"}</h2>
                </div>
                <button className="ghost-button icon-button" type="button" onClick={() => setShowSignIn(false)} aria-label="Close">
                  <XIcon />
                </button>
              </div>
              <EmailPasswordAuthForm
                anonymousMigrationAvailable={session.isAnonymous}
                onSignedIn={handleSharedSignedIn}
                onUserUpdated={props.onUserUpdated}
              />
            </section>
          </div>
        </>
      ) : null}

      <FloatingNotifications message={message} onDismiss={() => setMessage(null)} />

      <main className="workspace-shell">
        {app ? (
          <section className="workspace-grid owner-grid">
            <StoreScreenshotGrid
              app={app}
              session={session}
              requestedLocales={storeLocales.length ? storeLocales : reviewLinkConfig.locales}
              referenceLocale={app.referenceLocale ?? null}
              screenshots={screenshots}
              userLanguages={[]}
              currentUser={currentUser ?? null}
              currentUserId={currentUser?.id ?? null}
              loading={loading}
              message={message}
              refreshing={false}
              canRefresh={false}
              canAddLocale={false}
              showReviewerIntro
              reviewLinkConfig={reviewLinkConfig}
              showRefresh={false}
              canEditDescriptions={false}
              onRefreshSignedScreenshotUrls={refreshSignedScreenshotUrls}
              onUploadRestricted={() => setShowSignIn(true)}
              onSignInRequired={() => setShowSignIn(true)}
              onAddLocale={(locale) => {
                setStoreLocales((current) => (current.includes(locale) ? current : [...current, locale].sort()));
              }}
              onCommentsChanged={updateCommentCounts}
            />
          </section>
        ) : (
          <div className="store-screenshot-empty">{loading ? "Loading shared workspace..." : "Shared workspace is not available."}</div>
        )}
      </main>
    </div>
  );
}

function OwnedAppWorkspace(props: {
  app: AppSummary;
  session: SessionState;
  user: UserProfile | null;
  onUserUpdated: (user: UserProfile) => void;
  localeSelectionResetAppId: string | null;
  onLocaleSelectionReset: () => void;
  onOpenSubscription: () => void;
}) {
  const [screenshots, setScreenshots] = useState<StoreScreenshot[]>([]);
  const [storeLocales, setStoreLocales] = useState<string[]>([]);
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
  const [loadingScreenshots, setLoadingScreenshots] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [storeMessage, setStoreMessage] = useState<string | null>(null);
  const [showReviewLinkModal, setShowReviewLinkModal] = useState(false);
  const [createdReviewLinkUrl, setCreatedReviewLinkUrl] = useState<string | null>(null);
  const [reviewLinks, setReviewLinks] = useState<ReviewLinkSummary[]>([]);
  const [loadingReviewLinks, setLoadingReviewLinks] = useState(false);
  const [showReviewLinksModal, setShowReviewLinksModal] = useState(false);
  const [openCommentsRequestKey, setOpenCommentsRequestKey] = useState(0);
  const notify = useFloatingNotification();

  useEffect(() => {
    let cancelled = false;
    setLoadingScreenshots(true);
    listStoreScreenshots(props.app.id, props.session)
      .then((nextScreenshots) => {
        if (!cancelled) {
          setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setStoreMessage(error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingScreenshots(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.app.id, props.session]);

  useEffect(() => {
    let cancelled = false;
    getBillingAccount(props.session)
      .then((account) => {
        if (!cancelled) {
          setBillingAccount(account);
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setStoreMessage(error.message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.session]);

  useEffect(() => {
    let cancelled = false;
    setLoadingReviewLinks(true);
    listReviewLinks(props.app.id, props.session)
      .then((links) => {
        if (!cancelled) {
          setReviewLinks(links);
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setStoreMessage(error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingReviewLinks(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.app.id, props.session]);

  const localeCount = new Set(screenshots.filter((image) => image.imageType !== appIconImageType).map((image) => image.localeTag)).size;
  const openCommentCount = screenshots.reduce((total, screenshot) =>
    total +
    (screenshot.unresolvedCommentCount ?? screenshot.commentCount) +
    (screenshot.unresolvedOkCount ?? 0) +
    (screenshot.unresolvedNeedsWorkCount ?? screenshot.needsWorkCount),
    0
  );
  const referenceLocales = props.app.referenceLocale ? [props.app.referenceLocale] : [];
  const reviewLocaleOptions = useMemo(() => {
    const screenshotLocales = screenshots
      .filter((image) => image.imageType !== appIconImageType)
      .map((image) => image.localeTag);
    return sortLocalesWithReference(
      Array.from(new Set([...referenceLocales, ...storeLocales, ...screenshotLocales])),
      props.app.referenceLocale ?? null
    );
  }, [props.app.referenceLocale, referenceLocales.join("|"), screenshots, storeLocales]);

  const canRefreshFromStores = Boolean(props.app.iosAppStoreId || props.app.androidPackageName);
  const canUpload = billingAccount?.uploadEnabled ?? false;
  const onUploadRestricted = props.onOpenSubscription;

  async function refreshSignedScreenshotUrls(): Promise<StoreScreenshot[]> {
    const nextScreenshots = await listStoreScreenshots(props.app.id, props.session);
    const mergedScreenshots = preserveReusableScreenshotImageUrls(screenshots, nextScreenshots);
    setScreenshots(mergedScreenshots);
    return mergedScreenshots;
  }

  async function refreshScreenshots(locales: string[], platform?: Platform) {
    setRefreshing(true);
    setStoreMessage(null);
    try {
      const response = await refreshStoreScreenshots(props.app.id, locales, props.session, platform);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, response.screenshots));
      const refreshMessage = storeRefreshFeedbackMessage(response.summary);
      setStoreMessage(refreshMessage);
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to refresh screenshots.");
    } finally {
      setRefreshing(false);
    }
  }

  async function stackScreenshots(screenshotId: string, targetScreenshotId: string) {
    try {
      const nextScreenshots = await stackStoreScreenshot(props.app.id, screenshotId, targetScreenshotId, props.session);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
      notify("Stacked screenshot history.");
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to stack screenshots.");
    }
  }

  async function extractScreenshotFromStack(screenshotId: string) {
    try {
      const nextScreenshots = await unstackStoreScreenshot(props.app.id, screenshotId, props.session);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
      notify("Extracted screenshot from stack.");
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to extract screenshot from stack.");
    }
  }

  async function saveScreenshotDescription(screenshotId: string, description: string | null) {
    const nextScreenshots = await updateStoreScreenshotDescription(
      props.app.id,
      screenshotId,
      { description },
      props.session
    );
    setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
    notify("Saved screenshot description.");
  }

  async function uploadScreenshots(
    platform: Platform,
    localeTag: string,
    files: File[],
    items: UploadStoreScreenshotAssignment[],
    onProgress?: (progress: UploadStoreScreenshotProgress) => void
  ) {
    try {
      const nextScreenshots = await uploadStoreScreenshots(props.app.id, platform, localeTag, files, items, props.session, onProgress);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
      setStoreMessage(null);
    } catch (error) {
      if (isPaymentRequiredError(error)) {
        props.onOpenSubscription();
      }
      setStoreMessage(error instanceof Error ? error.message : "Failed to upload screenshots.");
      throw error;
    }
  }

  async function deleteUploadedScreenshot(screenshotId: string) {
    try {
      const nextScreenshots = await deleteUploadedStoreScreenshot(props.app.id, screenshotId, props.session);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
      setStoreMessage(null);
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to delete uploaded screenshot.");
    }
  }

  async function removeStoreSyncedScreenshot(screenshotId: string) {
    try {
      const nextScreenshots = await removeStoreScreenshot(props.app.id, screenshotId, props.session);
      setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
      setStoreMessage(null);
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to remove store screenshot.");
    }
  }

  async function refreshReviewLinks() {
    setLoadingReviewLinks(true);
    try {
      const links = await listReviewLinks(props.app.id, props.session);
      setReviewLinks(links);
    } catch (error) {
      setStoreMessage(error instanceof Error ? error.message : "Failed to load review links.");
    } finally {
      setLoadingReviewLinks(false);
    }
  }

  async function handleDeleteReviewLink(code: string) {
    await deleteReviewLink(props.app.id, code, props.session);
    setReviewLinks((current) => current.filter((link) => link.code !== code));
  }

  async function addLocaleAndRefresh(locale: string) {
    const normalizedLocale = normalizeLocaleInput(locale);
    if (!normalizedLocale) {
      return;
    }
    setStoreLocales((current) =>
      sortLocalesWithReference(
        Array.from(new Set([...current, normalizedLocale])),
        props.app.referenceLocale ?? null
      )
    );
    if (canRefreshFromStores) {
      await refreshScreenshots([normalizedLocale]);
    }
  }

  return (
    <section className="workspace-grid owner-grid">
      <div className="owner-metric-row">
        <LocalesCard
          value={String(localeCount)}
          existingLocales={reviewLocaleOptions}
          disabled={refreshing}
          onAddLocale={addLocaleAndRefresh}
        />
        <ReviewLinksCard
          value={loadingReviewLinks ? "..." : String(reviewLinks.length)}
          onCreate={() => {
            setCreatedReviewLinkUrl(null);
            setShowReviewLinkModal(true);
          }}
          onOpen={() => setShowReviewLinksModal(true)}
        />
        <MetricCard
          label="Open comments"
          value={String(openCommentCount)}
          detail="Native-speaker notes to resolve."
          onOpen={() => setOpenCommentsRequestKey((current) => current + 1)}
        />
      </div>
      <StoreScreenshotGrid
        app={props.app}
        session={props.session}
        requestedLocales={storeLocales}
        referenceLocale={props.app.referenceLocale ?? null}
        screenshots={screenshots}
        userLanguages={props.user?.languages ?? []}
        currentUser={props.user}
        currentUserId={props.user?.id ?? null}
        loading={loadingScreenshots}
        message={storeMessage}
        refreshing={refreshing}
        localeSelectionResetAppId={props.localeSelectionResetAppId}
        canRefresh={canRefreshFromStores}
        canAddLocale={true}
        onAddLocale={addLocaleAndRefresh}
        onCommentsChanged={(screenshotId, comments) => {
          const commentCounts = storeScreenshotCommentCountPatch(comments);
          setScreenshots((current) =>
            current.map((screenshot) => screenshot.id === screenshotId ? { ...screenshot, ...commentCounts } : screenshot)
          );
        }}
        onRefreshSignedScreenshotUrls={refreshSignedScreenshotUrls}
        onRefreshPlatformLocale={(platform, locale) => refreshScreenshots([locale], platform)}
        onStackScreenshots={stackScreenshots}
        onExtractScreenshot={extractScreenshotFromStack}
        onUpdateDescription={saveScreenshotDescription}
        onUploadScreenshots={uploadScreenshots}
        onDeleteUploadedScreenshot={deleteUploadedScreenshot}
        onRemoveStoreScreenshot={removeStoreSyncedScreenshot}
        canEditDescriptions={true}
        canUpload={canUpload}
        openCommentsRequestKey={openCommentsRequestKey}
        onLocaleSelectionReset={props.onLocaleSelectionReset}
        onUploadRestricted={onUploadRestricted}
        onUserUpdated={props.onUserUpdated}
      />
      {showReviewLinkModal ? (
        <CreateReviewLinkModal
          app={props.app}
          session={props.session}
          localeOptions={reviewLocaleOptions}
          referenceLocales={referenceLocales}
          screenshots={screenshots}
          onClose={() => setShowReviewLinkModal(false)}
          onCreated={(url) => {
            setShowReviewLinkModal(false);
            setCreatedReviewLinkUrl(url);
            void refreshReviewLinks();
          }}
        />
      ) : null}
      {createdReviewLinkUrl ? (
        <ReviewLinkCreatedModal
          url={createdReviewLinkUrl}
          onClose={() => setCreatedReviewLinkUrl(null)}
        />
      ) : null}
      {showReviewLinksModal ? (
        <ReviewLinksModal
          links={reviewLinks}
          loading={loadingReviewLinks}
          onClose={() => setShowReviewLinksModal(false)}
          onCopyError={(message) => setStoreMessage(message)}
          onDelete={handleDeleteReviewLink}
          onRefresh={() => void refreshReviewLinks()}
        />
      ) : null}
    </section>
  );
}

function CreateReviewLinkModal(props: {
  app: AppSummary;
  session: SessionState;
  localeOptions: string[];
  referenceLocales: string[];
  screenshots: StoreScreenshot[];
  onClose: () => void;
  onCreated: (url: string) => void;
}) {
  useModalScrollLock();
  const [selectedLocales, setSelectedLocales] = useState<string[]>(() => initialReviewLinkLocales(props.referenceLocales));
  const [cropFocus, setCropFocus] = useState<ReviewLinkCropFocus>(defaultReviewLinkCropFocus);
  const [welcomeGroupKey, setWelcomeGroupKey] = useState<string | null>(null);
  const [welcomeLocale, setWelcomeLocale] = useState<string | null>(null);
  const [welcomePosition, setWelcomePosition] = useState<number | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const allLocalesSelected = selectedLocales.includes("ALL");
  const selectedLocaleValues = selectedLocales.filter((locale) => locale !== "ALL");
  const requiredReferenceLocales = initialReviewLinkLocales(props.referenceLocales).filter((locale) => locale !== "ALL");
  const referenceLocaleSet = new Set(requiredReferenceLocales);
  const localeOptionValues = Array.from(new Set(
    props.localeOptions
      .map((locale) => normalizeLocaleInput(locale))
      .filter((locale): locale is string => Boolean(locale))
  ));
  const hasSelectedReferenceLocale = allLocalesSelected
    ? requiredReferenceLocales.length > 0
    : selectedLocaleValues.some((locale) => referenceLocaleSet.has(locale));
  const selectedReviewLocaleValues = allLocalesSelected
    ? localeOptionValues.filter((locale) => !referenceLocaleSet.has(locale))
    : selectedLocaleValues.filter((locale) => !referenceLocaleSet.has(locale));
  const hasSelectedReviewLocale = selectedReviewLocaleValues.length > 0;
  const canCreate = hasSelectedReferenceLocale && hasSelectedReviewLocale;
  const reviewLinkRequirementMessage = "Select at least one reference and one non-reference locale.";
  const sanitizedWelcomeMessage = sanitizeReviewLinkMessage(welcomeMessage);
  const previewAppIconKeys = useMemo(() => {
    const previewAppIcons = uniqueImages(props.screenshots.filter((screenshot) => screenshot.imageType === appIconImageType));
    return new Set(previewAppIcons.map((image) => canonicalImageKey(image.imageUrl)));
  }, [props.screenshots]);
  const previewScreenshots = props.screenshots.filter((screenshot) =>
    screenshot.imageType !== appIconImageType &&
    !isIconLikeImage(screenshot, previewAppIconKeys)
  );
  const welcomeGroupOptions = buildWelcomeGroupOptions(previewScreenshots);
  const previewGroupScreenshots = welcomeScreenshotsForGroup(previewScreenshots, welcomeGroupKey);
  const welcomePositionOptions = buildWelcomePositionOptions(previewGroupScreenshots);
  const previewWelcomeLocaleOptions = welcomeLocaleOptionsForScreenshots(previewScreenshots, requiredReferenceLocales, welcomePosition, welcomeGroupKey);
  const previewReviewLocaleOptions = previewWelcomeLocaleOptions.filter((locale) => !referenceLocaleSet.has(locale));
  const previewLocalePickerOptions = hasSelectedReviewLocale ? selectedReviewLocaleValues : previewReviewLocaleOptions;
  const defaultSelectedLocales = initialCreateReviewLinkLocales(previewScreenshots, requiredReferenceLocales);
  const defaultSelectedLocalesSignature = defaultSelectedLocales.join("|");
  const previewSelectedLocale = hasSelectedReviewLocale
    ? welcomeLocale && selectedReviewLocaleValues.includes(welcomeLocale)
      ? welcomeLocale
      : selectedReviewLocaleValues[0] ?? previewReviewLocaleOptions[0] ?? null
    : null;
  const previewWelcome = selectReviewerWelcomeScreenshots(previewScreenshots, requiredReferenceLocales, selectedLocales, {
    groupKey: welcomeGroupKey,
    locale: previewSelectedLocale,
    position: welcomePosition
  });
  const previewLanguageName = languageNameForLocale(previewSelectedLocale);
  const defaultPreviewTitle = previewLanguageName ? `Check ${previewLanguageName} wording` : "Review localized text";
  const previewTitle = sanitizedWelcomeMessage || defaultPreviewTitle;
  const previewDisplayWelcome = hasSelectedReviewLocale
    ? previewWelcome
    : { ...previewWelcome, reviewImage: null, startImage: null, reviewLocale: null };
  const welcomeMessageRows = reviewLinkMessageInputRows(welcomeMessage);

  useEffect(() => {
    setSelectedLocales(defaultSelectedLocales);
    setCropFocus(defaultReviewLinkCropFocus);
    setWelcomeGroupKey(null);
    setWelcomeLocale(null);
    setWelcomePosition(null);
    setWelcomeMessage("");
    setCreateError(null);
    setCreating(false);
  }, [defaultSelectedLocalesSignature, props.app.id]);

  useEffect(() => {
    if (welcomeGroupKey !== null && !welcomeGroupOptions.some((option) => option.value === welcomeGroupKey)) {
      setWelcomeGroupKey(null);
    }
  }, [welcomeGroupKey, welcomeGroupOptions.map((option) => option.value).join("|")]);

  useEffect(() => {
    if (welcomePosition !== null && !welcomePositionOptions.some((option) => option.value === String(welcomePosition))) {
      setWelcomePosition(null);
    }
  }, [welcomePosition, welcomePositionOptions.map((option) => option.value).join("|")]);

  useEffect(() => {
    if (welcomeLocale && !selectedReviewLocaleValues.includes(welcomeLocale)) {
      setWelcomeLocale(null);
    }
  }, [selectedReviewLocaleValues.join("|"), welcomeLocale]);

  function updateSelectedLocales(locales: string[]) {
    setSelectedLocales(locales);
    setCreateError(null);
  }

  function updatePreviewWelcomeLocale(locale: string) {
    setWelcomeLocale(locale);
    if (selectedReviewLocaleValues.length === 0 && !referenceLocaleSet.has(locale)) {
      setSelectedLocales((current) => {
        if (current.includes("ALL") || current.includes(locale)) {
          return current;
        }
        return Array.from(new Set([...requiredReferenceLocales, ...current, locale]));
      });
    }
    setCreateError(null);
  }

  function updateCropFocus(nextFocus: ReviewLinkCropFocus) {
    setCropFocus(nextFocus);
    setCreateError(null);
  }

  function updateWelcomeGroup(value: string) {
    setWelcomeGroupKey(value === "AUTO" ? null : value);
    setWelcomePosition(null);
    setCreateError(null);
  }

  function updateWelcomePosition(value: string) {
    setWelcomePosition(value === "AUTO" ? null : Number(value));
    setCreateError(null);
  }

  function updateWelcomeMessage(value: string) {
    setWelcomeMessage(limitReviewLinkMessageInput(value));
    setCreateError(null);
  }

  async function handleCreateReviewLink() {
    if (!canCreate) {
      setCreateError(reviewLinkRequirementMessage);
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await createReviewLink(props.app.id, {
        locales: selectedLocales,
        cropFocus,
        message: sanitizedWelcomeMessage || null,
        welcomeGroupKey,
        welcomeLocale: previewSelectedLocale,
        welcomePosition
      }, props.session);
      props.onCreated(createAbsoluteReviewLinkUrl(created.path));
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Failed to create review link.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel review-link-modal" role="dialog" aria-modal="true" aria-labelledby="review-link-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 id="review-link-title">Create link</h2>
          </div>
        </div>
        <div className="review-link-body">
          <div className="review-link-config-panel">
            <label className="review-link-message-field">
              <span>Welcome title</span>
              <textarea
                value={welcomeMessage}
                maxLength={reviewLinkMessageMaxLength}
                rows={welcomeMessageRows}
                onChange={(event) => updateWelcomeMessage(event.target.value)}
                placeholder={defaultPreviewTitle}
              />
            </label>
            <StoreControlDropdown
              className="welcome-control"
              eyebrow="Screenshot group"
              minWidth={300}
              options={[{ label: "Auto", value: "AUTO" }, ...welcomeGroupOptions]}
              value={welcomeGroupKey ?? "AUTO"}
              onSelect={updateWelcomeGroup}
            />
            <StoreControlDropdown
              className="welcome-control"
              eyebrow="Welcome screenshot"
              minWidth={220}
              options={[{ label: "Auto", value: "AUTO" }, ...welcomePositionOptions]}
              value={welcomePosition === null ? "AUTO" : String(welcomePosition)}
              onSelect={updateWelcomePosition}
            />
            <ReviewLinkCropFocusControl value={cropFocus} onChange={updateCropFocus} />
            <ReviewLinkLocaleSelector
              localeOptions={props.localeOptions}
              referenceLocales={props.referenceLocales}
              selectedLocales={selectedLocales}
              onChange={updateSelectedLocales}
            />
            {createError ? (
              <p className="error-copy">{createError}</p>
            ) : !canCreate ? (
              <p className="error-copy">{reviewLinkRequirementMessage}</p>
            ) : null}
          </div>
          <div className="review-link-preview-panel">
            <span>Welcome preview</span>
            <ReviewerIntroCard
              appName={props.app.name}
              loading={false}
              title={previewTitle}
              referenceImage={previewDisplayWelcome.referenceImage}
              reviewImage={previewDisplayWelcome.reviewImage}
              localeOptions={previewLocalePickerOptions}
              selectedLocale={previewSelectedLocale}
              localePlaceholder="Select language"
              cropFocus={cropFocus}
              preview
              canStart={hasSelectedReviewLocale && Boolean(previewDisplayWelcome.startImage)}
              onLocaleChange={updatePreviewWelcomeLocale}
              onStartReview={() => undefined}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button className="ghost-button" type="button" onClick={props.onClose}>
            Cancel
          </button>
          <button className="primary-button" type="button" disabled={!canCreate || creating} onClick={() => void handleCreateReviewLink()}>
            <LinkIcon />
            {creating ? "Creating..." : "Create link"}
          </button>
        </div>
      </section>
    </div>
  );
}

function ReviewLinkCreatedModal(props: {
  url: string;
  onClose: () => void;
}) {
  useModalScrollLock();
  const linkInputId = useId();
  const [copied, setCopied] = useState(false);
  const copiedResetTimeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (copiedResetTimeoutRef.current !== null) {
      window.clearTimeout(copiedResetTimeoutRef.current);
    }
  }, []);

  async function copyReviewLink() {
    try {
      await copyTextToClipboard(props.url);
      setCopied(true);
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current);
      }
      copiedResetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copiedResetTimeoutRef.current = null;
      }, 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel review-link-created-modal" role="dialog" aria-modal="true" aria-labelledby="review-link-created-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 id="review-link-created-title">Link created</h2>
          </div>
          <button className="ghost-button icon-button" type="button" onClick={props.onClose} aria-label="Close">
            <XIcon />
          </button>
        </div>
        <div className="review-link-created-body">
          <div className="review-link-created-field">
            <label htmlFor={linkInputId}>Review link</label>
            <div
              className="review-link-created-input-wrap"
              role="button"
              tabIndex={0}
              aria-label="Copy review link"
              onClick={() => void copyReviewLink()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void copyReviewLink();
                }
              }}
            >
              <input
                id={linkInputId}
                readOnly
                value={props.url}
                onFocus={(event) => event.currentTarget.select()}
              />
              <button
                className={`review-link-created-copy ${copied ? "copied" : ""}`}
                type="button"
                aria-label={copied ? "Copied" : "Copy review link"}
                onClick={(event) => {
                  event.stopPropagation();
                  void copyReviewLink();
                }}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewLinksModal(props: {
  links: ReviewLinkSummary[];
  loading: boolean;
  onClose: () => void;
  onCopyError: (message: string) => void;
  onDelete: (code: string) => Promise<void>;
  onRefresh: () => void;
}) {
  useModalScrollLock();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [confirmingDeleteCode, setConfirmingDeleteCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const totalUses = props.links.reduce((total, link) => total + link.useCount, 0);

  async function copyReviewLink(link: ReviewLinkSummary) {
    try {
      await copyTextToClipboard(createAbsoluteReviewLinkUrl(link.path));
      setCopiedCode(link.code);
      window.setTimeout(() => {
        setCopiedCode((current) => current === link.code ? null : current);
      }, 1800);
    } catch (error) {
      props.onCopyError(error instanceof Error ? error.message : "Failed to copy review link.");
    }
  }

  async function deleteLink(code: string) {
    setDeletingCode(code);
    try {
      await props.onDelete(code);
      setConfirmingDeleteCode(null);
    } catch (error) {
      props.onCopyError(error instanceof Error ? error.message : "Failed to delete review link.");
    } finally {
      setDeletingCode(null);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={props.onClose}>
      <section className="modal-panel review-links-modal" role="dialog" aria-modal="true" aria-labelledby="review-links-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Review links</p>
            <h2 id="review-links-title">Generated links</h2>
          </div>
          <button className="ghost-button icon-button" type="button" onClick={props.onClose} aria-label="Close">
            <XIcon />
          </button>
        </div>
        <div className="review-links-summary">
          <span>
            <strong>{props.links.length}</strong>
            Active links
          </span>
          <span>
            <strong>{totalUses}</strong>
            Total opens
          </span>
          <button className="ghost-button compact" type="button" disabled={props.loading} onClick={props.onRefresh}>
            {props.loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        <div className="review-links-scroll">
          {props.links.length ? (
            <table className="admin-table review-links-table">
              <thead>
                <tr>
                  <th>Link</th>
                  <th>Configuration</th>
                  <th>Created</th>
                  <th>Usage</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {props.links.map((link) => (
                  <tr key={link.code}>
                    <td>
                      <div
                        className="review-links-copy-field"
                        role="button"
                        tabIndex={0}
                        aria-label="Copy review link"
                        onClick={() => void copyReviewLink(link)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            void copyReviewLink(link);
                          }
                        }}
                      >
                        <input
                          readOnly
                          value={createAbsoluteReviewLinkUrl(link.path)}
                          onFocus={(event) => event.currentTarget.select()}
                        />
                        <button
                          className={`review-link-created-copy ${copiedCode === link.code ? "copied" : ""}`}
                          type="button"
                          aria-label={copiedCode === link.code ? "Copied" : "Copy review link"}
                          onClick={(event) => {
                            event.stopPropagation();
                            void copyReviewLink(link);
                          }}
                        >
                          {copiedCode === link.code ? <CheckIcon /> : <CopyIcon />}
                        </button>
                      </div>
                    </td>
                    <td>
                      <strong>{reviewLinkTitleSummary(link)}</strong>
                      <span>{reviewLinkLocaleSummary(link)}</span>
                    </td>
                    <td>
                      <strong>{formatScreenshotTimestamp(link.createdAt)}</strong>
                    </td>
                    <td>
                      <strong>{link.useCount}</strong>
                      <span>{link.lastUsedAt ? `Last opened ${formatScreenshotTimestamp(link.lastUsedAt)}` : "Not opened yet"}</span>
                    </td>
                    <td>
                      <div className="review-link-row-actions">
                        {confirmingDeleteCode === link.code ? (
                          <>
                            <button
                              className="ghost-button compact danger"
                              type="button"
                              disabled={deletingCode === link.code}
                              onClick={() => void deleteLink(link.code)}
                            >
                              {deletingCode === link.code ? "Deleting..." : "Confirm"}
                            </button>
                            <button className="ghost-button compact" type="button" onClick={() => setConfirmingDeleteCode(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="ghost-button compact danger review-link-delete-button"
                            type="button"
                            aria-label="Delete review link"
                            onClick={() => setConfirmingDeleteCode(link.code)}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="store-screenshot-empty">{props.loading ? "Loading review links..." : "No review links yet."}</div>
          )}
        </div>
      </section>
    </div>
  );
}

function ReviewLinkCropFocusControl(props: {
  value: ReviewLinkCropFocus;
  onChange: (value: ReviewLinkCropFocus) => void;
}) {
  return (
    <div className="review-link-focus-control">
      <span>Crop focus</span>
      <div className="review-link-focus-slider">
        <span>Top</span>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={props.value}
          onChange={(event) => props.onChange(normalizeReviewLinkCropFocus(Number(event.target.value)))}
          aria-label="Welcome screenshot crop focus"
        />
        <span>Bottom</span>
      </div>
    </div>
  );
}

function ReviewLinkLocaleSelector(props: {
  localeOptions: string[];
  referenceLocales: string[];
  selectedLocales: string[];
  onChange: (locales: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState<number | null>(null);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const normalizedQuery = normalizeLocaleInput(query);
  const trimmedQuery = query.toLowerCase().trim();
  const allLocalesSelected = props.selectedLocales.includes("ALL");
  const selectedLocaleValues = props.selectedLocales.filter((locale) => locale !== "ALL");
  const selectedLocaleSet = new Set(selectedLocaleValues);
  const requiredReferenceLocales = initialReviewLinkLocales(props.referenceLocales).filter((locale) => locale !== "ALL");
  const referenceLocaleSet = new Set(requiredReferenceLocales);
  const localeOptionSet = new Set(props.localeOptions);
  const existingOptions = [
    ...(allLocalesSelected ? [] : [{ kind: "all" as const, label: "All locales", detail: "", value: "ALL" }]),
    ...props.localeOptions
      .filter((locale) => allLocalesSelected || !selectedLocaleSet.has(locale))
      .map((locale) => ({ kind: "existing" as const, label: locale, detail: languageNameForLocale(locale) ?? "", value: locale }))
  ].filter((option) => !trimmedQuery || option.label.toLowerCase().includes(trimmedQuery) || option.detail.toLowerCase().includes(trimmedQuery));
  const canAddTypedLocale = Boolean(
    normalizedQuery &&
    normalizedQuery !== "ALL" &&
    !localeOptionSet.has(normalizedQuery) &&
    !selectedLocaleSet.has(normalizedQuery)
  );
  const addableLocales = trimmedQuery
    ? localeSuggestions
        .filter((locale) => !localeOptionSet.has(locale))
        .filter((locale) => !selectedLocaleSet.has(locale))
        .filter((locale) => locale !== normalizedQuery || !canAddTypedLocale)
        .filter((locale) => locale.toLowerCase().includes(trimmedQuery) || welcomeLocaleDisplayName(locale).toLowerCase().includes(trimmedQuery))
    : [];
  const addOptions = [
    ...(canAddTypedLocale && normalizedQuery ? [normalizedQuery] : []),
    ...addableLocales
  ].map((locale) => ({ kind: "add" as const, label: locale, value: locale }));
  const menuOptions = [...existingOptions, ...addOptions];
  const menuOptionSignature = menuOptions.map((option) => `${option.kind}:${option.value}`).join("|");
  const activeOptionIndex = hoveredOptionIndex ?? highlightedOptionIndex;
  const selectedChipValues = allLocalesSelected ? ["ALL"] : selectedLocaleValues;

  useEffect(() => {
    setHighlightedOptionIndex(menuOptions.length === 1 ? 0 : null);
    setHoveredOptionIndex(null);
  }, [menuOptions.length, menuOptionSignature, open, query]);

  useEffect(() => {
    if (!open || hoveredOptionIndex !== null || highlightedOptionIndex === null || menuOptions.length === 0) {
      return;
    }
    window.requestAnimationFrame(() => optionRefs.current[highlightedOptionIndex]?.scrollIntoView({ block: "nearest" }));
  }, [highlightedOptionIndex, hoveredOptionIndex, menuOptions.length, open]);

  useEffect(() => {
    if (open) {
      focusDropdownSearchInput(searchInputRef.current);
    }
  }, [open]);

  function normalizeSelectedLocales(locales: string[]): string[] {
    if (locales.includes("ALL")) {
      return ["ALL"];
    }

    const uniqueLocales = Array.from(new Set([...requiredReferenceLocales, ...locales]));
    const referenceLocales = requiredReferenceLocales.filter((locale) => uniqueLocales.includes(locale));
    const optionLocales = props.localeOptions.filter((locale) => uniqueLocales.includes(locale) && !referenceLocales.includes(locale));
    const customLocales = uniqueLocales.filter((locale) => !referenceLocales.includes(locale) && !optionLocales.includes(locale));
    return [...referenceLocales, ...optionLocales, ...customLocales];
  }

  function addLocale(locale: string) {
    props.onChange(normalizeSelectedLocales(addSelectedLocale(props.selectedLocales, locale)));
    setOpen(false);
    setQuery("");
  }

  function removeLocale(locale: string) {
    if (locale === "ALL") {
      props.onChange(requiredReferenceLocales.length ? requiredReferenceLocales : []);
      return;
    }
    if (referenceLocaleSet.has(locale)) {
      return;
    }
    props.onChange(normalizeSelectedLocales(selectedLocaleValues.filter((selectedLocale) => selectedLocale !== locale)));
  }

  function confirmOption(option: (typeof menuOptions)[number] | undefined) {
    if (!option) {
      return;
    }
    if (option.kind === "all") {
      props.onChange(["ALL"]);
      setOpen(false);
      setQuery("");
      return;
    }
    addLocale(option.value);
  }

  function handleLocaleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? 0 : (current + 1) % menuOptions.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? menuOptions.length - 1 : (current - 1 + menuOptions.length) % menuOptions.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const activeOption = activeOptionIndex === null ? undefined : menuOptions[activeOptionIndex];
      confirmOption(activeOption);
    }
    if (event.key === " ") {
      event.preventDefault();
      const activeOption = activeOptionIndex === null ? undefined : menuOptions[activeOptionIndex];
      confirmOption(activeOption);
    }
  }

  return (
    <div className="review-link-locale-selector" ref={ref}>
      <span className="review-link-control-label">Locales</span>
      <div className="review-link-chip-list" role="list" aria-label="Review link locales">
        {selectedChipValues.map((locale) => {
          const removable = locale === "ALL" || !referenceLocaleSet.has(locale);
          const label = locale === "ALL" ? "All locales" : locale;
          return removable ? (
            <button
              className="review-link-locale-chip"
              type="button"
              key={locale}
              onClick={() => removeLocale(locale)}
              aria-label={`Remove ${label}`}
            >
              <span>{label}</span>
              <XIcon />
            </button>
          ) : (
            <span className="review-link-locale-chip fixed" role="listitem" key={locale}>
              <span>{label}</span>
            </span>
          );
        })}
        <button
          className="review-link-add-locale-button"
          type="button"
          aria-label="Add locale"
          title="Add locale"
          onClick={() => {
            setOpen((current) => {
              const next = !current;
              if (next) {
                setQuery("");
                setHighlightedOptionIndex(null);
                setHoveredOptionIndex(null);
              }
              return next;
            });
          }}
        >
          <PlusIcon />
        </button>
      </div>
      {open ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="locale-dropdown-menu review-link-locale-menu"
          minWidth={260}
          onDismiss={() => setOpen(false)}
          onMouseLeave={() => setHoveredOptionIndex(null)}
          open={open}
        >
          <input
            className="locale-dropdown-search"
            ref={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleLocaleInputKeyDown}
            placeholder="Search locale"
          />
          <button
            className="locale-clear-button"
            type="button"
            disabled={props.selectedLocales.length === 0}
            onClick={() => props.onChange(normalizeSelectedLocales([]))}
          >
            Clear selection
          </button>
          <div className="locale-dropdown-options">
            {existingOptions.map((option, index) => (
              <button
                className={activeOptionIndex === index ? "highlighted" : ""}
                type="button"
                key={option.value}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                onMouseEnter={() => setHoveredOptionIndex(index)}
                onClick={() => confirmOption(option)}
              >
                <span>{option.label}</span>
                {option.detail ? <small>{option.detail}</small> : null}
              </button>
            ))}
          </div>
          {addOptions.length ? (
            <div className="locale-add-options">
              {addOptions.map((option, index) => {
                const optionIndex = existingOptions.length + index;
                return (
                  <button
                    className={activeOptionIndex === optionIndex ? "highlighted" : ""}
                    type="button"
                    key={option.value}
                    ref={(element) => {
                      optionRefs.current[optionIndex] = element;
                    }}
                    onMouseEnter={() => setHoveredOptionIndex(optionIndex)}
                    onClick={() => confirmOption(option)}
                  >
                    <span>{option.label}</span>
                    <span>Add</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          {menuOptions.length === 0 ? <div className="menu-empty">No matching locales.</div> : null}
        </DropdownPortal>
      ) : null}
    </div>
  );
}

function StoreScreenshotGrid(props: {
  app: AppSummary;
  session: SessionState;
  requestedLocales: string[];
  referenceLocale: string | null;
  screenshots: StoreScreenshot[];
  userLanguages: UserLanguage[];
  currentUser: UserProfile | null;
  currentUserId: string | null;
  loading: boolean;
  message: string | null;
  refreshing: boolean;
  localeSelectionResetAppId?: string | null;
  canRefresh: boolean;
  canAddLocale: boolean;
  canUpload?: boolean;
  onUploadRestricted?: () => void;
  onSignInRequired?: () => void;
  onUserUpdated?: (user: UserProfile) => void;
  showRefresh?: boolean;
  showReviewerIntro?: boolean;
  reviewLinkConfig?: ReviewLinkConfig;
  canEditDescriptions?: boolean;
  onAddLocale: (locale: string) => void | Promise<void>;
  onCommentsChanged: (screenshotId: string, comments: StoreScreenshotComment[]) => void;
  onRefreshSignedScreenshotUrls?: () => Promise<StoreScreenshot[]>;
  onRefreshPlatformLocale?: (platform: Platform, locale: string) => void;
  onStackScreenshots?: (screenshotId: string, targetScreenshotId: string) => Promise<void>;
  onExtractScreenshot?: (screenshotId: string) => Promise<void>;
  onUpdateDescription?: (screenshotId: string, description: string | null) => Promise<void>;
  onUploadScreenshots?: (
    platform: Platform,
    localeTag: string,
    files: File[],
    items: UploadStoreScreenshotAssignment[],
    onProgress?: (progress: UploadStoreScreenshotProgress) => void
  ) => Promise<void>;
  onDeleteUploadedScreenshot?: (screenshotId: string) => Promise<void>;
  onRemoveStoreScreenshot?: (screenshotId: string) => Promise<void>;
  openCommentsRequestKey?: number;
  onLocaleSelectionReset?: () => void;
}) {
  const initialFilterState = readStoreScreenshotFilterStateFromHistory(props.app.id);
  const [selectedLocales, setSelectedLocales] = useState(() => initialFilterState?.selectedLocales ?? readStoreLocalesFromUrl());
  const [welcomeLocaleOverride, setWelcomeLocaleOverride] = useState<string | null>(null);
  const [reviewerIntroCollapsed, setReviewerIntroCollapsed] = useState(() => readReviewerIntroCollapsedPreference(props.app.id));
  const [tileSize, setTileSize] = useState<StoreTileSize>(readStoreTileSizePreference);
  const [groupingMode, setGroupingMode] = useState<StoreGroupingMode>(readStoreGroupingModePreference);
  const [platformFilter, setPlatformFilter] = useState<StorePlatformFilter>(initialFilterState?.platformFilter ?? "ALL");
  const [needsWorkOnly, setNeedsWorkOnly] = useState(initialFilterState?.needsWorkOnly ?? false);
  const [commentedOnly, setCommentedOnly] = useState(initialFilterState?.commentedOnly ?? false);
  const [unresolvedOnly, setUnresolvedOnly] = useState(initialFilterState?.unresolvedOnly ?? false);
  const [updatedSince, setUpdatedSince] = useState(initialFilterState?.updatedSince ?? "");
  const allLocalesSelected = selectedLocales.includes("ALL");
  const selectedLocaleValues = selectedLocales.filter((locale) => locale !== "ALL");
  const selectedLocaleSet = useMemo(() => new Set(selectedLocaleValues), [selectedLocaleValues.join("|")]);
  const locales = useMemo(() => {
    const screenshotLocales = props.screenshots
      .filter((screenshot) => screenshot.imageType !== appIconImageType)
      .map((screenshot) => screenshot.localeTag);
    return sortLocalesWithReference(
      Array.from(new Set([...props.requestedLocales, ...screenshotLocales, ...selectedLocaleValues])),
      props.referenceLocale
    );
  }, [props.referenceLocale, props.requestedLocales, props.screenshots, selectedLocaleValues.join("|")]);

  useEffect(() => {
    function onPopState() {
      const filterState = readStoreScreenshotFilterStateFromHistory(props.app.id);
      setSelectedLocales(filterState?.selectedLocales ?? readStoreLocalesFromUrl());
      setPlatformFilter(filterState?.platformFilter ?? "ALL");
      setCommentedOnly(filterState?.commentedOnly ?? false);
      setNeedsWorkOnly(filterState?.needsWorkOnly ?? false);
      setUnresolvedOnly(filterState?.unresolvedOnly ?? false);
      setUpdatedSince(filterState?.updatedSince ?? "");
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [props.app.id]);

  useEffect(() => {
    writeStoreLocalesToUrl(selectedLocales);
    replaceStoreScreenshotFilterStateInHistory({
      appId: props.app.id,
      selectedLocales,
      platformFilter,
      commentedOnly,
      needsWorkOnly,
      unresolvedOnly,
      updatedSince
    });
  }, [props.app.id, selectedLocales, platformFilter, commentedOnly, needsWorkOnly, unresolvedOnly, updatedSince]);

  useEffect(() => {
    if (!props.requestedLocales.length) {
      return;
    }
    setSelectedLocales((current) => {
      if (current.includes("ALL")) {
        return current;
      }
      const nextLocales = sortLocalesWithReference(
        Array.from(new Set([...current, ...props.requestedLocales])),
        props.referenceLocale
      );
      return storeLocalesEqual(current, nextLocales) ? current : nextLocales;
    });
  }, [props.referenceLocale, props.requestedLocales.join("|")]);

  useEffect(() => {
    if (props.localeSelectionResetAppId !== props.app.id || props.loading) {
      return;
    }
    setSelectedLocales(storeLocaleSelectionForCurrentApp(props.referenceLocale, props.screenshots));
    props.onLocaleSelectionReset?.();
  }, [props.app.id, props.localeSelectionResetAppId, props.loading, props.referenceLocale, props.screenshots, props.onLocaleSelectionReset]);

  useEffect(() => {
    if (!props.openCommentsRequestKey) {
      return;
    }
    const nextFilterState: StoreScreenshotFilterHistoryState = {
      appId: props.app.id,
      selectedLocales: ["ALL"],
      platformFilter: "ALL",
      commentedOnly: true,
      needsWorkOnly: false,
      unresolvedOnly: false,
      updatedSince: ""
    };
    pushStoreScreenshotFilterStateToHistory(nextFilterState);
    setSelectedLocales(nextFilterState.selectedLocales);
    setPlatformFilter(nextFilterState.platformFilter);
    setCommentedOnly(nextFilterState.commentedOnly);
    setNeedsWorkOnly(nextFilterState.needsWorkOnly);
    setUnresolvedOnly(nextFilterState.unresolvedOnly);
    setUpdatedSince(nextFilterState.updatedSince);
  }, [props.app.id, props.openCommentsRequestKey]);

  useEffect(() => {
    setReviewerIntroCollapsed(readReviewerIntroCollapsedPreference(props.app.id));
  }, [props.app.id]);

  useEffect(() => {
    window.localStorage.setItem(storeTileSizeStorageKey, tileSize);
  }, [tileSize]);

  useEffect(() => {
    window.localStorage.setItem(storeGroupingModeStorageKey, groupingMode);
  }, [groupingMode]);

  const [activeScreenshot, setActiveScreenshot] = useState<StoreScreenshot | null>(null);
  const [requestedReviewScreenshotId, setRequestedReviewScreenshotId] = useState<string | null>(readReviewScreenshotIdFromUrl);
  const [measuredScreenshotDimensions, setMeasuredScreenshotDimensions] = useState<ImageDimensionsById>({});
  const [pendingUploadTarget, setPendingUploadTarget] = useState<{ platform: Platform; localeTag: string } | null>(null);
  const [uploadDraft, setUploadDraft] = useState<{ platform: Platform; localeTag: string; files: File[] } | null>(null);
  const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const signedUrlRefreshRef = useRef<Promise<StoreScreenshot[]> | null>(null);
  const imageLoadRetryIdsRef = useRef<Set<string>>(new Set());

  const appIcons = uniqueImages(props.screenshots.filter((screenshot) => screenshot.imageType === appIconImageType));
  const appIconKeys = useMemo(() => new Set(appIcons.map((image) => canonicalImageKey(image.imageUrl))), [appIcons]);
  const updatedSinceTimestamp = updatedSince ? localDateStartTimestamp(updatedSince) : null;
  const filteredScreenshots = props.screenshots.filter(
    (screenshot) =>
      screenshot.imageType !== appIconImageType &&
      !isIconLikeImage(screenshot, appIconKeys) &&
      (allLocalesSelected || selectedLocaleSet.has(screenshot.localeTag)) &&
      matchesPlatformFilter(screenshot, platformFilter) &&
      (!needsWorkOnly || screenshot.needsWorkCount > 0) &&
      (!unresolvedOnly || hasUnresolvedCommentActivity(screenshot)) &&
      (!commentedOnly || hasCommentActivity(screenshot)) &&
      (updatedSinceTimestamp === null || hasCommentActivitySince(screenshot, updatedSinceTimestamp))
  );
  const iosScreenshots = uniqueImages(filteredScreenshots.filter(
    (screenshot) => screenshot.platform === "IOS" && screenshot.imageType !== appIconImageType
  ));
  const androidScreenshots = uniqueImages(filteredScreenshots.filter(
    (screenshot) => screenshot.platform === "ANDROID" && screenshot.imageType !== appIconImageType
  ));
  const singleSelectedLocale = !allLocalesSelected && selectedLocaleValues.length === 1 ? selectedLocaleValues[0] : null;
  const canChooseGrouping = allLocalesSelected || selectedLocaleValues.length > 1;
  const groupingDisabledReason = canChooseGrouping ? null : "Select multiple locales to group within aspect ratios.";
  const effectiveGroupingMode = canChooseGrouping ? groupingMode : null;
  const showRefresh = props.showRefresh ?? true;
  const canRefreshIos = Boolean(props.app.iosAppStoreId);
  const canRefreshAndroid = Boolean(props.app.androidPackageName);
  const showIosSection = platformFilter !== "PLAY_STORE";
  const showAndroidSection = platformFilter !== "APP_STORE";
  const hasVisibleScreenshots = iosScreenshots.length > 0 || androidScreenshots.length > 0;
  const platformActionLocaleOptions = allLocalesSelected ? locales : selectedLocaleValues;
  const showSelectedLocaleRows = effectiveGroupingMode === "locale" && selectedLocaleValues.length > 0;
  const showPlatformHeaderActions = effectiveGroupingMode !== "locale" &&
    platformActionLocaleOptions.length > 0 &&
    Boolean(props.canUpload || props.onUploadRestricted || showRefresh);
  const showUploadableEmptySections = Boolean(
    (props.canUpload || props.onUploadRestricted || showRefresh) &&
    (singleSelectedLocale || showSelectedLocaleRows || showPlatformHeaderActions)
  );
  const hasAnyStoreScreenshots = props.screenshots.some((screenshot) =>
    screenshot.imageType !== appIconImageType && !isIconLikeImage(screenshot, appIconKeys)
  );
  const referenceLocales = props.referenceLocale ? [props.referenceLocale] : [];
  const showFilterRow = props.app.relationship === "OWNER";
  const hasActiveScreenshotFilters = platformFilter !== "ALL" || commentedOnly || needsWorkOnly || unresolvedOnly || Boolean(updatedSince);
  const reviewerScreenshots = props.screenshots.filter((screenshot) =>
    screenshot.imageType !== appIconImageType &&
    !isIconLikeImage(screenshot, appIconKeys)
  );
  const reviewerIntroPosition = props.reviewLinkConfig?.welcomePosition ?? null;
  const reviewerIntroGroupKey = props.reviewLinkConfig?.welcomeGroupKey ?? null;
  const reviewerIntroWelcomeLocale = welcomeLocaleOverride ?? props.reviewLinkConfig?.welcomeLocale ?? null;
  const reviewerWelcomeLocaleOptions = welcomeLocaleOptionsForScreenshots(reviewerScreenshots, referenceLocales, reviewerIntroPosition, reviewerIntroGroupKey);
  const reviewerWelcome = selectReviewerWelcomeScreenshots(reviewerScreenshots, referenceLocales, selectedLocales, {
    groupKey: reviewerIntroGroupKey,
    locale: reviewerIntroWelcomeLocale,
    position: reviewerIntroPosition
  });
  const reviewerLanguageName = languageNameForLocale(reviewerWelcome.reviewLocale);
  const reviewerIntroCustomTitle = sanitizeReviewLinkMessage(props.reviewLinkConfig?.message ?? "");
  const reviewerIntroTitle = reviewerIntroCustomTitle || (reviewerLanguageName ? `Check ${reviewerLanguageName} wording` : "Review localized text");
  const reviewerIntroCropFocus = props.reviewLinkConfig?.cropFocus ?? defaultReviewLinkCropFocus;
  const reviewerIntroSelectedLocale = reviewerWelcome.reviewLocale ?? reviewerWelcomeLocaleOptions[0] ?? null;

  useEffect(() => {
    const imageIds = new Set(props.screenshots.map((image) => image.id));
    setMeasuredScreenshotDimensions((current) => {
      const next: ImageDimensionsById = {};
      let changed = false;
      for (const [imageId, dimensions] of Object.entries(current)) {
        if (imageIds.has(imageId)) {
          next[imageId] = dimensions;
        } else {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [props.screenshots]);

  function clearScreenshotFilters() {
    setPlatformFilter("ALL");
    setCommentedOnly(false);
    setNeedsWorkOnly(false);
    setUnresolvedOnly(false);
    setUpdatedSince("");
  }

  function selectWelcomeLocale(locale: string) {
    setWelcomeLocaleOverride(locale);
    setSelectedLocales(libraryLocalesForWelcomeLocale(locale, referenceLocales));
  }

  function handleReviewerIntroCollapsedChange(collapsed: boolean) {
    setReviewerIntroCollapsed(collapsed);
    writeReviewerIntroCollapsedPreference(props.app.id, collapsed);
  }

  function rememberScreenshotDimensions(image: StoreScreenshot, dimensions: ImageDimensions) {
    setMeasuredScreenshotDimensions((current) => {
      const existing = current[image.id];
      if (existing?.width === dimensions.width && existing.height === dimensions.height) {
        return current;
      }
      return { ...current, [image.id]: dimensions };
    });
  }

  useEffect(() => {
    if (welcomeLocaleOverride && !reviewerWelcomeLocaleOptions.includes(welcomeLocaleOverride)) {
      setWelcomeLocaleOverride(null);
    }
  }, [reviewerWelcomeLocaleOptions.join("|"), welcomeLocaleOverride]);

  useEffect(() => {
    function onPopState() {
      const nextReviewScreenshotId = readReviewScreenshotIdFromUrl();
      setRequestedReviewScreenshotId(nextReviewScreenshotId);
      if (!nextReviewScreenshotId) {
        setActiveScreenshot(null);
      }
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!requestedReviewScreenshotId) {
      setActiveScreenshot(null);
      return;
    }

    const requestedScreenshot = props.screenshots.find((screenshot) =>
      screenshot.id === requestedReviewScreenshotId &&
      screenshot.imageType !== appIconImageType &&
      !isIconLikeImage(screenshot, appIconKeys)
    );
    if (requestedScreenshot) {
      setActiveScreenshot(requestedScreenshot);
    } else if (!props.loading) {
      setActiveScreenshot(null);
    }
  }, [appIconKeys, props.loading, props.screenshots, requestedReviewScreenshotId]);

  async function refreshSignedScreenshotUrls(): Promise<StoreScreenshot[]> {
    if (!props.onRefreshSignedScreenshotUrls) {
      return props.screenshots;
    }
    if (!signedUrlRefreshRef.current) {
      signedUrlRefreshRef.current = props.onRefreshSignedScreenshotUrls()
        .finally(() => {
          signedUrlRefreshRef.current = null;
        });
    }
    return signedUrlRefreshRef.current;
  }

  async function prepareScreenshotForReview(screenshot: StoreScreenshot): Promise<StoreScreenshot> {
    if (!hasStaleUploadedScreenshotImageUrl(props.screenshots)) {
      return screenshot;
    }
    try {
      const nextScreenshots = await refreshSignedScreenshotUrls();
      return nextScreenshots.find((candidate) => candidate.id === screenshot.id) ?? screenshot;
    } catch {
      return screenshot;
    }
  }

  function activateReviewModal(screenshot: StoreScreenshot) {
    setActiveScreenshot(screenshot);
    setRequestedReviewScreenshotId(screenshot.id);
    writeReviewScreenshotIdToUrl(screenshot.id, { replace: false });
  }

  function openReviewModal(screenshot: StoreScreenshot) {
    void prepareScreenshotForReview(screenshot).then(activateReviewModal);
  }

  function closeReviewModal() {
    setActiveScreenshot(null);
    setRequestedReviewScreenshotId(null);
    writeReviewScreenshotIdToUrl(null);
  }

  function startFirstReviewScreenshot() {
    if (reviewerWelcome.startImage) {
      openReviewModal(reviewerWelcome.startImage);
    }
  }

  const setReviewedScreenshotInUrl = useCallback((screenshotId: string) => {
    setRequestedReviewScreenshotId(screenshotId);
    writeReviewScreenshotIdToUrl(screenshotId);
  }, []);

  const switchActiveReviewScreenshot = useCallback((screenshot: StoreScreenshot) => {
    void prepareScreenshotForReview(screenshot).then((nextScreenshot) => {
      setActiveScreenshot(nextScreenshot);
      setRequestedReviewScreenshotId(nextScreenshot.id);
      writeReviewScreenshotIdToUrl(nextScreenshot.id);
    });
  }, [props.screenshots, props.onRefreshSignedScreenshotUrls]);

  function retryExpiredImageUrl(image: StoreScreenshot) {
    if (image.sourceType !== "UPLOAD" || imageLoadRetryIdsRef.current.has(image.id)) {
      return;
    }
    imageLoadRetryIdsRef.current.add(image.id);
    void refreshSignedScreenshotUrls();
  }

  function startScreenshotUpload(platform: Platform, localeTag: string) {
    setPendingUploadTarget({ platform, localeTag });
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
      uploadInputRef.current.click();
    }
  }

  function startRestrictedScreenshotUpload() {
    props.onUploadRestricted?.();
  }

  function handleUploadFileSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (!pendingUploadTarget || files.length === 0) {
      return;
    }
    setUploadProgress({});
    setUploadDraft({ ...pendingUploadTarget, files });
    setPendingUploadTarget(null);
  }

  function referenceImagesForUpload(platform: Platform): StoreScreenshot[] {
    if (!props.referenceLocale) {
      return [];
    }
    return activeStackImages(props.screenshots.filter(
      (screenshot) =>
        screenshot.platform === platform &&
        screenshot.localeTag === props.referenceLocale &&
        screenshot.imageType !== appIconImageType &&
        !isIconLikeImage(screenshot, appIconKeys)
    ));
  }

  async function confirmUpload(items: UploadStoreScreenshotAssignment[]) {
    if (!uploadDraft || !props.onUploadScreenshots) {
      return;
    }
    if (!props.canUpload) {
      props.onUploadRestricted?.();
      return;
    }
    setUploadingScreenshots(true);
    setUploadProgress({});
    try {
      await props.onUploadScreenshots(uploadDraft.platform, uploadDraft.localeTag, uploadDraft.files, items, (progress) => {
        setUploadProgress((current) => ({
          ...current,
          [progress.fileIndex]: progress.percent
        }));
      });
      setUploadDraft(null);
      setUploadProgress({});
    } catch {
      // The parent reports the concrete error and may open billing; keep the upload arrangement for retry.
    } finally {
      setUploadingScreenshots(false);
    }
  }

  return (
    <>
      {props.showReviewerIntro ? (
        <ReviewerIntroCard
          appName={props.app.name}
          loading={props.loading}
          title={reviewerIntroTitle}
          referenceImage={reviewerWelcome.referenceImage}
          reviewImage={reviewerWelcome.reviewImage}
          localeOptions={reviewerWelcomeLocaleOptions}
          selectedLocale={reviewerIntroSelectedLocale}
          cropFocus={reviewerIntroCropFocus}
          collapsed={reviewerIntroCollapsed}
          collapsible
          canStart={Boolean(reviewerWelcome.startImage)}
          onCollapsedChange={handleReviewerIntroCollapsedChange}
          onLocaleChange={selectWelcomeLocale}
          onStartReview={startFirstReviewScreenshot}
        />
      ) : null}
      <div className="store-screenshot-card">
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        hidden
        onChange={handleUploadFileSelection}
      />
      <div className="store-screenshot-header">
        <div>
          <h2>{props.loading ? "Loading screenshots" : "Screenshot library"}</h2>
        </div>
        <div className="store-screenshot-controls">
          <TileSizeToggle value={tileSize} onChange={setTileSize} />
          <span className="tooltip-shell" data-tooltip={groupingDisabledReason ?? undefined}>
            <GroupingModeToggle value={groupingMode} disabled={!canChooseGrouping} onChange={setGroupingMode} />
          </span>
          <LocaleDropdown
            locales={locales}
            selectedLocales={selectedLocales}
            canAddLocale={props.canAddLocale}
            onAddLocale={props.onAddLocale}
            onChange={setSelectedLocales}
          />
        </div>
      </div>
      {showFilterRow ? (
        <div className="store-screenshot-filter-row">
          <StoreControlDropdown
            eyebrow="Platform"
            options={[
              { label: "All", value: "ALL" },
              { label: "App Store", value: "APP_STORE" },
              { label: "Play Store", value: "PLAY_STORE" }
            ]}
            value={platformFilter}
            onSelect={(value) => setPlatformFilter(value as StorePlatformFilter)}
          />
          <button
            className={`filter-toggle-button ${commentedOnly ? "active" : ""}`}
            type="button"
            onClick={() => setCommentedOnly((current) => !current)}
          >
            Commented on
          </button>
          <button
            className={`filter-toggle-button ${needsWorkOnly ? "active" : ""}`}
            type="button"
            onClick={() => setNeedsWorkOnly((current) => !current)}
          >
            Needs work
          </button>
          <button
            className={`filter-toggle-button ${unresolvedOnly ? "active" : ""}`}
            type="button"
            onClick={() => setUnresolvedOnly((current) => !current)}
          >
            Unresolved
          </button>
          <div className={`date-filter-control ${updatedSince ? "has-value" : ""}`}>
            <label htmlFor="store-updated-since-filter">Updated since</label>
            <input
              id="store-updated-since-filter"
              type="date"
              value={updatedSince}
              onChange={(event) => setUpdatedSince(event.target.value)}
            />
          </div>
          {hasActiveScreenshotFilters ? (
            <button
              className="filter-clear-button"
              type="button"
              aria-label="Clear screenshot filters"
              title="Clear screenshot filters"
              onClick={clearScreenshotFilters}
            >
              <XIcon />
            </button>
          ) : null}
        </div>
      ) : null}
      {props.message ? <p className="store-source-message">{props.message}</p> : null}
      {hasVisibleScreenshots || showUploadableEmptySections ? (
        <div className="store-image-sections">
          {showIosSection ? (
            <StoreImageSection
              images={iosScreenshots}
              title="iOS screenshots"
              groupingMode={effectiveGroupingMode}
              onSelect={openReviewModal}
              onImageLoadError={retryExpiredImageUrl}
              onStack={props.onStackScreenshots}
              onExtract={props.onExtractScreenshot}
              onUpload={props.onUploadScreenshots ? startScreenshotUpload : props.onUploadRestricted ? startRestrictedScreenshotUpload : undefined}
              onDeleteUploaded={props.onDeleteUploadedScreenshot}
              onRemoveStore={props.onRemoveStoreScreenshot}
              onRefresh={showRefresh ? props.onRefreshPlatformLocale : undefined}
              referenceLocales={referenceLocales}
              singleSelectedLocale={singleSelectedLocale}
              selectedLocales={selectedLocaleValues}
              actionLocaleOptions={platformActionLocaleOptions}
              canRefresh={canRefreshIos}
              refreshing={props.refreshing}
              hasActiveFilters={hasActiveScreenshotFilters}
              measuredDimensionsByImageId={measuredScreenshotDimensions}
              onImageDimensionsChange={rememberScreenshotDimensions}
              platform="IOS"
              tileSize={tileSize}
            />
          ) : null}
          {showAndroidSection ? (
            <StoreImageSection
              images={androidScreenshots}
              title="Android screenshots"
              groupingMode={effectiveGroupingMode}
              onSelect={openReviewModal}
              onImageLoadError={retryExpiredImageUrl}
              onStack={props.onStackScreenshots}
              onExtract={props.onExtractScreenshot}
              onUpload={props.onUploadScreenshots ? startScreenshotUpload : props.onUploadRestricted ? startRestrictedScreenshotUpload : undefined}
              onDeleteUploaded={props.onDeleteUploadedScreenshot}
              onRemoveStore={props.onRemoveStoreScreenshot}
              onRefresh={showRefresh ? props.onRefreshPlatformLocale : undefined}
              referenceLocales={referenceLocales}
              singleSelectedLocale={singleSelectedLocale}
              selectedLocales={selectedLocaleValues}
              actionLocaleOptions={platformActionLocaleOptions}
              canRefresh={canRefreshAndroid}
              refreshing={props.refreshing}
              hasActiveFilters={hasActiveScreenshotFilters}
              measuredDimensionsByImageId={measuredScreenshotDimensions}
              onImageDimensionsChange={rememberScreenshotDimensions}
              platform="ANDROID"
              tileSize={tileSize}
            />
          ) : null}
        </div>
      ) : (
        <div className="store-screenshot-empty">
          {props.loading ? "Loading..." : hasAnyStoreScreenshots ? "No screenshots match these filters." : "No store screenshots have been refreshed yet."}
        </div>
      )}
      {activeScreenshot ? (
        <ScreenshotReviewModal
          app={props.app}
          session={props.session}
          screenshot={activeScreenshot}
          screenshots={props.screenshots}
          referenceLocales={referenceLocales}
          userLanguages={props.userLanguages}
          currentUser={props.currentUser}
          currentUserId={props.currentUserId}
          imageDimensionsById={measuredScreenshotDimensions}
          onUserUpdated={props.onUserUpdated}
          onSignInRequired={props.onSignInRequired}
          onClose={closeReviewModal}
          onCommentsChanged={props.onCommentsChanged}
          onUpdateDescription={props.onUpdateDescription ?? (async () => undefined)}
          onReviewedScreenshotChanged={setReviewedScreenshotInUrl}
          onSwitchReviewedScreenshot={switchActiveReviewScreenshot}
          onImageLoadError={retryExpiredImageUrl}
          canEditDescriptions={props.canEditDescriptions ?? false}
        />
      ) : null}
      {uploadDraft ? (
        <UploadScreenshotsModal
          platform={uploadDraft.platform}
          localeTag={uploadDraft.localeTag}
          files={uploadDraft.files}
          referenceImages={referenceImagesForUpload(uploadDraft.platform)}
          uploading={uploadingScreenshots}
          progress={uploadProgress}
          onCancel={() => {
            setUploadDraft(null);
            setUploadProgress({});
          }}
          onConfirm={confirmUpload}
        />
      ) : null}
      </div>
    </>
  );
}

function TileSizeToggle(props: {
  value: StoreTileSize;
  onChange: (value: StoreTileSize) => void;
}) {
  return (
    <div className={`tile-size-toggle ${props.value === "small" ? "select-first" : "select-second"}`} role="group" aria-label="Tile size">
      <button
        className={props.value === "small" ? "active" : ""}
        type="button"
        onClick={() => props.onChange("small")}
      >
        Small
      </button>
      <button
        className={props.value === "regular" ? "active" : ""}
        type="button"
        onClick={() => props.onChange("regular")}
      >
        Regular
      </button>
    </div>
  );
}

function GroupingModeToggle(props: {
  value: StoreGroupingMode;
  disabled: boolean;
  onChange: (value: StoreGroupingMode) => void;
}) {
  return (
    <div className={`tile-size-toggle grouping-mode-toggle ${props.value === "locale" ? "select-first" : "select-second"} ${props.disabled ? "disabled" : ""}`} role="group" aria-label="Screenshot grouping">
      <button
        className={props.value === "locale" ? "active" : ""}
        type="button"
        disabled={props.disabled}
        onClick={() => props.onChange("locale")}
      >
        Group by Locale
      </button>
      <button
        className={props.value === "position" ? "active" : ""}
        type="button"
        disabled={props.disabled}
        onClick={() => props.onChange("position")}
      >
        Group by Position
      </button>
    </div>
  );
}

function LocaleDropdown(props: {
  locales: string[];
  selectedLocales: string[];
  canAddLocale: boolean;
  onChange: (locales: string[]) => void;
  onAddLocale: (locale: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState<number | null>(null);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const normalizedQuery = normalizeLocaleInput(query);
  const trimmedQuery = query.toLowerCase().trim();
  const allLocalesSelected = props.selectedLocales.includes("ALL");
  const selectedLocaleValues = props.selectedLocales.filter((locale) => locale !== "ALL");
  const selectedLocaleSet = new Set(selectedLocaleValues);
  const existingOptions = [
    { kind: "all" as const, label: "All locales", detail: "", value: "ALL" },
    ...props.locales.map((locale) => ({ kind: "existing" as const, label: locale, detail: languageNameForLocale(locale) ?? "", value: locale }))
  ].filter((option) => !trimmedQuery || option.label.toLowerCase().includes(trimmedQuery) || option.detail.toLowerCase().includes(trimmedQuery));
  const canAddTypedLocale = Boolean(props.canAddLocale && normalizedQuery && !props.locales.includes(normalizedQuery));
  const addableLocales = props.canAddLocale && trimmedQuery
    ? localeSuggestions
        .filter((locale) => !props.locales.includes(locale))
        .filter((locale) => locale !== normalizedQuery || !canAddTypedLocale)
        .filter((locale) => locale.toLowerCase().includes(trimmedQuery))
    : [];
  const addOptions = [
    ...(canAddTypedLocale && normalizedQuery ? [normalizedQuery] : []),
    ...addableLocales
  ].map((locale) => ({ kind: "add" as const, label: locale, detail: languageNameForLocale(locale) ?? "", value: locale }));
  const menuOptions = [...existingOptions, ...addOptions];
  const menuOptionSignature = menuOptions.map((option) => `${option.kind}:${option.value}`).join("|");
  const activeOptionIndex = hoveredOptionIndex ?? highlightedOptionIndex;
  const selectedLabel = allLocalesSelected
    ? "All locales"
    : selectedLocaleValues.length === 1
      ? selectedLocaleValues[0]
      : selectedLocaleValues.length > 1
        ? `${selectedLocaleValues.length} locales`
        : "No locales";

  useEffect(() => {
    setHighlightedOptionIndex(menuOptions.length === 1 ? 0 : null);
    setHoveredOptionIndex(null);
  }, [menuOptions.length, menuOptionSignature, open, query]);

  useEffect(() => {
    if (!open || hoveredOptionIndex !== null || highlightedOptionIndex === null || menuOptions.length === 0) {
      return;
    }
    window.requestAnimationFrame(() => optionRefs.current[highlightedOptionIndex]?.scrollIntoView({ block: "nearest" }));
  }, [highlightedOptionIndex, hoveredOptionIndex, menuOptions.length, open]);

  useEffect(() => {
    if (open) {
      focusDropdownSearchInput(searchInputRef.current);
    }
  }, [open]);

  function addLocale(locale: string) {
    props.onChange(addSelectedLocale(props.selectedLocales, locale));
    void props.onAddLocale(locale);
    setOpen(false);
    setQuery("");
  }

  function selectAllLocales() {
    props.onChange(["ALL"]);
  }

  function selectOnlyLocale(locale: string) {
    props.onChange([locale]);
  }

  function toggleLocale(locale: string) {
    props.onChange(toggleSelectedLocale(props.selectedLocales, locale));
  }

  function confirmOption(option: (typeof menuOptions)[number] | undefined) {
    if (!option) {
      return;
    }
    if (option.kind === "all") {
      selectAllLocales();
    } else if (option.kind === "add") {
      addLocale(option.value);
    } else {
      toggleLocale(option.value);
    }
  }

  function isOptionSelected(option: (typeof menuOptions)[number]) {
    if (option.kind === "all") {
      return allLocalesSelected;
    }
    if (option.kind === "existing") {
      return selectedLocaleSet.has(option.value);
    }
    return false;
  }

  function handleLocaleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? 0 : (current + 1) % menuOptions.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? menuOptions.length - 1 : (current - 1 + menuOptions.length) % menuOptions.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      const activeOption = activeOptionIndex === null ? undefined : menuOptions[activeOptionIndex];
      if (activeOption) {
        if (!isOptionSelected(activeOption)) {
          confirmOption(activeOption);
        }
        setOpen(false);
      }
    }
    if (event.key === " ") {
      event.preventDefault();
      if (menuOptions.length === 0) {
        return;
      }
      const activeOption = activeOptionIndex === null ? undefined : menuOptions[activeOptionIndex];
      confirmOption(activeOption);
    }
  }

  return (
    <div className="locale-dropdown" ref={ref}>
      <button
        className={`ghost-button menu-trigger locale-dropdown-trigger ${open ? "open" : ""}`}
        type="button"
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (next) {
              setQuery("");
              setHighlightedOptionIndex(null);
              setHoveredOptionIndex(null);
            }
            return next;
          });
        }}
      >
        <DropdownCopy eyebrow="Locale" value={selectedLabel} />
        <ChevronDownIcon />
      </button>
      {open ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="locale-dropdown-menu"
          minWidth={260}
          onDismiss={() => setOpen(false)}
          onMouseLeave={() => setHoveredOptionIndex(null)}
          open={open}
        >
          <input
            className="locale-dropdown-search"
            ref={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleLocaleInputKeyDown}
            placeholder="Search or add locale"
          />
          <button
            className="locale-clear-button"
            type="button"
            disabled={props.selectedLocales.length === 0}
            onMouseEnter={() => {
              setHoveredOptionIndex(null);
              setHighlightedOptionIndex(null);
            }}
            onClick={() => props.onChange([])}
          >
            Clear selection
          </button>
          <div className="locale-dropdown-options">
            {existingOptions.map((option, index) => {
              const selected = option.kind === "all" ? allLocalesSelected : selectedLocaleSet.has(option.value);
              const highlighted = activeOptionIndex === index;
              const canSelectOnlyLocale = selected && option.kind === "existing" && selectedLocaleValues.length > 1;
              const showSelectOnlyLocaleAction = canSelectOnlyLocale && hoveredOptionIndex === index;
              return (
                <div
                  className={`locale-option-row ${selected ? "active" : ""} ${highlighted ? "highlighted" : ""} ${canSelectOnlyLocale ? "can-select-only" : ""}`}
                  key={option.value}
                  onMouseEnter={() => setHoveredOptionIndex(index)}
                >
                  <button
                    className="locale-option-main"
                    type="button"
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    onClick={() => confirmOption(option)}
                  >
                    <span>{option.label}</span>
                    {option.detail && !(selected && hoveredOptionIndex === index) ? <small>{option.detail}</small> : null}
                  </button>
                  {showSelectOnlyLocaleAction ? (
                    <button
                      className="locale-option-action"
                      type="button"
                      aria-label={`Select only ${option.label}`}
                      title={`Select only ${option.label}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectOnlyLocale(option.value);
                      }}
                    >
                      Only this
                    </button>
                  ) : null}
                </div>
              );
            })}
            {addOptions.length ? (
              <div className="locale-add-options">
                {addOptions.map((option, index) => {
                  const optionIndex = existingOptions.length + index;
                  return (
                    <button
                      className={activeOptionIndex === optionIndex ? "highlighted" : ""}
                      type="button"
                      key={option.value}
                      ref={(element) => {
                        optionRefs.current[optionIndex] = element;
                      }}
                      onMouseEnter={() => setHoveredOptionIndex(optionIndex)}
                      onClick={() => confirmOption(option)}
                    >
                      <span>{option.label}</span>
                      <span>{option.detail || "Add"}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </DropdownPortal>
      ) : null}
    </div>
  );
}

function StoreControlDropdown(props: {
  className?: string;
  disabled?: boolean;
  eyebrow: string;
  minWidth?: number;
  options: Array<{ label: string; value: string }>;
  value: string;
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const selected = props.options.find((option) => option.value === props.value) ?? props.options[0];

  return (
    <div className={`menu-shell store-control-dropdown ${props.className ?? ""}`.trim()} ref={ref}>
      <button
        className={`ghost-button menu-trigger ${open ? "open" : ""}`}
        type="button"
        disabled={props.disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <DropdownCopy eyebrow={props.eyebrow} value={selected.label} />
        <ChevronDownIcon />
      </button>
      {open ? (
        <DropdownPortal
          anchorRef={ref}
          className="store-control-panel"
          minWidth={props.minWidth ?? 190}
          onDismiss={() => setOpen(false)}
          open={open}
        >
          {props.options.map((option) => (
            <button
              className={`menu-option ${option.value === props.value ? "active" : ""}`}
              key={option.value}
              type="button"
              onClick={() => {
                props.onSelect(option.value);
                setOpen(false);
              }}
            >
              <span className="menu-option-primary">{option.label}</span>
            </button>
          ))}
        </DropdownPortal>
      ) : null}
    </div>
  );
}

function ScreenshotReviewModal(props: {
  app: AppSummary;
  session: SessionState;
  screenshot: StoreScreenshot;
  screenshots: StoreScreenshot[];
  referenceLocales: string[];
  userLanguages: UserLanguage[];
  currentUser: UserProfile | null;
  currentUserId: string | null;
  imageDimensionsById?: ImageDimensionsById;
  onUserUpdated?: (user: UserProfile) => void;
  onSignInRequired?: () => void;
  onClose: () => void;
  onCommentsChanged: (screenshotId: string, comments: StoreScreenshotComment[]) => void;
  onUpdateDescription: (screenshotId: string, description: string | null) => Promise<void>;
  onReviewedScreenshotChanged: (screenshotId: string) => void;
  onSwitchReviewedScreenshot: (screenshot: StoreScreenshot) => void;
  onImageLoadError?: (image: StoreScreenshot) => void;
  canEditDescriptions: boolean;
}) {
  useModalScrollLock();
  const notify = useFloatingNotification();
  const [comments, setComments] = useState<StoreScreenshotComment[]>([]);
  const [body, setBody] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [needsWorkDraftOpen, setNeedsWorkDraftOpen] = useState(false);
  const [needsWorkBody, setNeedsWorkBody] = useState("");
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingCommentAction, setSavingCommentAction] = useState<"comment" | "reply" | "needsWork" | "ok" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commentNotice, setCommentNotice] = useState<string | null>(null);
  const [pendingCommentSubmission, setPendingCommentSubmission] = useState<{
    kind: "signed-in" | "anonymous";
    action: "comment" | "reply" | "needsWork" | "ok";
    request: CreateStoreScreenshotCommentRequest;
    onSuccess?: () => void;
    draftIdentityScope?: string | null;
  } | null>(null);
  const [identityDisplayName, setIdentityDisplayName] = useState("");
  const [identityShareEmail, setIdentityShareEmail] = useState(false);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [selectedReviewedId, setSelectedReviewedId] = useState(props.screenshot.id);
  const [selectedComparisonId, setSelectedComparisonId] = useState<string | null>(null);
  const [showLatestReviewedOnly, setShowLatestReviewedOnly] = useState(readReviewLatestOnlyPreference);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const selectedReviewedIdRef = useRef(selectedReviewedId);
  const commentAutosaveTimerRef = useRef<number | null>(null);
  const needsWorkAutosaveTimerRef = useRef<number | null>(null);
  const replyAutosaveTimerRef = useRef<number | null>(null);
  const reviewImagePairRef = useRef<HTMLDivElement | null>(null);
  const reviewPaneIntroPlayedRef = useRef(false);
  const restoredDraftNoticeKeyRef = useRef<string | null>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const needsWorkTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const identityTitleId = useId();
  const [activeReviewPaneIndex, setActiveReviewPaneIndex] = useState(0);
  const [reviewImageDimensions, setReviewImageDimensions] = useState<ImageDimensionsById>({});
  const knownReviewImageDimensions = useMemo(
    () => ({ ...(props.imageDimensionsById ?? {}), ...reviewImageDimensions }),
    [props.imageDimensionsById, reviewImageDimensions]
  );
  const reviewedImages = useMemo(
    () => findReviewedScreenshots(props.screenshot, props.screenshots, knownReviewImageDimensions),
    [knownReviewImageDimensions, props.screenshot, props.screenshots]
  );
  const latestReviewedImages = useMemo(
    () => latestScreenshotVersions(reviewedImages),
    [reviewedImages]
  );
  const visibleReviewedImages = showLatestReviewedOnly ? latestReviewedImages : reviewedImages;
  const selectedReviewed = visibleReviewedImages.find((image) => image.id === selectedReviewedId)
    ?? (showLatestReviewedOnly ? latestScreenshotVersionForStack(props.screenshot, reviewedImages) : null)
    ?? props.screenshot;
  const referenceLocaleSet = useMemo(() => new Set(props.referenceLocales), [props.referenceLocales.join("|")]);
  const selectedReviewedIsReference = referenceLocaleSet.has(selectedReviewed.localeTag);
  const comparisonImages = useMemo(
    () => selectedReviewedIsReference ? [] : findComparisonScreenshots(selectedReviewed, props.screenshots, props.referenceLocales, props.userLanguages, knownReviewImageDimensions),
    [knownReviewImageDimensions, selectedReviewed, selectedReviewedIsReference, props.screenshots, props.referenceLocales, props.userLanguages]
  );
  const referenceReviewOptions = useMemo(() => {
    if (!selectedReviewedIsReference) {
      return [];
    }
    const optionsByLocale = new Map<string, StoreScreenshot>();
    for (const image of findEquivalentReviewScreenshots(selectedReviewed, props.screenshots, knownReviewImageDimensions)) {
      if (!referenceLocaleSet.has(image.localeTag) && !optionsByLocale.has(image.localeTag)) {
        optionsByLocale.set(image.localeTag, image);
      }
    }
    return sortLocalesWithReferenceLocales(Array.from(optionsByLocale.keys()), props.referenceLocales)
      .map((locale) => optionsByLocale.get(locale))
      .filter((image): image is StoreScreenshot => Boolean(image));
  }, [knownReviewImageDimensions, selectedReviewed, selectedReviewedIsReference, props.screenshots, props.referenceLocales, referenceLocaleSet]);
  const selectedComparison = comparisonImages.find((image) => image.id === selectedComparisonId) ?? comparisonImages[0] ?? null;
  const descriptionImage = selectedComparison ?? (props.referenceLocales.includes(selectedReviewed.localeTag) ? selectedReviewed : null);
  const canEditDescription = Boolean(props.canEditDescriptions && descriptionImage && props.referenceLocales.includes(descriptionImage.localeTag));
  const selectedReviewedIndex = visibleReviewedImages.findIndex((image) => image.id === selectedReviewed.id);
  const canSelectPreviousReviewed = selectedReviewedIndex > 0;
  const canSelectNextReviewed = selectedReviewedIndex >= 0 && selectedReviewedIndex < visibleReviewedImages.length - 1;
  const draftIdentityScope = useMemo(
    () => commentDraftIdentityScope(props.session, props.currentUser),
    [props.currentUser?.id, props.currentUser?.isAnonymous, props.session]
  );
  const draftIdentityScopeRef = useRef<string | null>(draftIdentityScope);
  const selectedDraftScreenshotIdRef = useRef(selectedReviewed.id);

  useEffect(() => {
    window.localStorage.setItem(reviewLatestOnlyStorageKey, showLatestReviewedOnly ? "true" : "false");
  }, [showLatestReviewedOnly]);
  const selectedReviewDimensions = knownReviewImageDimensions[selectedReviewed.id] ?? (selectedComparison ? knownReviewImageDimensions[selectedComparison.id] : null);
  const measuredReviewAspectRatio = selectedReviewDimensions && selectedReviewDimensions.width > 0 && selectedReviewDimensions.height > 0
    ? selectedReviewDimensions.width / selectedReviewDimensions.height
    : null;
  const reviewLayoutAspectRatio = measuredReviewAspectRatio ?? getScreenshotReviewLayoutAspectRatio(selectedReviewed, props.screenshots, knownReviewImageDimensions);
  const landscapeAsset = [selectedReviewed, selectedComparison]
    .filter((image): image is StoreScreenshot => Boolean(image))
    .some((image) => isLandscapeReviewImage(image, knownReviewImageDimensions[image.id]));
  const stackImagePanes = landscapeAsset;

  useEffect(() => {
    selectedReviewedIdRef.current = selectedReviewedId;
  }, [selectedReviewedId]);

  useEffect(() => {
    draftIdentityScopeRef.current = draftIdentityScope;
  }, [draftIdentityScope]);

  useEffect(() => {
    selectedDraftScreenshotIdRef.current = selectedReviewed.id;
  }, [selectedReviewed.id]);

  useEffect(() => () => {
    clearCommentAutosaveTimers();
  }, []);

  useEffect(() => {
    if (selectedReviewedIdRef.current === props.screenshot.id) {
      return;
    }
    flushCurrentDrafts();
    selectedReviewedIdRef.current = props.screenshot.id;
    setSelectedReviewedId(props.screenshot.id);
    setSelectedComparisonId(null);
  }, [props.screenshot.id]);

  useEffect(() => {
    if (!showLatestReviewedOnly || visibleReviewedImages.some((image) => image.id === selectedReviewedId)) {
      return;
    }

    const selectedReviewedImage = reviewedImages.find((image) => image.id === selectedReviewedId);
    const nextReviewed = selectedReviewedImage
      ? latestScreenshotVersionForStack(selectedReviewedImage, reviewedImages)
      : visibleReviewedImages[0];
    if (!nextReviewed || nextReviewed.id === selectedReviewedId) {
      return;
    }

    flushCurrentDrafts();
    selectedReviewedIdRef.current = nextReviewed.id;
    setSelectedReviewedId(nextReviewed.id);
    setSelectedComparisonId(null);
  }, [reviewedImages, selectedReviewedId, showLatestReviewedOnly, visibleReviewedImages]);

  useEffect(() => {
    props.onReviewedScreenshotChanged(selectedReviewed.id);
  }, [props.onReviewedScreenshotChanged, selectedReviewed.id]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setCommentNotice(null);
    setComments([]);
    if (!canCurrentSessionViewComments()) {
      setCommentNotice(commentVisibilityNotice());
      return () => {
        cancelled = true;
      };
    }
    listScreenshotComments(props.app.id, selectedReviewed.id, props.session)
      .then((nextComments) => {
        if (!cancelled) {
          setCommentNotice(null);
          setComments(nextComments);
          props.onCommentsChanged(selectedReviewed.id, nextComments);
          restoreReplyDraftForComments(nextComments);
        }
      })
      .catch((loadError: Error) => {
        if (!cancelled) {
          if (loadError instanceof ApiError && loadError.status === 401 && props.session.mode === "public") {
            setCommentNotice(commentVisibilityNotice());
          } else {
            setError(loadError.message);
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.app.commentVisibilityPolicy, props.app.id, selectedReviewed.id, props.session]);

  function selectReviewedOffset(offset: number) {
    const currentIndex = visibleReviewedImages.findIndex((image) => image.id === selectedReviewed.id);
    const nextImage = visibleReviewedImages[currentIndex + offset];
    if (nextImage) {
      selectReviewedImage(nextImage.id);
    }
  }

  useEffect(() => {
    function onDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeReviewModal();
        return;
      }
      if (isInteractiveElement(document.activeElement)) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        selectReviewedOffset(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        selectReviewedOffset(1);
      }
    }

    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [props.onClose, selectedReviewed.id, visibleReviewedImages]);

  useEffect(() => {
    setDescription(descriptionImage?.ownerDescription ?? "");
  }, [descriptionImage?.id, descriptionImage?.ownerDescription]);

  const updateReviewPanePager = useCallback(() => {
    const imagePair = reviewImagePairRef.current;
    if (!imagePair) {
      return;
    }

    const panes = Array.from(imagePair.querySelectorAll<HTMLElement>(".review-image-pane"));
    if (!panes.length) {
      setActiveReviewPaneIndex(0);
      return;
    }

    const pairRect = imagePair.getBoundingClientRect();
    const pairCenter = pairRect.left + imagePair.clientWidth / 2;
    let nextIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    panes.forEach((pane, index) => {
      const paneRect = pane.getBoundingClientRect();
      const paneCenter = paneRect.left + paneRect.width / 2;
      const distance = Math.abs(paneCenter - pairCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    setActiveReviewPaneIndex((current) => current === nextIndex ? current : nextIndex);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateReviewPanePager);
    return () => window.cancelAnimationFrame(frame);
  }, [selectedReviewed.id, comparisonImages.length, updateReviewPanePager]);

  useEffect(() => {
    if (reviewPaneIntroPlayedRef.current) {
      return;
    }

    let cancelled = false;
    let attemptFrame = 0;
    let scrollFrame = 0;
    let scrollTimer = 0;
    let attempts = 0;
    const maxAttempts = 18;

    function scheduleAttempt() {
      attemptFrame = window.requestAnimationFrame(runAttempt);
    }

    function runAttempt() {
      if (cancelled || reviewPaneIntroPlayedRef.current) {
        return;
      }

      const imagePair = reviewImagePairRef.current;
      if (!imagePair) {
        if (attempts++ < maxAttempts) {
          scheduleAttempt();
        }
        return;
      }

      const panes = imagePair.querySelectorAll<HTMLElement>(".review-image-pane");
      const style = window.getComputedStyle(imagePair);
      const canPage = panes.length > 1 && imagePair.scrollWidth > imagePair.clientWidth + 1;
      const snapEnabled = style.scrollSnapType !== "none";
      if (!canPage || !snapEnabled) {
        updateReviewPanePager();
        if (attempts++ < maxAttempts) {
          scrollTimer = window.setTimeout(scheduleAttempt, 80);
        }
        return;
      }

      reviewPaneIntroPlayedRef.current = true;
      imagePair.scrollTo({ left: 0, behavior: "auto" });
      setActiveReviewPaneIndex(0);

      scrollTimer = window.setTimeout(() => {
        scrollFrame = window.requestAnimationFrame(() => {
          if (!cancelled) {
            scrollToReviewPane(1);
          }
        });
      }, 520);
    }

    scheduleAttempt();

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(attemptFrame);
      window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(scrollTimer);
    };
  }, [updateReviewPanePager]);

  function scrollToReviewPane(index: number) {
    setActiveReviewPaneIndex(index);

    const imagePair = reviewImagePairRef.current;
    const pane = imagePair?.querySelectorAll<HTMLElement>(".review-image-pane")[index];
    if (!imagePair || !pane) {
      return;
    }

    const targetLeft = pane.getBoundingClientRect().left - imagePair.getBoundingClientRect().left + imagePair.scrollLeft;
    imagePair.scrollTo({ left: targetLeft, behavior: "smooth" });
  }

  useEffect(() => {
    if (replyTo) {
      replyTextareaRef.current?.focus();
    }
  }, [replyTo]);

  useEffect(() => {
    if (needsWorkDraftOpen) {
      needsWorkTextareaRef.current?.focus();
    }
  }, [needsWorkDraftOpen]);

  useEffect(() => {
    setSelectedComparisonId(null);
    setConfirmRemoveId(null);
    restoreScreenshotDrafts();
  }, [draftIdentityScope, selectedReviewed.id]);

  useEffect(() => {
    if (!draftIdentityScope) {
      return;
    }
    const imported = transferCommentDraftAuthHandoff(draftIdentityScope, props.app.id, selectedReviewed.id);
    if (imported) {
      restoreScreenshotDrafts({ showNotice: true });
    }
  }, [draftIdentityScope, props.app.id, selectedReviewed.id]);

  useEffect(() => {
    if (!draftIdentityScope) {
      return;
    }
    if (commentAutosaveTimerRef.current !== null) {
      window.clearTimeout(commentAutosaveTimerRef.current);
    }
    commentAutosaveTimerRef.current = window.setTimeout(() => {
      commentAutosaveTimerRef.current = null;
      const identityScope = draftIdentityScopeRef.current;
      if (!identityScope) {
        return;
      }
      upsertCommentDraft({
        identityScope,
        appId: props.app.id,
        screenshotId: selectedDraftScreenshotIdRef.current,
        kind: "comment",
        body
      });
    }, commentDraftAutosaveDelayMs);
    return () => {
      if (commentAutosaveTimerRef.current !== null) {
        window.clearTimeout(commentAutosaveTimerRef.current);
        commentAutosaveTimerRef.current = null;
      }
    };
  }, [body]);

  useEffect(() => {
    if (!draftIdentityScope) {
      return;
    }
    if (needsWorkAutosaveTimerRef.current !== null) {
      window.clearTimeout(needsWorkAutosaveTimerRef.current);
    }
    needsWorkAutosaveTimerRef.current = window.setTimeout(() => {
      needsWorkAutosaveTimerRef.current = null;
      const identityScope = draftIdentityScopeRef.current;
      if (!identityScope) {
        return;
      }
      upsertCommentDraft({
        identityScope,
        appId: props.app.id,
        screenshotId: selectedDraftScreenshotIdRef.current,
        kind: "needsWork",
        body: needsWorkBody
      });
    }, commentDraftAutosaveDelayMs);
    return () => {
      if (needsWorkAutosaveTimerRef.current !== null) {
        window.clearTimeout(needsWorkAutosaveTimerRef.current);
        needsWorkAutosaveTimerRef.current = null;
      }
    };
  }, [needsWorkBody]);

  useEffect(() => {
    if (!draftIdentityScope || !replyTo) {
      return;
    }
    if (replyAutosaveTimerRef.current !== null) {
      window.clearTimeout(replyAutosaveTimerRef.current);
    }
    replyAutosaveTimerRef.current = window.setTimeout(() => {
      replyAutosaveTimerRef.current = null;
      const identityScope = draftIdentityScopeRef.current;
      if (!identityScope || !replyTo) {
        return;
      }
      upsertCommentDraft({
        identityScope,
        appId: props.app.id,
        screenshotId: selectedDraftScreenshotIdRef.current,
        kind: "reply",
        parentCommentId: replyTo,
        body: replyBody
      });
    }, commentDraftAutosaveDelayMs);
    return () => {
      if (replyAutosaveTimerRef.current !== null) {
        window.clearTimeout(replyAutosaveTimerRef.current);
        replyAutosaveTimerRef.current = null;
      }
    };
  }, [replyBody, replyTo]);

  function clearCommentAutosaveTimers() {
    for (const timer of [commentAutosaveTimerRef, needsWorkAutosaveTimerRef, replyAutosaveTimerRef]) {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }

  function restoreScreenshotDrafts(options: { showNotice?: boolean } = {}) {
    if (!draftIdentityScope) {
      setBody("");
      setReplyBody("");
      setReplyTo(null);
      setNeedsWorkBody("");
      setNeedsWorkDraftOpen(false);
      return;
    }

    const restoredKinds: string[] = [];
    let restoredLocalGuestDraft = false;
    const commentDraft = readCommentDraftForIdentityWithGuestFallback(draftIdentityScope, props.app.id, selectedReviewed.id, "comment");
    setBody(commentDraft?.body ?? "");
    if (commentDraft?.body.trim()) {
      restoredKinds.push("comment");
      restoredLocalGuestDraft ||= Boolean(commentDraft.restoredFromGuest);
    }

    const needsWorkDraft = readCommentDraftForIdentityWithGuestFallback(draftIdentityScope, props.app.id, selectedReviewed.id, "needsWork");
    setNeedsWorkBody(needsWorkDraft?.body ?? "");
    setNeedsWorkDraftOpen(Boolean(needsWorkDraft?.body.trim()));
    if (needsWorkDraft?.body.trim()) {
      restoredKinds.push("needsWork");
      restoredLocalGuestDraft ||= Boolean(needsWorkDraft.restoredFromGuest);
    }

    setReplyBody("");
    setReplyTo(null);
    if (restoredKinds.length && options.showNotice !== false) {
      showRestoredDraftNotice(
        restoredKinds.join("|"),
        restoredLocalGuestDraft ? "Restored a local draft from this browser." : undefined
      );
    }
  }

  function restoreReplyDraftForComments(nextComments: StoreScreenshotComment[] = comments) {
    if (!draftIdentityScope) {
      return;
    }
    const parentIds = new Set(nextComments.map((comment) => comment.id));
    const replyDrafts = readReplyCommentDraftsForIdentityWithGuestFallback(draftIdentityScope, props.app.id, selectedReviewed.id);
    let restoredReply: (StoredCommentDraft & { restoredFromGuest?: boolean }) | null = null;
    for (const draft of replyDrafts) {
      if (draft.parentCommentId && parentIds.has(draft.parentCommentId)) {
        restoredReply = restoredReply ?? draft;
      } else if (draft.parentCommentId) {
        removeCommentDraft(draft.identityScope, draft.appId, draft.screenshotId, "reply", draft.parentCommentId);
      }
    }
    if (!restoredReply?.parentCommentId) {
      return;
    }
    setReplyTo(restoredReply.parentCommentId);
    setReplyBody(restoredReply.body);
    showRestoredDraftNotice(
      `reply:${restoredReply.parentCommentId}`,
      restoredReply.restoredFromGuest ? "Restored a local draft from this browser." : undefined
    );
  }

  function showRestoredDraftNotice(key: string, message = "Restored your draft.") {
    const noticeKey = `${draftIdentityScope ?? "none"}:${selectedReviewed.id}:${key}`;
    if (restoredDraftNoticeKeyRef.current === noticeKey) {
      return;
    }
    restoredDraftNoticeKeyRef.current = noticeKey;
    setCommentNotice(message);
  }

  function commentDraftKindForAction(action: "comment" | "reply" | "needsWork" | "ok"): CommentDraftKind | null {
    if (action === "comment" || action === "reply" || action === "needsWork") {
      return action;
    }
    return null;
  }

  function persistDraftFromRequest(
    action: "comment" | "reply" | "needsWork" | "ok",
    request: CreateStoreScreenshotCommentRequest,
    identityScope: string | null = draftIdentityScopeRef.current
  ): CommentDraftKind | null {
    const kind = commentDraftKindForAction(action);
    if (!kind || !identityScope) {
      return null;
    }
    upsertCommentDraft({
      identityScope,
      appId: props.app.id,
      screenshotId: selectedReviewed.id,
      kind,
      parentCommentId: request.parentCommentId ?? null,
      body: request.body ?? ""
    });
    return kind;
  }

  function writeDraftAuthHandoffForRequest(
    action: "comment" | "reply" | "needsWork" | "ok",
    request: CreateStoreScreenshotCommentRequest,
    identityScope: string | null = draftIdentityScopeRef.current
  ) {
    const kind = persistDraftFromRequest(action, request, identityScope);
    if (!kind || !identityScope) {
      return;
    }
    writeCommentDraftAuthHandoff({
      sourceIdentityScope: identityScope,
      appId: props.app.id,
      screenshotId: selectedReviewed.id,
      kind,
      parentCommentId: request.parentCommentId ?? null
    });
  }

  function clearSubmittedDraft(
    action: "comment" | "reply" | "needsWork" | "ok",
    request: CreateStoreScreenshotCommentRequest,
    identityScope: string | null
  ) {
    const kind = commentDraftKindForAction(action);
    if (!kind || !identityScope) {
      return;
    }
    removeCommentDraft(identityScope, props.app.id, selectedReviewed.id, kind, request.parentCommentId ?? null);
  }

  function clearActiveDraft(kind: CommentDraftKind, parentCommentId?: string | null) {
    const identityScope = draftIdentityScopeRef.current;
    if (!identityScope) {
      return;
    }
    removeCommentDraft(identityScope, props.app.id, selectedReviewed.id, kind, parentCommentId);
  }

  function flushCurrentDrafts() {
    clearCommentAutosaveTimers();
    const identityScope = draftIdentityScopeRef.current;
    if (!identityScope) {
      return;
    }
    const screenshotId = selectedDraftScreenshotIdRef.current;
    upsertCommentDraft({
      identityScope,
      appId: props.app.id,
      screenshotId,
      kind: "comment",
      body
    });
    upsertCommentDraft({
      identityScope,
      appId: props.app.id,
      screenshotId,
      kind: "needsWork",
      body: needsWorkBody
    });
    if (replyTo) {
      upsertCommentDraft({
        identityScope,
        appId: props.app.id,
        screenshotId,
        kind: "reply",
        parentCommentId: replyTo,
        body: replyBody
      });
    }
  }

  function isSubmitShortcut(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
    return event.key === "Enter" && event.shiftKey && !event.nativeEvent.isComposing;
  }

  async function saveComment() {
    if (!body.trim()) {
      return;
    }
    await createComment({ body: body.trim(), commentType: "COMMENT" }, () => setBody(""), "comment");
  }

  async function submitComment(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await saveComment();
  }

  async function submitReply(parentCommentId: string) {
    if (!replyBody.trim()) {
      return;
    }
    await createComment({ body: replyBody.trim(), parentCommentId, commentType: "COMMENT" }, () => {
      setReplyBody("");
      setReplyTo(null);
    }, "reply");
  }

  async function markOk() {
    await createComment({ body: "OK", commentType: "OK" }, undefined, "ok");
  }

  function markNeedsWork() {
    setReplyBody("");
    setReplyTo(null);
    setNeedsWorkDraftOpen(true);
    window.setTimeout(() => needsWorkTextareaRef.current?.focus(), 0);
  }

  async function saveNeedsWork() {
    if (!needsWorkBody.trim()) {
      return;
    }
    await createComment({ body: needsWorkBody.trim(), commentType: "NEEDS_WORK" }, () => {
      setNeedsWorkBody("");
      setNeedsWorkDraftOpen(false);
    }, "needsWork");
  }

  async function submitNeedsWork(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await saveNeedsWork();
  }

  async function createComment(
    request: CreateStoreScreenshotCommentRequest,
    onSuccess?: () => void,
    action: "comment" | "reply" | "needsWork" | "ok" = "comment"
  ): Promise<StoreScreenshotComment[] | null> {
    const draftIdentityScopeAtSubmit = draftIdentityScopeRef.current;
    persistDraftFromRequest(action, request, draftIdentityScopeAtSubmit);

    if (!canCurrentSessionAuthorComments()) {
      writeDraftAuthHandoffForRequest(action, request, draftIdentityScopeAtSubmit);
      props.onSignInRequired?.();
      setCommentNotice("Sign in to add comments to this workspace.");
      return null;
    }

    if (isAnonymousReviewSession()) {
      const anonymousName = props.currentUser?.displayName ?? readAnonymousReviewerName();
      if (!anonymousName) {
        setIdentityDisplayName("");
        setIdentityShareEmail(false);
        setIdentityError(null);
        setPendingCommentSubmission({ kind: "anonymous", action, request, onSuccess, draftIdentityScope: draftIdentityScopeAtSubmit });
        return null;
      }
      if (props.session.mode === "public" || (props.session.isAnonymous && !props.currentUser?.reviewerIdentityConfigured)) {
        setSaving(true);
        setSavingCommentAction(action);
        setError(null);
        setCommentNotice(null);
        try {
          const anonymousSession = await ensureAnonymousReviewSession(anonymousName);
          return submitCommentRequest(withAnonymousIdentity(request, anonymousName), onSuccess, anonymousSession, action, draftIdentityScopeAtSubmit);
        } catch (anonymousError) {
          setError(anonymousError instanceof Error ? anonymousError.message : "Failed to prepare anonymous reviewer.");
          setSaving(false);
          setSavingCommentAction(null);
          return null;
        }
      }
      return submitCommentRequest(withAnonymousIdentity(request, anonymousName), onSuccess, undefined, action, draftIdentityScopeAtSubmit);
    }

    if (shouldPromptForCommentIdentity()) {
      setIdentityDisplayName(props.currentUser?.displayName ?? "");
      setIdentityShareEmail(props.currentUser?.shareEmailWithAppOwner ?? false);
      setIdentityError(null);
      setPendingCommentSubmission({ kind: "signed-in", action, request, onSuccess, draftIdentityScope: draftIdentityScopeAtSubmit });
      return null;
    }

    return submitCommentRequest(withAuthorIdentity(request, props.currentUser), onSuccess, undefined, action, draftIdentityScopeAtSubmit);
  }

  async function submitPendingCommentWithIdentity(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!pendingCommentSubmission) {
      return;
    }

    setSavingIdentity(true);
    setIdentityError(null);
    try {
      if (pendingCommentSubmission.kind === "anonymous") {
        const anonymousName = normalizeAnonymousReviewerName(identityDisplayName);
        if (!anonymousName) {
          setIdentityError("Name is required.");
          return;
        }
        writeAnonymousReviewerName(anonymousName);
        const anonymousSession = await ensureAnonymousReviewSession(anonymousName);
        const submission = pendingCommentSubmission;
        setPendingCommentSubmission(null);
        await submitCommentRequest(withAnonymousIdentity(submission.request, anonymousName), submission.onSuccess, anonymousSession, submission.action, submission.draftIdentityScope ?? null);
        return;
      }

      if (!props.currentUser) {
        return;
      }

      const updatedUser = await updateMyCommentIdentity(
        {
          displayName: identityDisplayName.trim() || null,
          shareEmailWithAppOwner: identityShareEmail
        },
        props.session
      );
      props.onUserUpdated?.(updatedUser);
      const submission = pendingCommentSubmission;
      setPendingCommentSubmission(null);
      await submitCommentRequest(withAuthorIdentity(submission.request, updatedUser), submission.onSuccess, undefined, submission.action, submission.draftIdentityScope ?? null);
    } catch (saveIdentityError) {
      setIdentityError(saveIdentityError instanceof Error ? saveIdentityError.message : "Failed to save reviewer identity.");
    } finally {
      setSavingIdentity(false);
    }
  }

  function shouldPromptForCommentIdentity(): boolean {
    return props.session.mode === "firebase" && !props.session.isAnonymous && Boolean(props.currentUser && !props.currentUser.reviewerIdentityConfigured);
  }

  function canCurrentSessionAuthorComments(): boolean {
    return !isAnonymousReviewSession() || props.app.commentAuthorPolicy === "ANYONE";
  }

  function canCurrentSessionViewComments(): boolean {
    return canSessionViewComments(props.session);
  }

  function canSessionViewComments(session: SessionState): boolean {
    return !isAnonymousReviewSession(session) || props.app.commentVisibilityPolicy === "EVERYONE";
  }

  function isAnonymousReviewSession(session: SessionState = props.session): boolean {
    return session.mode === "public" || session.isAnonymous;
  }

  async function ensureAnonymousReviewSession(displayName: string): Promise<SessionState> {
    if (props.session.mode === "firebase" && props.session.isAnonymous) {
      const updatedUser = await updateMyCommentIdentity(
        {
          displayName,
          shareEmailWithAppOwner: false
        },
        props.session
      );
      props.onUserUpdated?.(updatedUser);
      return props.session;
    }

    const anonymousUser = await signInAnonymouslyForReview();
    const anonymousSession = createFirebaseSession(anonymousUser);
    const updatedUser = await updateMyCommentIdentity(
      {
        displayName,
        shareEmailWithAppOwner: false
      },
      anonymousSession
    );
    props.onUserUpdated?.(updatedUser);
    return anonymousSession;
  }

  function commentVisibilityNotice(): string {
    if (props.app.commentVisibilityPolicy === "SIGNED_IN") {
      return "Sign in to view comments for this workspace.";
    }
    return "Comments are limited by this workspace owner.";
  }

  function withAuthorIdentity(request: CreateStoreScreenshotCommentRequest, user: UserProfile | null): CreateStoreScreenshotCommentRequest {
    if (!user) {
      return request;
    }
    return {
      ...request,
      authorDisplayName: user.displayName ?? null
    };
  }

  function withAnonymousIdentity(request: CreateStoreScreenshotCommentRequest, displayName: string): CreateStoreScreenshotCommentRequest {
    return {
      ...request,
      authorDisplayName: displayName
    };
  }

  async function submitCommentRequest(
    request: CreateStoreScreenshotCommentRequest,
    onSuccess?: () => void,
    sessionOverride?: SessionState,
    action: "comment" | "reply" | "needsWork" | "ok" = "comment",
    draftIdentityScopeToClear: string | null = draftIdentityScopeRef.current
  ): Promise<StoreScreenshotComment[] | null> {
    const requestSession = sessionOverride ?? props.session;
    setSaving(true);
    setSavingCommentAction(action);
    setError(null);
    setCommentNotice(null);
    try {
      const nextComments = await createScreenshotComment(props.app.id, selectedReviewed.id, request, requestSession);
      if (canSessionViewComments(requestSession)) {
        setCommentNotice(null);
        setComments(nextComments);
        props.onCommentsChanged(selectedReviewed.id, nextComments);
      } else {
        setCommentNotice("Comment added. Sign in to view comments for this workspace.");
      }
      clearSubmittedDraft(action, request, draftIdentityScopeToClear);
      onSuccess?.();
      return nextComments;
    } catch (saveError) {
      if (saveError instanceof ApiError && saveError.status === 401 && isAnonymousReviewSession()) {
        writeDraftAuthHandoffForRequest(action, request, draftIdentityScopeToClear);
        props.onSignInRequired?.();
        setCommentNotice("Sign in to add comments to this workspace.");
      } else {
        setError(saveError instanceof Error ? saveError.message : "Failed to save comment.");
      }
      return null;
    } finally {
      setSaving(false);
      setSavingCommentAction(null);
    }
  }

  async function removeComment(commentId: string) {
    setSaving(true);
    setError(null);
    try {
      const nextComments = await deleteScreenshotComment(props.app.id, selectedReviewed.id, commentId, props.session);
      setComments(nextComments);
      props.onCommentsChanged(selectedReviewed.id, nextComments);
      setConfirmRemoveId(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Failed to remove comment.");
    } finally {
      setSaving(false);
    }
  }

  async function resolveComment(commentId: string) {
    setSaving(true);
    setError(null);
    try {
      const nextComments = await resolveScreenshotComment(props.app.id, selectedReviewed.id, commentId, props.session);
      setComments(nextComments);
      props.onCommentsChanged(selectedReviewed.id, nextComments);
    } catch (resolveError) {
      setError(resolveError instanceof Error ? resolveError.message : "Failed to resolve comment.");
    } finally {
      setSaving(false);
    }
  }

  async function unresolveComment(commentId: string) {
    setSaving(true);
    setError(null);
    try {
      const nextComments = await unresolveScreenshotComment(props.app.id, selectedReviewed.id, commentId, props.session);
      setComments(nextComments);
      props.onCommentsChanged(selectedReviewed.id, nextComments);
    } catch (unresolveError) {
      setError(unresolveError instanceof Error ? unresolveError.message : "Failed to unresolve comment.");
    } finally {
      setSaving(false);
    }
  }

  async function copyCommentBody(comment: StoreScreenshotComment) {
    try {
      await copyTextToClipboard(formatCommentBody(comment));
      notify("Copied comment.");
    } catch {
      notify("Failed to copy comment.");
    }
  }

  async function saveDescription() {
    if (!descriptionImage) {
      return;
    }
    setSavingDescription(true);
    setError(null);
    try {
      await props.onUpdateDescription(descriptionImage.id, description.trim() || null);
    } catch (descriptionError) {
      setError(descriptionError instanceof Error ? descriptionError.message : "Failed to save description.");
    } finally {
      setSavingDescription(false);
    }
  }

  function closeReviewModal() {
    flushCurrentDrafts();
    props.onClose();
  }

  function selectReviewedImage(imageId: string) {
    if (imageId === selectedReviewed.id) {
      return;
    }
    flushCurrentDrafts();
    selectedReviewedIdRef.current = imageId;
    setSelectedReviewedId(imageId);
  }

  function rememberReviewImageDimensions(image: StoreScreenshot, dimensions: ImageDimensions) {
    setReviewImageDimensions((current) => {
      const existing = current[image.id];
      if (existing?.width === dimensions.width && existing.height === dimensions.height) {
        return current;
      }
      return { ...current, [image.id]: dimensions };
    });
  }

  const latestReviewedToggle = reviewedImages.length > latestReviewedImages.length ? (
    <label className="review-latest-toggle">
      <input
        type="checkbox"
        checked={showLatestReviewedOnly}
        onChange={(event) => setShowLatestReviewedOnly(event.target.checked)}
      />
      <span>Latest only</span>
    </label>
  ) : null;

  function commentAuthorLabel(comment: StoreScreenshotComment): string {
    return comment.authorDisplayName?.trim() || comment.authorPublicLabel || "Anonymous";
  }

  function renderComment(comment: StoreScreenshotComment, reply = false) {
    const canViewStableReviewerId = Boolean(props.currentUserId && props.currentUserId === props.app.ownerUserId);
    const canRemove = Boolean(props.currentUserId && (comment.authorUserId === props.currentUserId || props.currentUserId === props.app.ownerUserId));
    const canReply = !reply && (comment.commentType === "COMMENT" || comment.commentType === "NEEDS_WORK" || comment.commentType === "OK");
    const isOwner = Boolean(props.currentUserId && props.currentUserId === props.app.ownerUserId);
    const canToggleResolution =
      !reply &&
      (comment.commentType === "COMMENT" || comment.commentType === "NEEDS_WORK" || comment.commentType === "OK") &&
      isOwner;
    const canResolve = canToggleResolution && !comment.resolvedAt;
    const confirmingRemove = confirmRemoveId === comment.id;
    const commentBody = formatCommentBody(comment);
    const toggleReply = () => {
      if (!canReply) {
        return;
      }
      flushCurrentDrafts();
      if (replyTo === comment.id) {
        setReplyTo(null);
        setReplyBody("");
        return;
      }
      const replyDraft = draftIdentityScope
        ? readCommentDraftForIdentityWithGuestFallback(draftIdentityScope, props.app.id, selectedReviewed.id, "reply", comment.id)
        : null;
      setReplyTo(comment.id);
      setReplyBody(replyDraft?.body ?? "");
      if (replyDraft?.body.trim()) {
        showRestoredDraftNotice(
          `reply:${comment.id}`,
          replyDraft.restoredFromGuest ? "Restored a local draft from this browser." : undefined
        );
      }
    };
    return (
      <article
        className={`comment-item ${comment.commentType === "OK" ? "ok" : ""} ${comment.commentType === "NEEDS_WORK" ? "needs-work" : ""} ${comment.resolvedAt ? "resolved" : ""} ${reply ? "reply" : ""}`}
        key={comment.id}
      >
        <div className="comment-meta">
          <span className="comment-author">
            <strong
              className="comment-author-name tooltip-shell"
              data-tooltip={canViewStableReviewerId ? comment.authorPublicLabel : undefined}
            >
              <span>{commentAuthorLabel(comment)}</span>
            </strong>
            {comment.authorEmail ? <small>{comment.authorEmail}</small> : null}
          </span>
          {comment.resolvedAt && canToggleResolution ? (
            <button className="comment-status-button" type="button" disabled={saving} onClick={() => unresolveComment(comment.id)}>
              <span>Resolved</span>
              <span>Unresolve</span>
            </button>
          ) : (
            <span>{comment.resolvedAt ? "Resolved" : new Date(comment.createdAt).toLocaleString()}</span>
          )}
        </div>
        <p className="comment-body">
          <span className="comment-body-text">{commentBody}</span>
          <button
            className="comment-copy-button comment-copy-inline-button"
            type="button"
            aria-label="Copy comment"
            title="Copy comment"
            onClick={() => void copyCommentBody(comment)}
          >
            <CopyIcon />
          </button>
        </p>
        <div className="comment-row-actions comment-item-actions">
          <div>
            {canReply ? (
              <button className="comment-reply-button" type="button" onClick={toggleReply}>
                Reply
              </button>
            ) : null}
          </div>
          <div className="comment-row-actions-right">
            {canResolve ? (
              <button className="comment-reply-button" type="button" disabled={saving} onClick={() => resolveComment(comment.id)}>
                Resolve
              </button>
            ) : null}
            {canRemove && !confirmingRemove ? (
              <button className="comment-reply-button danger-text" type="button" disabled={saving} onClick={() => setConfirmRemoveId(comment.id)}>
                Remove
              </button>
            ) : null}
          </div>
        </div>
        {confirmingRemove ? (
          <div className="comment-confirm-row">
            <span>Remove this comment?</span>
            <button className="comment-reply-button danger-text" type="button" disabled={saving} onClick={() => removeComment(comment.id)}>
              Remove
            </button>
            <button className="comment-reply-button" type="button" disabled={saving} onClick={() => setConfirmRemoveId(null)}>
              Cancel
            </button>
          </div>
        ) : null}
        {!reply ? comment.replies.map((child) => renderComment(child, true)) : null}
        {!reply && replyTo === comment.id ? (
          <div className="comment-reply-form">
            <textarea
              ref={replyTextareaRef}
              value={replyBody}
              onChange={(event) => setReplyBody(event.target.value)}
              onKeyDown={(event) => {
                if (isSubmitShortcut(event)) {
                  event.preventDefault();
                  void submitReply(comment.id);
                }
              }}
              placeholder="Reply"
            />
            <p className="comment-shortcut-note">Shift+Enter to send reply.</p>
            <div className="comment-row-actions">
              <button className="ghost-button" type="button" disabled={saving || !replyBody.trim()} onClick={() => submitReply(comment.id)}>
                Send reply
              </button>
              <button
                className="comment-reply-button"
                type="button"
                disabled={saving}
                onClick={() => {
                  clearActiveDraft("reply", comment.id);
                  setReplyBody("");
                  setReplyTo(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </article>
    );
  }

  return createPortal(
    <div className="review-modal-backdrop" role="presentation" onMouseDown={closeReviewModal}>
      <section className="review-modal-panel" role="dialog" aria-modal="true" aria-labelledby="review-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">{selectedReviewed.platform === "IOS" ? "iOS" : "Android"} / {selectedReviewed.localeTag}</p>
            <h2 id="review-modal-title">Screenshot review</h2>
          </div>
          <button className="ghost-button icon-button" type="button" onClick={closeReviewModal} aria-label="Close comments">
            <XIcon />
          </button>
        </div>
        <div className={`review-modal-layout ${landscapeAsset ? "landscape" : "portrait"} ${stackImagePanes ? "stacked" : ""}`}>
          <div
            className="review-image-pair"
            ref={reviewImagePairRef}
            onScroll={updateReviewPanePager}
          >
            <ReviewImagePane
              title="Reference"
              images={selectedReviewedIsReference ? visibleReviewedImages : comparisonImages}
              selectedId={selectedReviewedIsReference ? selectedReviewed.id : selectedComparison?.id ?? null}
              emptyText="No reference screenshot for this position."
              onSelect={selectedReviewedIsReference ? selectReviewedImage : setSelectedComparisonId}
              onImageLoadError={props.onImageLoadError}
              onImageLoadDimensions={rememberReviewImageDimensions}
              layoutAspectRatio={reviewLayoutAspectRatio}
              countByVersionStack={selectedReviewedIsReference}
              versionStackImages={selectedReviewedIsReference ? reviewedImages : undefined}
              headerAction={selectedReviewedIsReference ? latestReviewedToggle : null}
            />
            <ReviewImagePane
              title="Under review"
              images={selectedReviewedIsReference ? [] : visibleReviewedImages}
              selectedId={selectedReviewedIsReference ? null : selectedReviewed.id}
              emptyText={selectedReviewedIsReference ? "Select a locale to review against this reference." : "No screenshot under review."}
              emptyContent={selectedReviewedIsReference ? (
                <ReferenceLocaleReviewSwitcher
                  options={referenceReviewOptions}
                  onSelect={props.onSwitchReviewedScreenshot}
                />
              ) : undefined}
              onSelect={selectReviewedImage}
              onImageLoadError={props.onImageLoadError}
              onImageLoadDimensions={rememberReviewImageDimensions}
              layoutAspectRatio={reviewLayoutAspectRatio}
              countByVersionStack={!selectedReviewedIsReference}
              versionStackImages={selectedReviewedIsReference ? undefined : reviewedImages}
              headerAction={selectedReviewedIsReference ? null : latestReviewedToggle}
            />
          </div>
          <div className="review-image-pager-dots" role="group" aria-label="Screenshot panel position">
            {["Reference", "Under review"].map((label, index) => {
              const active = activeReviewPaneIndex === index;
              return (
                <button
                  className={`review-image-pager-dot ${active ? "active" : ""}`}
                  key={label}
                  type="button"
                  aria-label={`Show ${label.toLowerCase()} panel`}
                  aria-current={active ? "true" : undefined}
                  onClick={() => scrollToReviewPane(index)}
                />
              );
            })}
          </div>
          <aside className="review-comments-panel">
            <section className="review-description-box">
              <div>
                <p className="eyebrow">Screenshot goal</p>
                {descriptionImage ? null : <h3>No reference selected</h3>}
              </div>
              {canEditDescription ? (
                <>
                  <textarea
                    value={description}
                    maxLength={500}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Short owner note about the text goal for this screenshot"
                  />
                  <button className="ghost-button" type="button" disabled={savingDescription} onClick={saveDescription}>
                    {savingDescription ? "Saving..." : "Save description"}
                  </button>
                </>
              ) : (
                <p>{descriptionImage?.ownerDescription || "No owner description yet."}</p>
              )}
            </section>
            <div className="comment-popover-actions review-marker-actions">
              <button className="ghost-button review-marker-button negative" type="button" disabled={saving} onClick={markNeedsWork}>
                <AlertIcon />
                <span>
                  <strong>Needs work</strong>
                  <small>Translation is awkward and should be adjusted.</small>
                </span>
              </button>
              <button className="primary-button review-marker-button" type="button" disabled={saving} onClick={markOk}>
                {savingCommentAction === "ok" ? (
                  <LoadingButtonContent label="Adding..." />
                ) : (
                  <>
                    <CheckIcon />
                    <span>
                      <strong>OK</strong>
                      <small>Translation is on point.</small>
                    </span>
                  </>
                )}
              </button>
            </div>
            {selectedReviewed.missingSince ? (
              <p className="comment-note">This screenshot is no longer present in the latest store refresh.</p>
            ) : null}
            {error ? <p className="error-copy">{error}</p> : null}
            {commentNotice ? <p className="comment-note">{commentNotice}</p> : null}
            <div className="comment-list">
              {comments.map((comment) => renderComment(comment))}
            </div>
            {needsWorkDraftOpen ? (
              <form className="comment-form needs-work-draft" onSubmit={submitNeedsWork}>
                <div className="comment-meta">
                  <strong>Needs work</strong>
                  <span>Draft</span>
                </div>
                <textarea
                  ref={needsWorkTextareaRef}
                  value={needsWorkBody}
                  onChange={(event) => setNeedsWorkBody(event.target.value)}
                  onKeyDown={(event) => {
                    if (isSubmitShortcut(event)) {
                      event.preventDefault();
                      void saveNeedsWork();
                    }
                  }}
                  placeholder="Describe what should be adjusted"
                />
                <p className="comment-shortcut-note">Shift+Enter to add Needs Work.</p>
                <div className="comment-row-actions">
                  <button className="ghost-button" type="submit" disabled={saving || !needsWorkBody.trim()}>
                    {savingCommentAction === "needsWork" ? <LoadingButtonContent label="Adding..." /> : "Add Needs Work"}
                  </button>
                  <button
                    className="comment-reply-button"
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      clearActiveDraft("needsWork");
                      setNeedsWorkBody("");
                      setNeedsWorkDraftOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <form className="comment-form" onSubmit={submitComment}>
                <textarea
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  onKeyDown={(event) => {
                    if (isSubmitShortcut(event)) {
                      event.preventDefault();
                      void saveComment();
                    }
                  }}
                  placeholder="Add comment"
                />
                <p className="comment-shortcut-note">Shift+Enter to add comment.</p>
                <button className="primary-button" type="submit" disabled={saving || !body.trim()}>
                  {savingCommentAction === "comment" ? <LoadingButtonContent label="Adding..." /> : "Add comment"}
                </button>
              </form>
            )}
            <div className="review-screenshot-navigation" aria-label="Screenshot navigation">
              <button className="ghost-button" type="button" disabled={!canSelectPreviousReviewed} onClick={() => selectReviewedOffset(-1)}>
                <ChevronLeftIcon />
                Previous
              </button>
              <button className="ghost-button" type="button" disabled={!canSelectNextReviewed} onClick={() => selectReviewedOffset(1)}>
                Next
                <ChevronRightIcon />
              </button>
            </div>
          </aside>
        </div>
      </section>
      {pendingCommentSubmission ? (
        <div className="comment-identity-backdrop" role="presentation" onMouseDown={(event) => event.stopPropagation()}>
          <form
            className="comment-identity-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={identityTitleId}
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={submitPendingCommentWithIdentity}
          >
            <div>
              <h3 id={identityTitleId}>{pendingCommentSubmission.kind === "anonymous" ? "Comment attribution" : "Set your profile"}</h3>
              <p>
                {pendingCommentSubmission.kind === "anonymous"
                  ? "Enter the name shown on your comments. It will be reused from this browser."
                  : "Choose the name shown on your comments. You can change this later in Profile."}
              </p>
            </div>
            <label className="comment-identity-field">
              <span>
                {pendingCommentSubmission.kind === "anonymous" ? "Name" : "Display name"}{" "}
                {pendingCommentSubmission.kind === "anonymous" ? null : <small>optional</small>}
              </span>
              <input
                type="text"
                maxLength={80}
                value={identityDisplayName}
                onChange={(event) => setIdentityDisplayName(event.target.value)}
                placeholder="Name shown on comments"
                autoFocus
              />
            </label>
            {pendingCommentSubmission.kind === "signed-in" ? (
              <label className="comment-identity-check">
                <input
                  type="checkbox"
                  checked={identityShareEmail}
                  onChange={(event) => setIdentityShareEmail(event.target.checked)}
                />
                <span>
                  <strong>Show email to app owners</strong>
                  <small>Applies to all comments by this profile.</small>
                </span>
              </label>
            ) : null}
            {identityError ? <p className="error-copy">{identityError}</p> : null}
            <div className="comment-identity-actions">
              {pendingCommentSubmission.kind === "anonymous" && props.onSignInRequired ? (
                <button
                  className="comment-identity-sign-in"
                  type="button"
                  disabled={savingIdentity}
                  onClick={() => {
                    writeDraftAuthHandoffForRequest(pendingCommentSubmission.action, pendingCommentSubmission.request, pendingCommentSubmission.draftIdentityScope ?? null);
                    setPendingCommentSubmission(null);
                    props.onSignInRequired?.();
                  }}
                >
                  Sign in instead
                </button>
              ) : (
                <span aria-hidden="true" />
              )}
              <div className="comment-identity-submit-actions">
                <button
                  className="comment-reply-button"
                  type="button"
                  disabled={savingIdentity}
                  onClick={() => setPendingCommentSubmission(null)}
                >
                  Cancel
                </button>
                <button className="primary-button" type="submit" disabled={savingIdentity || (pendingCommentSubmission.kind === "anonymous" && !identityDisplayName.trim())}>
                  {savingIdentity ? <LoadingButtonContent label="Saving..." /> : "Save and continue"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>,
    document.body
  );
}

function ReviewImagePane(props: {
  title: string;
  images: StoreScreenshot[];
  selectedId: string | null;
  emptyText: string;
  emptyContent?: ReactNode;
  onSelect: (id: string) => void;
  onImageLoadError?: (image: StoreScreenshot) => void;
  onImageLoadDimensions?: (image: StoreScreenshot, dimensions: ImageDimensions) => void;
  layoutAspectRatio: number;
  countByVersionStack?: boolean;
  versionStackImages?: StoreScreenshot[];
  headerAction?: ReactNode;
}) {
  const selectedIndex = Math.max(0, props.images.findIndex((image) => image.id === props.selectedId));
  const image = props.images[selectedIndex] ?? props.images[0] ?? null;
  const sourceVerb = image?.sourceType === "UPLOAD" ? "Uploaded" : "Seen";
  const imageSourceUrl = image ? displayImageUrl(image) : null;
  const imageLoadKey = image && imageSourceUrl ? `${image.id}:${imageSourceUrl}` : null;
  const [loadedImageKey, setLoadedImageKey] = useState<string | null>(null);
  const [failedImageKey, setFailedImageKey] = useState<string | null>(null);
  const [loadedImageRatio, setLoadedImageRatio] = useState<{ key: string; ratio: number } | null>(null);
  const mobileFrameAspectRatio = useMemo(() => {
    if (loadedImageRatio && loadedImageRatio.key === imageLoadKey) {
      return loadedImageRatio.ratio;
    }
    if (!image) {
      return props.layoutAspectRatio;
    }

    const dimensions = getDisplayImageDimensions(image);
    if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
      return getReviewLayoutAspectRatioCandidate(image);
    }

    return dimensions.width / dimensions.height;
  }, [image, imageLoadKey, loadedImageRatio, props.layoutAspectRatio]);
  const frameStyle = {
    "--review-frame-aspect-ratio": String(props.layoutAspectRatio),
    "--review-mobile-frame-aspect-ratio": String(mobileFrameAspectRatio)
  } as CSSProperties & Record<"--review-frame-aspect-ratio" | "--review-mobile-frame-aspect-ratio", string>;
  const imageCounter = useMemo(() => {
    if (!image) {
      return null;
    }

    if (!props.countByVersionStack) {
      if (props.images.length <= 1) {
        return null;
      }

      return {
        spotIndex: selectedIndex + 1,
        spotCount: props.images.length,
        versionIndex: 0,
        versionCount: 0
      };
    }

    const versionStackImages = props.versionStackImages ?? props.images;
    const stacks = groupImageStacks(versionStackImages);
    const stackIndex = stacks.findIndex((stack) => stack.images.some((candidate) => candidate.id === image.id));
    const selectedStack = stackIndex >= 0 ? stacks[stackIndex] : null;
    const versionIndex = selectedStack?.images.findIndex((candidate) => candidate.id === image.id) ?? -1;
    const versionCount = selectedStack?.images.length ?? 0;
    if (props.images.length <= 1 && versionCount <= 1) {
      return null;
    }

    return {
      spotIndex: stackIndex >= 0 ? stackIndex + 1 : selectedIndex + 1,
      spotCount: stacks.length || props.images.length,
      versionIndex: versionIndex >= 0 ? versionIndex + 1 : 0,
      versionCount
    };
  }, [image?.id, props.countByVersionStack, props.images, props.versionStackImages, selectedIndex]);

  useEffect(() => {
    if (!imageLoadKey) {
      setLoadedImageKey(null);
      setFailedImageKey(null);
      return;
    }

    setLoadedImageKey((current) => (current === imageLoadKey ? current : null));
    setFailedImageKey((current) => (current === imageLoadKey ? current : null));
  }, [imageLoadKey]);

  function selectOffset(offset: number) {
    const nextImage = props.images[selectedIndex + offset];
    if (nextImage) {
      props.onSelect(nextImage.id);
    }
  }

  return (
    <section className="review-image-pane">
      <div className="review-image-pane-header">
        <div>
          <h3>{props.title}</h3>
          {image ? (
            <span><strong>{image.localeTag}</strong> / {image.imageType}</span>
          ) : (
            <span className="review-image-placeholder-line" aria-hidden="true">&nbsp;</span>
          )}
        </div>
        {props.headerAction ?? null}
      </div>
      {image ? (
        <>
          <div className="review-image-frame" style={frameStyle}>
            {props.images.length > 1 ? (
              <button type="button" className="image-stack-nav previous" disabled={selectedIndex === 0} onClick={() => selectOffset(-1)} aria-label="Previous screenshot">
                <ChevronLeftIcon />
              </button>
            ) : null}
            <img
              key={imageLoadKey}
              className={loadedImageKey === imageLoadKey ? "" : "loading"}
              alt={`${image.localeTag} ${image.imageType}`}
              src={imageSourceUrl ?? image.imageUrl}
              referrerPolicy="no-referrer"
              onError={() => {
                setLoadedImageKey(null);
                setLoadedImageRatio(null);
                if (imageLoadKey) {
                  setFailedImageKey(imageLoadKey);
                }
                props.onImageLoadError?.(image);
              }}
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalWidth <= 0 || naturalHeight <= 0 || !imageLoadKey) {
                  setLoadedImageKey(null);
                  setLoadedImageRatio(null);
                  return;
                }
                setLoadedImageRatio({ key: imageLoadKey, ratio: naturalWidth / naturalHeight });
                setLoadedImageKey(imageLoadKey);
                setFailedImageKey(null);
                props.onImageLoadDimensions?.(image, { width: naturalWidth, height: naturalHeight });
              }}
            />
            {loadedImageKey !== imageLoadKey ? (
              <div className="review-image-loading">
                {failedImageKey === imageLoadKey ? "Image unavailable." : "Loading image..."}
              </div>
            ) : null}
            {props.images.length > 1 ? (
              <button type="button" className="image-stack-nav next" disabled={selectedIndex >= props.images.length - 1} onClick={() => selectOffset(1)} aria-label="Next screenshot">
                <ChevronRightIcon />
              </button>
            ) : null}
          </div>
          <div className="review-image-meta">
            <span>{sourceVerb} {formatScreenshotTimestamp(image.firstRetrievedAt)}</span>
            {imageCounter ? (
              <span className="review-image-counts">
                {imageCounter.versionCount > 1 ? (
                  <span className="review-image-version-count">{imageCounter.versionIndex}/{imageCounter.versionCount}</span>
                ) : null}
                <strong>{imageCounter.spotIndex}/{imageCounter.spotCount}</strong>
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="review-image-frame empty" style={frameStyle}>
            {props.emptyContent ?? <div className="store-screenshot-empty">{props.emptyText}</div>}
          </div>
          <div className="review-image-meta review-image-placeholder-line" aria-hidden="true">
            <span>&nbsp;</span>
          </div>
        </>
      )}
    </section>
  );
}

function ReferenceLocaleReviewSwitcher(props: {
  options: StoreScreenshot[];
  onSelect: (screenshot: StoreScreenshot) => void;
}) {
  return (
    <div className="review-reference-switcher">
      <strong>Select review locale</strong>
      {props.options.length ? (
        <>
          <p>Select a locale to review against this reference.</p>
          <div>
            {props.options.map((image) => {
              const localeName = languageNameForLocale(image.localeTag);
              return (
                <button className="ghost-button" type="button" key={image.id} onClick={() => props.onSelect(image)}>
                  <span>{localeName ?? image.localeTag}</span>
                  {localeName ? <small>{image.localeTag}</small> : null}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <p>No matching non-reference locale is available for this screenshot position.</p>
      )}
    </div>
  );
}

function UploadScreenshotsModal(props: {
  platform: Platform;
  localeTag: string;
  files: File[];
  referenceImages: StoreScreenshot[];
  uploading: boolean;
  progress: Record<number, number>;
  onCancel: () => void;
  onConfirm: (items: UploadStoreScreenshotAssignment[]) => void;
}) {
  useModalScrollLock();
  const slotCount = Math.max(props.referenceImages.length, props.files.length);
  const [slots, setSlots] = useState<Array<number | null>>(() =>
    Array.from({ length: slotCount }, (_value, index) => index < props.files.length ? index : null)
  );
  const [draggedSlot, setDraggedSlot] = useState<number | null>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    setSlots(Array.from({ length: slotCount }, (_value, index) => index < props.files.length ? index : null));
  }, [props.files, slotCount]);

  useEffect(() => {
    const urls = props.files.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [props.files]);

  function moveSlot(sourceIndex: number, targetIndex: number) {
    setSlots((current) => {
      const next = [...current];
      const source = next[sourceIndex] ?? null;
      next[sourceIndex] = next[targetIndex] ?? null;
      next[targetIndex] = source;
      return next;
    });
  }

  function draggedUploadSlot(dataTransfer: DataTransfer) {
    const value = dataTransfer.getData("application/x-upload-slot") || dataTransfer.getData("text/plain");
    const source = Number(value);
    return Number.isInteger(source) && source >= 0 && source < slots.length ? source : null;
  }

  function setScreenshotDragImage(event: DragEvent<HTMLDivElement>) {
    const image = event.currentTarget.querySelector<HTMLImageElement>("img");
    if (!image) {
      return;
    }
    event.dataTransfer.setDragImage(image, image.clientWidth / 2, image.clientHeight / 2);
  }

  function confirm() {
    const items = slots.flatMap((fileIndex, position) => {
      if (fileIndex === null) {
        return [];
      }
      return [{
        fileIndex,
        position,
        targetScreenshotId: props.referenceImages[position]?.id ?? null
      }];
    });
    props.onConfirm(items);
  }

  const assignedCount = slots.filter((slot) => slot !== null).length;

  return createPortal(
    <div className="review-modal-backdrop">
      <section className="upload-modal-panel" role="dialog" aria-modal="true" aria-label="Arrange uploaded screenshots">
        <div className="upload-modal-header">
          <div>
            <p className="eyebrow">Upload screenshots</p>
            <h2>{props.platform === "IOS" ? "iOS" : "Android"} / {props.localeTag}</h2>
          </div>
        </div>

        <div className="upload-arrange-grid" style={{ "--upload-slot-count": String(slotCount) } as CSSProperties & Record<"--upload-slot-count", string>}>
          <div className="upload-arrange-label">Reference</div>
          <div className="upload-arrange-row">
            {Array.from({ length: slotCount }, (_value, index) => {
              const image = props.referenceImages[index];
              return (
                <div className="upload-arrange-cell reference" key={`reference-${index}`}>
                  {image ? (
                    <>
                      <img alt={`${image.localeTag} reference ${index + 1}`} src={tileImageUrl(image, "small")} referrerPolicy="no-referrer" />
                      <span>#{image.position + 1} / {image.imageType}</span>
                    </>
                  ) : (
                    <span>No reference</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="upload-arrange-label">Uploaded</div>
          <div className="upload-arrange-row">
            {slots.map((fileIndex, index) => {
              const file = fileIndex === null ? null : props.files[fileIndex];
              const previewUrl = fileIndex === null ? null : previewUrls[fileIndex];
              const progressValue = fileIndex === null ? 0 : props.progress[fileIndex] ?? 0;
              return (
                <div
                  className={`upload-arrange-cell uploaded ${file ? "filled" : ""}`}
                  key={`uploaded-${index}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const source = draggedUploadSlot(event.dataTransfer);
                    if (source !== null) {
                      moveSlot(source, index);
                    }
                    setDraggedSlot(null);
                  }}
                >
                  {file && previewUrl ? (
                    <div
                      className="upload-preview"
                      draggable
                      onDragStart={(event) => {
                        setDraggedSlot(index);
                        event.dataTransfer.setData("application/x-upload-slot", String(index));
                        event.dataTransfer.setData("text/plain", String(index));
                        event.dataTransfer.effectAllowed = "move";
                        setScreenshotDragImage(event);
                      }}
                      onDragEnd={() => setDraggedSlot(null)}
                    >
                      <img alt={file.name} draggable={false} src={previewUrl} />
                      <span>{file.name}</span>
                      {props.uploading ? (
                        <div className="upload-progress" aria-label={`${file.name} upload progress`}>
                          <span style={{ width: `${progressValue}%` }} />
                          <strong>{progressValue}%</strong>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <span>{draggedSlot === null ? "Drop here" : "Place here"}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="upload-modal-actions">
          <button className="ghost-button" type="button" disabled={props.uploading} onClick={props.onCancel}>
            Cancel
          </button>
          <button className="primary-button" type="button" disabled={props.uploading || assignedCount === 0} onClick={confirm}>
            {props.uploading ? "Uploading..." : "Confirm upload"}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

function StoreImageSection(props: {
  images: StoreScreenshot[];
  title: string;
  platform: Platform;
  variant?: "icon" | "screenshot";
  tileSize?: StoreTileSize;
  groupingMode?: StoreGroupingMode | null;
  singleSelectedLocale?: string | null;
  onSelect?: (image: StoreScreenshot) => void;
  onImageLoadError?: (image: StoreScreenshot) => void;
  onStack?: (screenshotId: string, targetScreenshotId: string) => Promise<void>;
  onExtract?: (screenshotId: string) => Promise<void>;
  onUpload?: (platform: Platform, localeTag: string) => void;
  onDeleteUploaded?: (screenshotId: string) => Promise<void>;
  onRemoveStore?: (screenshotId: string) => Promise<void>;
  onRefresh?: (platform: Platform, localeTag: string) => void;
  referenceLocales?: string[];
  selectedLocales?: string[];
  actionLocaleOptions?: string[];
  canRefresh?: boolean;
  refreshing?: boolean;
  hasActiveFilters?: boolean;
  measuredDimensionsByImageId?: ImageDimensionsById;
  onImageDimensionsChange?: (image: StoreScreenshot, dimensions: ImageDimensions) => void;
}) {
  const [selectedVersionByStack, setSelectedVersionByStack] = useState<Record<string, string>>({});
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [measuredTileDimensions, setMeasuredTileDimensions] = useState<Record<string, ImageDimensions>>({});
  const stacks = useMemo(
    () => props.variant === "icon"
      ? props.images.map((image) => ({ id: image.id, images: [image] }))
      : groupImageStacks(props.images),
    [props.images, props.variant]
  );
  useEffect(() => {
    const imageIds = new Set(props.images.map((image) => image.id));
    setMeasuredTileDimensions((current) => {
      const next: Record<string, ImageDimensions> = {};
      let changed = false;
      for (const [imageId, dimensions] of Object.entries(current)) {
        if (imageIds.has(imageId)) {
          next[imageId] = dimensions;
        } else {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [props.images]);
  const aspectRatioGroupKeys = props.variant === "icon"
    ? []
    : Array.from(new Set(stacks.map(stackAspectRatioGroupKey)));
  const groupByAspectRatio = props.variant !== "icon" && aspectRatioGroupKeys.length > 0;
  const groupWithinRatioByPosition = props.groupingMode === "position" && props.variant !== "icon";
  const groupWithinRatioByLocale = props.groupingMode === "locale" && props.variant !== "icon";
  const groupWithinRatio = groupWithinRatioByPosition || groupWithinRatioByLocale;
  const platformActionLocaleOptions = props.actionLocaleOptions?.length
    ? props.actionLocaleOptions
    : props.singleSelectedLocale ? [props.singleSelectedLocale] : [];
  const selectedLocaleRows = groupWithinRatioByLocale
    ? (props.selectedLocales ?? []).filter((locale) => locale !== "ALL")
    : [];
  const selectedEmptyLocaleRows = selectedLocaleRows.filter((locale) =>
    !stacks.some((stack) => stackAnchorImage(stack).localeTag === locale)
  );
  const groupedStacks = groupByAspectRatio
    ? Array.from(stacks.reduce((groups, stack) => {
        const groupKey = stackAspectRatioGroupKey(stack);
        const current = groups.get(groupKey) ?? [];
        groups.set(groupKey, [...current, stack]);
        return groups;
      }, new Map<string, ScreenshotImageStack[]>()))
        .sort(([aKey], [bKey]) => compareAspectRatioGroupKeys(aKey, bKey))
        .map(([key, groupStacks]) => ({
          key,
          stacks: [...groupStacks].sort((a, b) =>
            compareScreenshotTilesWithReference(stackAnchorImage(a), stackAnchorImage(b), props.referenceLocales ?? [])
          ),
          nestedGroups: groupWithinRatio ? groupScreenshotStacks(groupStacks, props.groupingMode ?? "position", props.referenceLocales ?? [], stackAnchorImage) : []
        }))
    : [];
  const emptyLocaleGroup = groupWithinRatioByLocale && selectedEmptyLocaleRows.length
    ? [{
        key: "empty-selected-locales",
        stacks: [],
        nestedGroups: selectedEmptyLocaleRows.map((locale) => ({ key: locale, stacks: [] as ScreenshotImageStack[] }))
      }]
    : [];
  const displayedGroups = [...groupedStacks, ...emptyLocaleGroup];
  const showGroupedRows = groupByAspectRatio || emptyLocaleGroup.length > 0;

  function activeStackIndex(stack: ScreenshotImageStack): number {
    const selectedId = selectedVersionByStack[stack.id];
    const selectedIndex = stack.images.findIndex((candidate) => candidate.id === selectedId);
    return selectedIndex >= 0 ? selectedIndex : stack.images.length - 1;
  }

  function activeStackImage(stack: ScreenshotImageStack): StoreScreenshot {
    return stack.images[activeStackIndex(stack)] ?? stack.images[0];
  }

  function stackAnchorImage(stack: ScreenshotImageStack): StoreScreenshot {
    return stack.images[0];
  }

  function tileDimensions(image: StoreScreenshot): ImageDimensions | null {
    return measuredTileDimensions[image.id] ?? props.measuredDimensionsByImageId?.[image.id] ?? getDisplayImageDimensions(image);
  }

  function tileAspectRatio(image: StoreScreenshot): number {
    const dimensions = tileDimensions(image);
    if (dimensions && dimensions.width > 0 && dimensions.height > 0) {
      return dimensions.width / dimensions.height;
    }
    return getReviewLayoutAspectRatioCandidate(image);
  }

  function stackAspectRatioGroupKey(stack: ScreenshotImageStack): string {
    return screenshotAspectRatioGroupKeyFromRatio(Math.max(tileAspectRatio(stackAnchorImage(stack)), 0.01));
  }

  function rememberTileDimensions(image: StoreScreenshot, dimensions: ImageDimensions) {
    setMeasuredTileDimensions((current) => {
      const existing = current[image.id];
      if (existing?.width === dimensions.width && existing.height === dimensions.height) {
        return current;
      }
      return { ...current, [image.id]: dimensions };
    });
    props.onImageDimensionsChange?.(image, dimensions);
  }

  function selectVersion(stack: ScreenshotImageStack, nextIndex: number) {
    const nextImage = stack.images[nextIndex];
    if (!nextImage) {
      return;
    }
    setSelectedVersionByStack((current) => ({ ...current, [stack.id]: nextImage.id }));
  }

  async function removeScreenshot(image: StoreScreenshot) {
    setDeleteConfirmationId(null);
    if (image.sourceType === "UPLOAD") {
      await props.onDeleteUploaded?.(image.id);
      return;
    }
    await props.onRemoveStore?.(image.id);
  }

  function handleGridDragOver(event: React.DragEvent<HTMLDivElement>) {
    if (props.onExtract && props.variant !== "icon") {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    }
  }

  function handleGridDrop(event: React.DragEvent<HTMLDivElement>) {
    if (!props.onExtract || props.variant === "icon") {
      return;
    }
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("application/x-store-screenshot-id") || event.dataTransfer.getData("text/plain");
    if (sourceId) {
      props.onExtract(sourceId);
    }
  }

  function groupHeader(group: { key: string; stacks: ScreenshotImageStack[] }): { label: string; detail?: string } {
    if (group.stacks.length === 0) {
      if (props.hasActiveFilters) {
        return {
          label: "No screenshots match filters"
        };
      }
      return {
        label: "No screenshots yet",
        detail: "Selected locales"
      };
    }
    return {
      label: aspectRatioGroupLabel(group.key)
    };
  }

  function nestedGroupHeader(key: string): { label: string; detail?: string } {
    if (groupWithinRatioByPosition) {
      return { label: `Position #${Number(key) + 1}` };
    }
    return {
      label: key,
      detail: props.referenceLocales?.includes(key) ? "Reference" : undefined
    };
  }

  function uploadButton(localeTag: string) {
    if (!props.onUpload || props.variant === "icon") {
      return null;
    }
    return (
      <button
        className="upload-screenshots-button"
        type="button"
        onClick={() => props.onUpload?.(props.platform, localeTag)}
        aria-label={`Upload ${props.title} for ${localeTag}`}
      >
        <PlusIcon />
      </button>
    );
  }

  function refreshButton(localeTag: string) {
    if (!props.onRefresh || props.variant === "icon") {
      return null;
    }
    const disabled = Boolean(props.refreshing || !props.canRefresh);
    const disabledReason = !props.canRefresh
      ? props.platform === "IOS" ? "Add an App Store app ID first." : "Add an Android package name first."
      : undefined;
    return (
      <span className="tooltip-shell store-locale-row-action" data-tooltip={disabledReason}>
        <button
          className={`ghost-button compact store-locale-refresh-button ${props.refreshing ? "is-loading" : ""}`}
          type="button"
          disabled={disabled}
          onClick={() => props.onRefresh?.(props.platform, localeTag)}
        >
          {props.refreshing ? <LoadingButtonContent label="Refreshing" /> : "Refresh from store"}
        </button>
      </span>
    );
  }

  function localeRowActions(localeTag: string) {
    if (!groupWithinRatioByLocale) {
      return null;
    }
    return (
      <div className="store-screenshot-group-actions">
        {refreshButton(localeTag)}
        {uploadButton(localeTag)}
      </div>
    );
  }

  function platformHeaderActions() {
    if (props.variant === "icon" || groupWithinRatioByLocale || (!props.onRefresh && !props.onUpload)) {
      return null;
    }

    const refreshDisabled = Boolean(props.refreshing || !props.canRefresh);
    const refreshDisabledReason = !props.canRefresh
      ? props.platform === "IOS" ? "Add an App Store app ID first." : "Add an Android package name first."
      : undefined;

    return (
      <div className="store-image-section-actions">
        {props.onRefresh ? (
          <PlatformLocaleActionButton
            buttonClassName="ghost-button compact store-locale-refresh-button"
            disabled={refreshDisabled}
            disabledReason={refreshDisabledReason}
            label={`Choose locale to refresh ${props.platform === "IOS" ? "iOS" : "Android"} screenshots`}
            loading={props.refreshing}
            loadingLabel="Refreshing"
            locales={platformActionLocaleOptions}
            onSelect={(locale) => props.onRefresh?.(props.platform, locale)}
          >
            Refresh from store
          </PlatformLocaleActionButton>
        ) : null}
        {props.onUpload ? (
          <PlatformLocaleActionButton
            buttonClassName="upload-screenshots-button"
            label={`Choose locale to upload ${props.platform === "IOS" ? "iOS" : "Android"} screenshots`}
            locales={platformActionLocaleOptions}
            onSelect={(locale) => props.onUpload?.(props.platform, locale)}
          >
            <PlusIcon />
          </PlatformLocaleActionButton>
        ) : null}
      </div>
    );
  }

  function renderStackTile(stack: ScreenshotImageStack) {
    const activeIndex = activeStackIndex(stack);
    const image = stack.images[activeIndex] ?? stack.images[0];
    const hasHistory = props.variant !== "icon" && stack.images.length > 1;
    const tileClass = props.variant === "icon" ? "store-icon-tile" : "store-screenshot-tile";
    const isReference = props.referenceLocales?.includes(image.localeTag) ?? false;
    const canDragTile = props.variant !== "icon" && Boolean(props.onStack || props.onExtract);
    const canSelectTile = Boolean(props.onSelect);
    const tileClasses = [
      tileClass,
      image.missingSince ? "missing" : "",
      hasHistory ? "stacked" : "",
      canSelectTile ? "can-select" : "",
      canDragTile ? "can-drag" : ""
    ].filter(Boolean).join(" ");
    const tileLabel = `${image.platform === "IOS" ? "iOS" : "Android"} ${image.localeTag} ${props.variant === "icon" ? "app icon" : `screenshot ${image.position + 1}`}`;
    const tilePreviewUrl = tileImageUrl(image, props.tileSize ?? "regular");
    const fallbackTileAspectRatio = props.variant === "icon" ? 1 : tileAspectRatio(image);
    const confirmingDelete = deleteConfirmationId === image.id;
    const sourceBadgeLabel = image.sourceType === "UPLOAD" ? "Uploaded" : "Store";
    const canRemoveTile = props.variant !== "icon" && (
      (image.sourceType === "UPLOAD" && Boolean(props.onDeleteUploaded)) ||
      (image.sourceType === "STORE" && Boolean(props.onRemoveStore))
    );
    const removeActionLabel = image.sourceType === "UPLOAD" ? "Delete" : "Remove";
    const removeAriaLabel = image.sourceType === "UPLOAD" ? "Delete uploaded screenshot" : "Remove store screenshot";
    const removeConfirmationLabel = image.sourceType === "UPLOAD" ? "Confirm uploaded screenshot deletion" : "Confirm store screenshot removal";
    const hasOk = props.variant !== "icon" && image.okCount > 0;
    const hasNeedsWork = props.variant !== "icon" && image.needsWorkCount > 0;

    return (
      <article
        className={[tileClasses, hasNeedsWork ? "has-needs-work" : "", hasOk ? "has-ok" : ""].filter(Boolean).join(" ")}
        key={stack.id}
        draggable={canDragTile}
        onClick={() => props.onSelect?.(image)}
        onDragStart={(event) => {
          const tileRect = event.currentTarget.getBoundingClientRect();
          event.dataTransfer.setData("application/x-store-screenshot-id", image.id);
          event.dataTransfer.setData("text/plain", image.id);
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setDragImage(
            event.currentTarget,
            event.clientX - tileRect.left,
            event.clientY - tileRect.top
          );
        }}
        onDragOver={(event) => {
          if (props.onStack && props.variant !== "icon") {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const sourceId = event.dataTransfer.getData("application/x-store-screenshot-id") || event.dataTransfer.getData("text/plain");
          const sourceImage = props.images.find((candidate) => candidate.id === sourceId);
          if (sourceImage && sourceImage.versionStackId !== image.versionStackId) {
            props.onStack?.(sourceId, image.id);
          }
        }}
      >
        <div className="store-image-frame">
          {hasHistory ? (
            <button
              className="image-stack-nav previous"
              type="button"
              aria-label="Previous screenshot version"
              disabled={activeIndex === 0}
              onClick={(event) => {
                event.stopPropagation();
                selectVersion(stack, activeIndex - 1);
              }}
            >
              <ChevronLeftIcon />
            </button>
          ) : null}
          <StoreTileImage
            aspectRatio={fallbackTileAspectRatio}
            label={tileLabel}
            src={tilePreviewUrl}
            square={props.variant === "icon"}
            onLoadError={() => props.onImageLoadError?.(image)}
            onLoadDimensions={(dimensions) => rememberTileDimensions(image, dimensions)}
          />
          {hasHistory ? (
            <button
              className="image-stack-nav next"
              type="button"
              aria-label="Next screenshot version"
              disabled={activeIndex >= stack.images.length - 1}
              onClick={(event) => {
                event.stopPropagation();
                selectVersion(stack, activeIndex + 1);
              }}
            >
              <ChevronRightIcon />
            </button>
          ) : null}
        </div>
        <div className="store-tile-meta">
          <div className="store-tile-title-row">
            <strong>{image.platform === "IOS" ? "iOS" : "Android"}</strong>
            {hasHistory ? <span className="screenshot-version-pill">{activeIndex + 1}/{stack.images.length}</span> : null}
          </div>
          <span>{image.localeTag}{props.variant === "icon" ? "" : ` / ${image.imageType}`}</span>
          <div className="store-tile-flags">
            {image.missingSince ? <span>No longer in store</span> : null}
          </div>
        </div>
        {props.variant !== "icon" ? (
          <span className="screenshot-position-badge">
            #{image.position + 1}
            {isReference ? <span> · {props.tileSize === "small" ? "R" : "Reference"}</span> : null}
          </span>
        ) : null}
        {props.variant !== "icon" && (image.commentCount > 0 || image.okCount > 0 || image.needsWorkCount > 0) ? (
          <span className="screenshot-comment-badge" title={`${image.commentCount} comments, ${image.okCount} OK, ${image.needsWorkCount} needs work`}>
            {image.commentCount > 0 ? (
              <span>
                <CommentIcon />
                {image.commentCount}
              </span>
            ) : null}
            {image.okCount > 0 ? (
              <span>
                <CheckIcon />
                {image.okCount}
              </span>
            ) : null}
            {image.needsWorkCount > 0 ? (
              <span className="needs-work-count">
                <AlertIcon />
                {image.needsWorkCount}
              </span>
            ) : null}
          </span>
        ) : null}
        {props.variant !== "icon" ? (
          <span className={`screenshot-source-badge ${image.sourceType.toLowerCase()}`} title={sourceBadgeLabel}>
            <span className="screenshot-source-icon" aria-hidden="true">
              {image.sourceType === "UPLOAD" ? <UploadIcon /> : <StorefrontIcon />}
            </span>
            <span className="screenshot-source-label">{sourceBadgeLabel}</span>
          </span>
        ) : null}
        {canRemoveTile ? (
          <div
            className={`screenshot-delete-actions ${confirmingDelete ? "confirming" : ""}`}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onDragStart={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <button
              className="screenshot-delete-upload-button"
              type="button"
              aria-expanded={confirmingDelete}
              aria-label={removeAriaLabel}
              title={removeActionLabel}
              onClick={() => setDeleteConfirmationId(image.id)}
            >
              <TrashIcon />
              <span className="screenshot-action-label">{removeActionLabel}</span>
            </button>
            {confirmingDelete ? (
              <div className="screenshot-delete-confirmation" role="group" aria-label={removeConfirmationLabel}>
                <button
                  className="screenshot-delete-confirm-button"
                  type="button"
                  onClick={() => void removeScreenshot(image)}
                >
                  Confirm
                </button>
                <button
                  className="screenshot-delete-cancel-button"
                  type="button"
                  onClick={() => setDeleteConfirmationId(null)}
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </article>
    );
  }

  return (
    <section className={`store-image-section ${props.tileSize === "small" ? "compact-tiles" : ""}`}>
      <div className="store-image-section-header">
        <div>
          <h3>{props.title}</h3>
          <span>{stacks.length}</span>
        </div>
        {platformHeaderActions()}
      </div>
      {stacks.length || emptyLocaleGroup.length ? (
        showGroupedRows ? (
          <div className="store-screenshot-position-groups" onDragOver={handleGridDragOver} onDrop={handleGridDrop}>
            {displayedGroups.map((group) => {
              const header = groupHeader(group);
              return (
                <div className="store-screenshot-position-group" key={group.key}>
                  <div className="store-screenshot-group-header">
                    <div>
                      <span>{header.label}</span>
                      {header.detail ? <small>{header.detail}</small> : null}
                    </div>
                  </div>
                  {groupWithinRatio ? (
                    <div className="store-screenshot-nested-groups">
                      {group.nestedGroups.map((nestedGroup) => {
                        const nestedHeader = nestedGroupHeader(nestedGroup.key);
                        return (
                          <div className="store-screenshot-nested-group" key={nestedGroup.key}>
                            <div className={`store-screenshot-group-header nested ${groupWithinRatioByLocale ? "locale-row" : ""}`}>
                              <div>
                                <span>{nestedHeader.label}</span>
                                {nestedHeader.detail ? <small>{nestedHeader.detail}</small> : null}
                              </div>
                              {localeRowActions(nestedGroup.key)}
                            </div>
                            {nestedGroup.stacks.length ? (
                              <div className="store-screenshot-position-row">
                                {nestedGroup.stacks.map(renderStackTile)}
                              </div>
                            ) : (
                              <div className="store-screenshot-empty inline">
                                {props.hasActiveFilters ? "No screenshots match these filters." : "No screenshots for this locale yet."}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="store-screenshot-position-row">
                      {group.stacks.map(renderStackTile)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={props.variant === "icon" ? "store-icon-grid" : "store-screenshot-grid"}
            onDragOver={handleGridDragOver}
            onDrop={handleGridDrop}
          >
            {stacks.map(renderStackTile)}
          </div>
        )
      ) : (
        <div className="store-screenshot-empty">
          {props.hasActiveFilters ? "No screenshots match these filters." : "No images for this section."}
        </div>
      )}
    </section>
  );
}

function LoadingButtonContent(props: { label: string }) {
  return (
    <span className="loading-button-content">
      <span className="button-spinner" aria-hidden="true" />
      <span>{props.label}</span>
    </span>
  );
}

function PlatformLocaleActionButton(props: {
  buttonClassName: string;
  children: ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  label: string;
  loading?: boolean;
  loadingLabel?: string;
  locales: string[];
  onSelect: (locale: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLSpanElement | null>(null);
  const trimmedQuery = query.toLowerCase().trim();
  const options = props.locales
    .filter((locale) => {
      if (!trimmedQuery) {
        return true;
      }
      const displayName = languageNameForLocale(locale)?.toLowerCase() ?? "";
      return locale.toLowerCase().includes(trimmedQuery) || displayName.includes(trimmedQuery);
    })
    .map((locale) => ({ locale, displayName: languageNameForLocale(locale) }));
  const disabledReason = props.loading ? undefined : props.disabledReason ?? (props.locales.length === 0 ? "Select a locale first." : undefined);
  const disabled = Boolean(props.loading || props.disabled || props.locales.length === 0);
  const directLocale = props.locales.length === 1 ? props.locales[0] : null;

  function selectLocale(locale: string) {
    props.onSelect(locale);
    setOpen(false);
    setQuery("");
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "Enter" && options[0]) {
      event.preventDefault();
      selectLocale(options[0].locale);
    }
  }

  return (
    <span className="tooltip-shell platform-locale-action" data-tooltip={disabledReason} ref={ref}>
      <button
        className={`${props.buttonClassName} ${props.loading ? "is-loading" : ""}`}
        type="button"
        disabled={disabled}
        aria-label={props.loading ? props.loadingLabel ?? "Loading" : props.label}
        title={props.label}
        onClick={() => {
          if (directLocale) {
            selectLocale(directLocale);
            return;
          }
          setOpen((current) => {
            const next = !current;
            if (next) {
              setQuery("");
            }
            return next;
          });
        }}
      >
        {props.loading ? <LoadingButtonContent label={props.loadingLabel ?? "Loading"} /> : props.children}
      </button>
      {open ? (
        <DropdownPortal
          align="end"
          anchorRef={ref}
          className="platform-locale-action-menu"
          minWidth={250}
          onDismiss={() => setOpen(false)}
          open={open}
        >
          <input
            className="locale-dropdown-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search locale"
          />
          <div className="platform-locale-action-options">
            {options.map((option) => (
              <button type="button" key={option.locale} onClick={() => selectLocale(option.locale)}>
                <span>{option.locale}</span>
                {option.displayName ? <small>{option.displayName}</small> : null}
              </button>
            ))}
            {options.length === 0 ? <div className="menu-empty">No matching locales.</div> : null}
          </div>
        </DropdownPortal>
      ) : null}
    </span>
  );
}

function StoreTileImage(props: {
  aspectRatio: number;
  label: string;
  src: string;
  square: boolean;
  onLoadError?: () => void;
  onLoadDimensions?: (dimensions: ImageDimensions) => void;
}) {
  const [naturalAspectRatio, setNaturalAspectRatio] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setNaturalAspectRatio(null);
    setFailed(false);
  }, [props.src]);

  const style: CSSProperties = {
    aspectRatio: props.square ? 1 : naturalAspectRatio ?? props.aspectRatio
  };

  return (
    <div
      className={`store-tile-image ${failed ? "failed" : ""}`}
      role="img"
      aria-label={props.label}
      style={style}
    >
      <img
        alt={props.label}
        draggable={false}
        referrerPolicy="no-referrer"
        src={props.src}
        onError={() => {
          setNaturalAspectRatio(null);
          setFailed(true);
          props.onLoadError?.();
        }}
        onLoad={(event) => {
          const image = event.currentTarget;
          setFailed(false);
          if (!props.square && image.naturalWidth > 0 && image.naturalHeight > 0) {
            const dimensions = { width: image.naturalWidth, height: image.naturalHeight };
            setNaturalAspectRatio(dimensions.width / dimensions.height);
            props.onLoadDimensions?.(dimensions);
          }
        }}
      />
      {failed ? <span>Image unavailable</span> : null}
    </div>
  );
}

const appIconImageType = "APP_ICON";

interface ScreenshotImageStack {
  id: string;
  images: StoreScreenshot[];
}

interface ReviewerWelcomeScreenshots {
  referenceImage: StoreScreenshot | null;
  reviewImage: StoreScreenshot | null;
  startImage: StoreScreenshot | null;
  reviewLocale: string | null;
}

function readStoreLocalesFromUrl(): string[] {
  const searchParams = new URLSearchParams(window.location.search);
  return readStoreLocalesFromSearchParams(searchParams)
    ?? normalizeReviewLinkLocales(readReviewLinkPayload(searchParams)?.ls)
    ?? ["en-US"];
}

function readReviewLinkConfigFromUrl(): ReviewLinkConfig {
  const searchParams = new URLSearchParams(window.location.search);
  const payload = readReviewLinkPayload(searchParams);
  return {
    locales: readStoreLocalesFromSearchParams(searchParams) ?? normalizeReviewLinkLocales(payload?.ls) ?? ["en-US"],
    cropFocus: normalizeReviewLinkCropFocus(payload?.f),
    message: sanitizeReviewLinkMessage(typeof payload?.m === "string" ? payload.m : ""),
    welcomeGroupKey: normalizeWelcomeGroupKey(payload?.g),
    welcomeLocale: normalizeReviewLinkWelcomeLocale(payload?.wl),
    welcomePosition: normalizeReviewLinkPosition(payload?.p)
  };
}

function readStoreLocalesFromSearchParams(searchParams: URLSearchParams): string[] | null {
  const value = (searchParams.get(storeLocalesSearchParam) ?? searchParams.get(legacyStoreLocalesSearchParam))?.trim();
  if (!value) {
    return null;
  }
  if (value.toUpperCase() === "NONE") {
    return [];
  }
  const values = value.split(",").map((part) => part.trim()).filter(Boolean);
  if (values.some((locale) => locale.toUpperCase() === "ALL")) {
    return ["ALL"];
  }
  const locales = Array.from(new Set(values.map((locale) => normalizeLocaleInput(locale)).filter((locale): locale is string => Boolean(locale))));
  return locales.length ? locales : null;
}

function readReviewLinkPayload(searchParams: URLSearchParams): Partial<ReviewLinkPayload> | null {
  const encodedPayload = searchParams.get(reviewLinkPayloadSearchParam)?.trim();
  if (!encodedPayload) {
    return null;
  }
  try {
    const parsed = JSON.parse(decodeBase64UrlJson(encodedPayload));
    return parsed && typeof parsed === "object" ? parsed as Partial<ReviewLinkPayload> : null;
  } catch {
    return null;
  }
}

function normalizeReviewLinkLocales(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const rawLocales = value.map((locale) => String(locale).trim()).filter(Boolean);
  if (rawLocales.some((locale) => locale.toUpperCase() === "ALL")) {
    return ["ALL"];
  }
  const locales = Array.from(new Set(rawLocales.map((locale) => normalizeLocaleInput(locale)).filter((locale): locale is string => Boolean(locale))));
  return locales.length || rawLocales.length === 0 ? locales : null;
}

function readStoreScreenshotFilterStateFromHistory(appId: string): StoreScreenshotFilterHistoryState | null {
  const rawState = historyStateRecord()[storeScreenshotFilterHistoryStateKey];
  if (!rawState || typeof rawState !== "object" || Array.isArray(rawState)) {
    return null;
  }
  const value = rawState as Record<string, unknown>;
  if (value.appId !== appId) {
    return null;
  }
  const selectedLocales = normalizeReviewLinkLocales(value.selectedLocales);
  if (!selectedLocales) {
    return null;
  }
  return {
    appId,
    selectedLocales,
    platformFilter: normalizeStorePlatformFilter(value.platformFilter),
    commentedOnly: value.commentedOnly === true,
    needsWorkOnly: value.needsWorkOnly === true,
    unresolvedOnly: value.unresolvedOnly === true,
    updatedSince: normalizeDateInputValue(value.updatedSince)
  };
}

function normalizeStorePlatformFilter(value: unknown): StorePlatformFilter {
  return value === "APP_STORE" || value === "PLAY_STORE" ? value : "ALL";
}

function normalizeDateInputValue(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

function normalizeReviewLinkCropFocus(value: unknown): ReviewLinkCropFocus {
  if (value === "top") {
    return 0;
  }
  if (value === "center") {
    return 50;
  }
  if (value === "bottom") {
    return 100;
  }
  const numericValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numericValue)) {
    return defaultReviewLinkCropFocus;
  }
  return Math.max(0, Math.min(100, Math.round(numericValue)));
}

function normalizeReviewLinkPosition(value: unknown): number | null {
  const position = typeof value === "number" ? value : Number.NaN;
  return Number.isInteger(position) && position >= 0 ? position : null;
}

function normalizeReviewLinkWelcomeLocale(value: unknown): string | null {
  return typeof value === "string" ? normalizeLocaleInput(value) : null;
}

function normalizeWelcomeGroupKey(value: unknown): string | null {
  const key = typeof value === "string" ? value.trim() : "";
  return parseWelcomeGroupKey(key) ? key : null;
}

function limitReviewLinkMessageInput(value: string): string {
  return value
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .slice(0, 3)
    .join("\n")
    .slice(0, reviewLinkMessageMaxLength);
}

function reviewLinkMessageInputRows(value: string): number {
  return Math.min(3, Math.max(1, limitReviewLinkMessageInput(value).split("\n").length));
}

function sanitizeReviewLinkMessage(value: string): string {
  return limitReviewLinkMessageInput(value)
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/^\n+|\n+$/g, "")
    .slice(0, reviewLinkMessageMaxLength);
}

function initialReviewLinkLocales(referenceLocales: string[]): string[] {
  const locales = Array.from(new Set(
    referenceLocales
      .map((locale) => normalizeLocaleInput(locale))
      .filter((locale): locale is string => Boolean(locale))
  ));
  return locales.length ? locales : ["ALL"];
}

function initialCreateReviewLinkLocales(screenshots: StoreScreenshot[], referenceLocales: string[]): string[] {
  const baseLocales = referenceLocales.length ? referenceLocales : initialReviewLinkLocales(referenceLocales);
  const welcome = selectReviewerWelcomeScreenshots(screenshots, referenceLocales, baseLocales);
  return welcome.reviewLocale
    ? libraryLocalesForWelcomeLocale(welcome.reviewLocale, referenceLocales)
    : baseLocales;
}

function storeLocaleSelectionForCurrentApp(referenceLocale: string | null, screenshots: StoreScreenshot[]): string[] {
  const screenshotLocales = screenshots
    .filter((screenshot) => screenshot.imageType !== appIconImageType)
    .map((screenshot) => screenshot.localeTag);
  const locales = sortLocalesWithReference(Array.from(new Set(screenshotLocales)), referenceLocale);
  if (locales.length) {
    return locales;
  }
  const normalizedReferenceLocale = referenceLocale ? normalizeLocaleInput(referenceLocale) : null;
  return normalizedReferenceLocale ? [normalizedReferenceLocale] : [];
}

function storeLocalesUrlValue(locales: string[]): string {
  return locales.length === 0 ? "NONE" : locales.includes("ALL") ? "ALL" : locales.join(",");
}

function historyStateRecord(): Record<string, unknown> {
  const state = window.history.state;
  return state && typeof state === "object" && !Array.isArray(state) ? state as Record<string, unknown> : {};
}

function historyStateWithStoreScreenshotFilter(filterState: StoreScreenshotFilterHistoryState): Record<string, unknown> {
  return {
    ...historyStateRecord(),
    [storeScreenshotFilterHistoryStateKey]: filterState
  };
}

function writeStoreLocalesToUrlSearchParams(url: URL, locales: string[]) {
  url.searchParams.set(storeLocalesSearchParam, storeLocalesUrlValue(locales));
  url.searchParams.delete(legacyStoreLocalesSearchParam);
}

function replaceStoreScreenshotFilterStateInHistory(filterState: StoreScreenshotFilterHistoryState) {
  window.history.replaceState(
    historyStateWithStoreScreenshotFilter(filterState),
    "",
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

function pushStoreScreenshotFilterStateToHistory(filterState: StoreScreenshotFilterHistoryState) {
  const url = new URL(window.location.href);
  writeStoreLocalesToUrlSearchParams(url, filterState.selectedLocales);
  window.history.pushState(
    historyStateWithStoreScreenshotFilter(filterState),
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
}

function writeStoreLocalesToUrl(locales: string[]) {
  const value = storeLocalesUrlValue(locales);
  const url = new URL(window.location.href);
  const hasExplicitLocaleParam = url.searchParams.has(storeLocalesSearchParam) || url.searchParams.has(legacyStoreLocalesSearchParam);
  const payloadLocales = normalizeReviewLinkLocales(readReviewLinkPayload(url.searchParams)?.ls);
  if (!hasExplicitLocaleParam && payloadLocales && storeLocalesEqual(payloadLocales, locales)) {
    return;
  }
  if (url.searchParams.get(storeLocalesSearchParam) === value && !url.searchParams.has(legacyStoreLocalesSearchParam)) {
    return;
  }
  writeStoreLocalesToUrlSearchParams(url, locales);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function storeLocalesEqual(a: string[], b: string[]): boolean {
  return storeLocalesUrlValue(a) === storeLocalesUrlValue(b);
}

function createReviewShareUrl(appId: string, config: ReviewLinkConfig, pathname?: string): string {
  const url = new URL(window.location.href);
  if (pathname !== undefined) {
    url.pathname = pathname;
  }
  url.search = "";
  url.hash = "";
  const payload: ReviewLinkPayload = {
    v: 1,
    ls: config.locales,
    f: config.cropFocus
  };
  if (config.message) {
    payload.m = config.message;
  }
  if (config.welcomePosition !== null) {
    payload.p = config.welcomePosition;
  }
  if (config.welcomeGroupKey) {
    payload.g = config.welcomeGroupKey;
  }
  if (config.welcomeLocale) {
    payload.wl = config.welcomeLocale;
  }
  url.searchParams.set("appId", appId);
  url.searchParams.set(reviewLinkPayloadSearchParam, encodeBase64UrlJson(JSON.stringify(payload)));
  return url.toString();
}

function createReviewShareUrlFromPayload(appId: string, payload: Partial<ReviewLinkPayload>): string {
  return createReviewShareUrl(appId, {
    locales: normalizeReviewLinkLocales(payload.ls) ?? ["en-US"],
    cropFocus: normalizeReviewLinkCropFocus(payload.f),
    message: sanitizeReviewLinkMessage(typeof payload.m === "string" ? payload.m : ""),
    welcomeGroupKey: normalizeWelcomeGroupKey(payload.g),
    welcomeLocale: normalizeReviewLinkWelcomeLocale(payload.wl),
    welcomePosition: normalizeReviewLinkPosition(payload.p)
  }, "/");
}

function createAbsoluteReviewLinkUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

function reviewLinkTitleSummary(link: ReviewLinkSummary): string {
  const title = typeof link.payload.m === "string" ? sanitizeReviewLinkMessage(link.payload.m) : "";
  return title || "Default welcome title";
}

function reviewLinkLocaleSummary(link: ReviewLinkSummary): string {
  const locales = normalizeReviewLinkLocales(link.payload.ls) ?? [];
  if (locales.includes("ALL")) {
    return "All locales";
  }
  return locales.length ? locales.map(welcomeLocaleDisplayName).join(", ") : "No locales";
}

function encodeBase64UrlJson(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function decodeBase64UrlJson(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = `${normalized}${"=".repeat((4 - normalized.length % 4) % 4)}`;
  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textArea);
  if (!copied) {
    throw new Error("Failed to copy review link.");
  }
}

function readStoreTileSizePreference(): StoreTileSize {
  return window.localStorage.getItem(storeTileSizeStorageKey) === "regular" ? "regular" : "small";
}

function readStoreGroupingModePreference(): StoreGroupingMode {
  const value = window.localStorage.getItem(storeGroupingModeStorageKey);
  return value === "position" ? "position" : "locale";
}

function readReviewLatestOnlyPreference(): boolean {
  return window.localStorage.getItem(reviewLatestOnlyStorageKey) !== "false";
}

function readStoredAuthDelivery(): AuthDelivery {
  const value = window.localStorage.getItem(authDeliveryStorageKey);
  return value === "password" ? "password" : "link";
}

function writeStoredAuthDelivery(delivery: AuthDelivery) {
  window.localStorage.setItem(authDeliveryStorageKey, delivery);
}

function normalizeAnonymousReviewerName(value: string): string {
  return value.trim().replace(/\s+/g, " ").slice(0, 80);
}

function readAnonymousReviewerName(): string | null {
  const value = normalizeAnonymousReviewerName(window.localStorage.getItem(anonymousReviewerNameStorageKey) ?? "");
  return value || null;
}

function writeAnonymousReviewerName(value: string) {
  const normalized = normalizeAnonymousReviewerName(value);
  if (normalized) {
    window.localStorage.setItem(anonymousReviewerNameStorageKey, normalized);
  }
}

function clearAnonymousReviewerName() {
  window.localStorage.removeItem(anonymousReviewerNameStorageKey);
}

function commentDraftIdentityScope(session: SessionState, user: UserProfile | null): string | null {
  if (user?.id) {
    return `${user.isAnonymous ? "anon-user" : "user"}:${user.id}`;
  }
  if (session.mode === "firebase" && !session.isAnonymous) {
    return null;
  }
  return `guest:${readOrCreateCommentDraftGuestId()}`;
}

function readOrCreateCommentDraftGuestId(): string {
  const existing = window.localStorage.getItem(commentDraftGuestIdStorageKey)?.trim();
  if (existing) {
    return existing;
  }
  const next = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(commentDraftGuestIdStorageKey, next);
  return next;
}

function readCommentDraftGuestIdentityScope(): string | null {
  const guestId = window.localStorage.getItem(commentDraftGuestIdStorageKey)?.trim();
  return guestId ? `guest:${guestId}` : null;
}

function shouldImportGuestCommentDrafts(identityScope: string): boolean {
  return identityScope.startsWith("user:");
}

function commentDraftKey(identityScope: string, appId: string, screenshotId: string, kind: CommentDraftKind, parentCommentId?: string | null): string {
  return [identityScope, appId, screenshotId, kind, parentCommentId ?? ""].map(encodeURIComponent).join("|");
}

function readCommentDrafts(): Record<string, StoredCommentDraft> {
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(window.localStorage.getItem(commentDraftsStorageKey) ?? "{}");
  } catch {
    window.localStorage.removeItem(commentDraftsStorageKey);
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }

  const now = Date.now();
  let changed = false;
  const entries = Object.entries(parsed as Record<string, unknown>)
    .map(([key, value]) => {
      const draft = normalizeStoredCommentDraft(value);
      if (!draft || draft.expiresAt <= now || !draft.body.trim()) {
        changed = true;
        return null;
      }
      return [key, draft] as const;
    })
    .filter((entry): entry is readonly [string, StoredCommentDraft] => Boolean(entry))
    .sort(([, a], [, b]) => b.updatedAt - a.updatedAt);
  if (entries.length > maxStoredCommentDrafts) {
    entries.length = maxStoredCommentDrafts;
    changed = true;
  }
  const drafts = Object.fromEntries(entries);
  if (changed) {
    writeCommentDrafts(drafts);
  }
  return drafts;
}

function normalizeStoredCommentDraft(value: unknown): StoredCommentDraft | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const draft = value as Record<string, unknown>;
  const kind = draft.kind === "comment" || draft.kind === "needsWork" || draft.kind === "reply" ? draft.kind : null;
  if (
    typeof draft.identityScope !== "string" ||
    typeof draft.appId !== "string" ||
    typeof draft.screenshotId !== "string" ||
    !kind ||
    typeof draft.body !== "string" ||
    typeof draft.updatedAt !== "number" ||
    typeof draft.expiresAt !== "number"
  ) {
    return null;
  }
  return {
    identityScope: draft.identityScope,
    appId: draft.appId,
    screenshotId: draft.screenshotId,
    kind,
    parentCommentId: typeof draft.parentCommentId === "string" ? draft.parentCommentId : null,
    body: draft.body,
    updatedAt: draft.updatedAt,
    expiresAt: draft.expiresAt
  };
}

function writeCommentDrafts(drafts: Record<string, StoredCommentDraft>) {
  window.localStorage.setItem(commentDraftsStorageKey, JSON.stringify(drafts));
}

function upsertCommentDraft(draft: Omit<StoredCommentDraft, "updatedAt" | "expiresAt">) {
  if (!draft.body.trim()) {
    removeCommentDraft(draft.identityScope, draft.appId, draft.screenshotId, draft.kind, draft.parentCommentId);
    return;
  }
  const now = Date.now();
  const drafts = readCommentDrafts();
  drafts[commentDraftKey(draft.identityScope, draft.appId, draft.screenshotId, draft.kind, draft.parentCommentId)] = {
    ...draft,
    updatedAt: now,
    expiresAt: now + commentDraftTtlMs
  };
  writeCommentDrafts(Object.fromEntries(
    Object.entries(drafts)
      .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
      .slice(0, maxStoredCommentDrafts)
  ));
}

function removeCommentDraft(identityScope: string, appId: string, screenshotId: string, kind: CommentDraftKind, parentCommentId?: string | null) {
  const drafts = readCommentDrafts();
  const key = commentDraftKey(identityScope, appId, screenshotId, kind, parentCommentId);
  if (key in drafts) {
    delete drafts[key];
    writeCommentDrafts(drafts);
  }
}

function readCommentDraft(identityScope: string, appId: string, screenshotId: string, kind: CommentDraftKind, parentCommentId?: string | null): StoredCommentDraft | null {
  return readCommentDrafts()[commentDraftKey(identityScope, appId, screenshotId, kind, parentCommentId)] ?? null;
}

function readCommentDraftForIdentityWithGuestFallback(
  identityScope: string,
  appId: string,
  screenshotId: string,
  kind: CommentDraftKind,
  parentCommentId?: string | null
): (StoredCommentDraft & { restoredFromGuest?: boolean }) | null {
  const identityDraft = readCommentDraft(identityScope, appId, screenshotId, kind, parentCommentId);
  if (identityDraft || !shouldImportGuestCommentDrafts(identityScope)) {
    return identityDraft;
  }

  const guestIdentityScope = readCommentDraftGuestIdentityScope();
  if (!guestIdentityScope || guestIdentityScope === identityScope) {
    return null;
  }

  const guestDraft = readCommentDraft(guestIdentityScope, appId, screenshotId, kind, parentCommentId);
  if (!guestDraft) {
    return null;
  }

  upsertCommentDraft({
    identityScope,
    appId,
    screenshotId,
    kind,
    parentCommentId,
    body: guestDraft.body
  });
  removeCommentDraft(guestIdentityScope, appId, screenshotId, kind, parentCommentId);
  return {
    ...guestDraft,
    identityScope,
    restoredFromGuest: true
  };
}

function readReplyCommentDrafts(identityScope: string, appId: string, screenshotId: string): StoredCommentDraft[] {
  return Object.values(readCommentDrafts())
    .filter((draft) => draft.identityScope === identityScope && draft.appId === appId && draft.screenshotId === screenshotId && draft.kind === "reply" && draft.parentCommentId)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

function readReplyCommentDraftsForIdentityWithGuestFallback(
  identityScope: string,
  appId: string,
  screenshotId: string
): Array<StoredCommentDraft & { restoredFromGuest?: boolean }> {
  const identityDrafts: Array<StoredCommentDraft & { restoredFromGuest?: boolean }> = readReplyCommentDrafts(identityScope, appId, screenshotId);
  if (!shouldImportGuestCommentDrafts(identityScope)) {
    return identityDrafts;
  }

  const guestIdentityScope = readCommentDraftGuestIdentityScope();
  if (!guestIdentityScope || guestIdentityScope === identityScope) {
    return identityDrafts;
  }

  const parentCommentIdsWithIdentityDraft = new Set(identityDrafts.map((draft) => draft.parentCommentId).filter(Boolean));
  const importedDrafts = readReplyCommentDrafts(guestIdentityScope, appId, screenshotId)
    .filter((draft) => draft.parentCommentId && !parentCommentIdsWithIdentityDraft.has(draft.parentCommentId))
    .map((draft) => {
      upsertCommentDraft({
        identityScope,
        appId,
        screenshotId,
        kind: "reply",
        parentCommentId: draft.parentCommentId,
        body: draft.body
      });
      removeCommentDraft(guestIdentityScope, appId, screenshotId, "reply", draft.parentCommentId);
      return {
        ...draft,
        identityScope,
        restoredFromGuest: true
      };
    });

  return [...identityDrafts, ...importedDrafts].sort((a, b) => b.updatedAt - a.updatedAt);
}

function writeCommentDraftAuthHandoff(handoff: Omit<StoredCommentDraftAuthHandoff, "createdAt" | "expiresAt">) {
  const now = Date.now();
  window.localStorage.setItem(commentDraftAuthHandoffStorageKey, JSON.stringify({
    ...handoff,
    createdAt: now,
    expiresAt: now + commentDraftTtlMs
  }));
}

function transferCommentDraftAuthHandoff(targetIdentityScope: string, appId: string, screenshotId: string): boolean {
  const handoff = readCommentDraftAuthHandoff();
  if (!handoff || handoff.appId !== appId || handoff.screenshotId !== screenshotId) {
    return false;
  }
  if (handoff.sourceIdentityScope === targetIdentityScope) {
    clearCommentDraftAuthHandoff();
    return false;
  }
  const sourceDraft = readCommentDraft(handoff.sourceIdentityScope, handoff.appId, handoff.screenshotId, handoff.kind, handoff.parentCommentId);
  if (!sourceDraft) {
    clearCommentDraftAuthHandoff();
    return false;
  }
  upsertCommentDraft({
    identityScope: targetIdentityScope,
    appId: handoff.appId,
    screenshotId: handoff.screenshotId,
    kind: handoff.kind,
    parentCommentId: handoff.parentCommentId,
    body: sourceDraft.body
  });
  removeCommentDraft(handoff.sourceIdentityScope, handoff.appId, handoff.screenshotId, handoff.kind, handoff.parentCommentId);
  clearCommentDraftAuthHandoff();
  return true;
}

function readCommentDraftAuthHandoff(): StoredCommentDraftAuthHandoff | null {
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(window.localStorage.getItem(commentDraftAuthHandoffStorageKey) ?? "null");
  } catch {
    clearCommentDraftAuthHandoff();
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }
  const value = parsed as Record<string, unknown>;
  const kind = value.kind === "comment" || value.kind === "needsWork" || value.kind === "reply" ? value.kind : null;
  if (
    typeof value.sourceIdentityScope !== "string" ||
    typeof value.appId !== "string" ||
    typeof value.screenshotId !== "string" ||
    !kind ||
    typeof value.createdAt !== "number" ||
    typeof value.expiresAt !== "number"
  ) {
    clearCommentDraftAuthHandoff();
    return null;
  }
  if (value.expiresAt <= Date.now()) {
    clearCommentDraftAuthHandoff();
    return null;
  }
  return {
    sourceIdentityScope: value.sourceIdentityScope,
    appId: value.appId,
    screenshotId: value.screenshotId,
    kind,
    parentCommentId: typeof value.parentCommentId === "string" ? value.parentCommentId : null,
    createdAt: value.createdAt,
    expiresAt: value.expiresAt
  };
}

function clearCommentDraftAuthHandoff() {
  window.localStorage.removeItem(commentDraftAuthHandoffStorageKey);
}

function reviewerIntroCollapsedStorageKey(appId: string): string {
  return `${reviewerIntroCollapsedStorageKeyPrefix}.${appId}`;
}

function readReviewerIntroCollapsedPreference(appId: string): boolean {
  return window.localStorage.getItem(reviewerIntroCollapsedStorageKey(appId)) === "true";
}

function writeReviewerIntroCollapsedPreference(appId: string, collapsed: boolean) {
  window.localStorage.setItem(reviewerIntroCollapsedStorageKey(appId), collapsed ? "true" : "false");
}

const reusableSignedImageUrlMinimumTtlMs = 5 * 60 * 1000;

function preserveReusableScreenshotImageUrls(previous: StoreScreenshot[], next: StoreScreenshot[]): StoreScreenshot[] {
  const reusableUrls = new Map<string, string>();
  previous.forEach((screenshot) => {
    if (screenshot.sourceType === "UPLOAD" && screenshot.storageObjectName && canReuseScreenshotImageUrl(screenshot.imageUrl)) {
      reusableUrls.set(screenshot.storageObjectName, screenshot.imageUrl);
    }
  });
  if (reusableUrls.size === 0) {
    return next;
  }

  let changed = false;
  const merged = next.map((screenshot) => {
    if (screenshot.sourceType !== "UPLOAD" || !screenshot.storageObjectName) {
      return screenshot;
    }
    const reusableUrl = reusableUrls.get(screenshot.storageObjectName);
    if (!reusableUrl || reusableUrl === screenshot.imageUrl) {
      return screenshot;
    }
    changed = true;
    return { ...screenshot, imageUrl: reusableUrl };
  });
  return changed ? merged : next;
}

function canReuseScreenshotImageUrl(value: string): boolean {
  const expiresAt = signedScreenshotImageUrlExpiresAt(value);
  return expiresAt === null || expiresAt - Date.now() > reusableSignedImageUrlMinimumTtlMs;
}

function hasStaleUploadedScreenshotImageUrl(screenshots: StoreScreenshot[]): boolean {
  return screenshots.some((screenshot) =>
    screenshot.sourceType === "UPLOAD" &&
    Boolean(screenshot.storageObjectName) &&
    !canReuseScreenshotImageUrl(screenshot.imageUrl)
  );
}

function signedScreenshotImageUrlExpiresAt(value: string): number | null {
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }

  const signedAt = url.searchParams.get("X-Amz-Date");
  const expiresSeconds = Number(url.searchParams.get("X-Amz-Expires"));
  if (!signedAt || !Number.isFinite(expiresSeconds) || expiresSeconds <= 0) {
    return null;
  }

  const match = signedAt.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (!match) {
    return null;
  }
  const signedAtMs = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6])
  );
  return signedAtMs + expiresSeconds * 1000;
}

function addSelectedLocale(selectedLocales: string[], locale: string): string[] {
  if (selectedLocales.includes("ALL")) {
    return [locale];
  }
  return selectedLocales.includes(locale) ? selectedLocales : [...selectedLocales, locale];
}

function toggleSelectedLocale(selectedLocales: string[], locale: string): string[] {
  if (selectedLocales.includes("ALL")) {
    return [locale];
  }
  if (selectedLocales.includes(locale)) {
    return selectedLocales.filter((selected) => selected !== locale);
  }
  return [...selectedLocales, locale];
}

function sortLocalesWithReference(locales: string[], referenceLocale: string | null): string[] {
  return [...locales].sort((a, b) => {
    const aReference = Boolean(referenceLocale && a === referenceLocale);
    const bReference = Boolean(referenceLocale && b === referenceLocale);
    if (aReference !== bReference) {
      return aReference ? -1 : 1;
    }
    return a.localeCompare(b);
  });
}

function countCommentType(comments: StoreScreenshotComment[], commentType: StoreScreenshotCommentType, includeResolved = false): number {
  return comments.reduce(
    (total, comment) =>
      total +
      (comment.commentType === commentType && (includeResolved || !comment.resolvedAt) ? 1 : 0) +
      countCommentType(comment.replies ?? [], commentType, includeResolved),
    0
  );
}

function countTopLevelCommentType(comments: StoreScreenshotComment[], commentType: StoreScreenshotCommentType, includeResolved = false): number {
  return comments.reduce(
    (total, comment) => total + (comment.commentType === commentType && (includeResolved || !comment.resolvedAt) ? 1 : 0),
    0
  );
}

function countVisibleCommentEntries(comments: StoreScreenshotComment[]): number {
  return countCommentType(comments, "COMMENT") + countCommentType(comments, "OK") + countCommentType(comments, "NEEDS_WORK");
}

function storeScreenshotCommentCountPatch(comments: StoreScreenshotComment[]): Pick<
  StoreScreenshot,
  "commentCount" |
  "okCount" |
  "needsWorkCount" |
  "unresolvedCommentCount" |
  "unresolvedOkCount" |
  "unresolvedNeedsWorkCount" |
  "lastCommentActivityAt"
> {
  const commentCount = countTopLevelCommentType(comments, "COMMENT");
  const okCount = countTopLevelCommentType(comments, "OK", true);
  const needsWorkCount = countTopLevelCommentType(comments, "NEEDS_WORK");
  return {
    commentCount,
    okCount,
    needsWorkCount,
    unresolvedCommentCount: commentCount,
    unresolvedOkCount: countTopLevelCommentType(comments, "OK"),
    unresolvedNeedsWorkCount: needsWorkCount,
    lastCommentActivityAt: latestCommentActivityAt(comments)
  };
}

function latestCommentActivityAt(comments: StoreScreenshotComment[]): string | null {
  let latestValue: string | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;

  function visit(entries: StoreScreenshotComment[]) {
    for (const comment of entries) {
      const time = Date.parse(comment.updatedAt);
      if (!Number.isNaN(time) && time > latestTime) {
        latestTime = time;
        latestValue = comment.updatedAt;
      }
      visit(comment.replies ?? []);
    }
  }

  visit(comments);
  return latestValue;
}

function formatCommentBody(comment: StoreScreenshotComment): string {
  if (comment.commentType === "OK") {
    return "OK";
  }
  if (comment.commentType === "NEEDS_WORK") {
    return comment.body || "Needs work";
  }
  return comment.body;
}

function isInteractiveElement(element: Element | null): boolean {
  if (!element || element === document.body) {
    return false;
  }
  return Boolean(element.closest("button, input, textarea, select, [contenteditable='true'], a[href]"));
}

function countImageStacks(images: StoreScreenshot[]): number {
  return groupImageStacks(images).length;
}

function activeStackImages(images: StoreScreenshot[]): StoreScreenshot[] {
  return groupImageStacks(images)
    .map((stack) => stack.images[stack.images.length - 1] ?? stack.images[0])
    .filter((image): image is StoreScreenshot => Boolean(image))
    .sort(compareScreenshotTiles);
}

function groupScreenshotStacks(
  stacks: ScreenshotImageStack[],
  groupingMode: StoreGroupingMode,
  referenceLocales: string[],
  activeImage: (stack: ScreenshotImageStack) => StoreScreenshot
): { key: string; stacks: ScreenshotImageStack[] }[] {
  return Array.from(stacks.reduce((groups, stack) => {
    const image = activeImage(stack);
    const groupKey = groupingMode === "position" ? String(image.position) : image.localeTag;
    const current = groups.get(groupKey) ?? [];
    groups.set(groupKey, [...current, stack]);
    return groups;
  }, new Map<string, ScreenshotImageStack[]>()))
    .sort(([aKey], [bKey]) => compareScreenshotGroupKeys(aKey, bKey, groupingMode, referenceLocales))
    .map(([key, groupStacks]) => ({
      key,
      stacks: [...groupStacks].sort((a, b) => compareScreenshotTilesWithReference(activeImage(a), activeImage(b), referenceLocales))
    }));
}

function groupImageStacks(images: StoreScreenshot[]): ScreenshotImageStack[] {
  const stacks = new Map<string, StoreScreenshot[]>();
  for (const image of images) {
    const stackId = image.versionStackId || image.id;
    stacks.set(stackId, [...(stacks.get(stackId) ?? []), image]);
  }

  return Array.from(stacks.entries())
    .map(([id, stackImages]) => ({
      id,
      images: stackImages.sort(compareScreenshotVersions)
    }))
    .sort((a, b) => compareScreenshotTiles(a.images[0], b.images[0]));
}

function findReviewedScreenshots(
  opened: StoreScreenshot,
  screenshots: StoreScreenshot[],
  dimensionsByImageId: ImageDimensionsById = {}
): StoreScreenshot[] {
  const reviewGroupKey = screenshotReviewLayoutGroupKey(opened, dimensionsByImageId);
  return screenshots
    .filter((image) =>
      image.imageType !== appIconImageType &&
      image.platform === opened.platform &&
      image.localeTag === opened.localeTag &&
      image.imageType === opened.imageType &&
      screenshotReviewLayoutGroupKey(image, dimensionsByImageId) === reviewGroupKey
    )
    .sort(compareScreenshotTiles);
}

function findEquivalentReviewScreenshots(
  opened: StoreScreenshot,
  screenshots: StoreScreenshot[],
  dimensionsByImageId: ImageDimensionsById = {}
): StoreScreenshot[] {
  const reviewGroupKey = screenshotReviewLayoutGroupKey(opened, dimensionsByImageId);
  return latestScreenshotVersions(screenshots.filter((image) =>
    image.id !== opened.id &&
    image.imageType !== appIconImageType &&
    image.platform === opened.platform &&
    image.imageType === opened.imageType &&
    image.position === opened.position &&
    screenshotReviewLayoutGroupKey(image, dimensionsByImageId) === reviewGroupKey
  ))
    .sort(compareScreenshotTiles);
}

function firstScreenshotForLocales(screenshots: StoreScreenshot[], locales: string[]): StoreScreenshot | null {
  for (const locale of locales) {
    const screenshot = screenshots.find((image) => image.localeTag === locale);
    if (screenshot) {
      return screenshot;
    }
  }
  return null;
}

function selectReviewerWelcomeScreenshots(
  screenshots: StoreScreenshot[],
  referenceLocales: string[],
  selectedLocales: string[],
  options: { groupKey?: string | null; locale?: string | null; position?: number | null } = {}
): ReviewerWelcomeScreenshots {
  const referenceLocaleSet = new Set(referenceLocales);
  const groupScreenshots = welcomeScreenshotsForGroup(latestScreenshotVersions(screenshots), options.groupKey ?? null);
  const latestScreenshots = welcomeScreenshotsForPosition(groupScreenshots, options.position ?? null);
  const referenceScreenshots = latestScreenshots.filter((image) => referenceLocaleSet.has(image.localeTag));
  const reviewScreenshots = latestScreenshots.filter((image) => !referenceLocaleSet.has(image.localeTag));
  const preferredLocales = selectedLocales.includes("ALL")
    ? []
    : selectedLocales.filter((locale) => locale !== "ALL" && !referenceLocaleSet.has(locale));
  const selectedReviewImage = options.locale
    ? firstScreenshotForLocales(latestScreenshots, [options.locale])
    : firstScreenshotForLocales(reviewScreenshots, preferredLocales);
  const firstAvailableReviewImage = reviewScreenshots[0] ?? null;
  const secondReferenceImage = referenceLocales[1]
    ? firstScreenshotForLocales(referenceScreenshots, [referenceLocales[1]])
    : null;
  const reviewImage = selectedReviewImage ?? firstAvailableReviewImage ?? secondReferenceImage;
  const startImage = reviewImage;
  const referenceImage = reviewImage
    ? findReferenceScreenshotForWelcome(reviewImage, latestScreenshots, referenceLocales) ?? firstScreenshotForLocales(referenceScreenshots, referenceLocales)
    : firstScreenshotForLocales(referenceScreenshots, referenceLocales);

  return {
    referenceImage,
    reviewImage,
    startImage,
    reviewLocale: reviewImage?.localeTag ?? null
  };
}

function findReferenceScreenshotForWelcome(reviewed: StoreScreenshot, screenshots: StoreScreenshot[], referenceLocales: string[]): StoreScreenshot | null {
  return findComparisonScreenshots(reviewed, screenshots, referenceLocales, [])[0] ?? null;
}

function welcomeScreenshotsForPosition(screenshots: StoreScreenshot[], position: number | null): StoreScreenshot[] {
  if (position === null) {
    return screenshots;
  }
  const positionedScreenshots = screenshots.filter((image) => image.position === position);
  return positionedScreenshots.length ? positionedScreenshots : screenshots;
}

function welcomeScreenshotsForGroup(screenshots: StoreScreenshot[], groupKey: string | null): StoreScreenshot[] {
  if (!groupKey) {
    return screenshots;
  }
  const groupedScreenshots = screenshots.filter((image) => welcomeGroupKeyForScreenshot(image) === groupKey);
  return groupedScreenshots.length ? groupedScreenshots : screenshots;
}

function buildWelcomeGroupOptions(screenshots: StoreScreenshot[]): Array<{ label: string; value: string }> {
  const latestScreenshots = latestScreenshotVersions(screenshots);
  return Array.from(latestScreenshots.reduce((groups, image) => {
    const groupKey = welcomeGroupKeyForScreenshot(image);
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), image]);
    return groups;
  }, new Map<string, StoreScreenshot[]>()))
    .sort(([aKey], [bKey]) => compareWelcomeGroupKeys(aKey, bKey))
    .map(([key, groupImages]) => ({
      label: welcomeGroupLabel(key, groupImages),
      value: key
    }));
}

function buildWelcomePositionOptions(screenshots: StoreScreenshot[]): Array<{ label: string; value: string }> {
  return Array.from(new Set(latestScreenshotVersions(screenshots).map((image) => image.position)))
    .sort((a, b) => a - b)
    .map((position) => ({ label: `Screenshot #${position + 1}`, value: String(position) }));
}

function welcomeLocaleOptionsForScreenshots(
  screenshots: StoreScreenshot[],
  referenceLocales: string[],
  position: number | null,
  groupKey: string | null = null
): string[] {
  const groupedScreenshots = welcomeScreenshotsForGroup(latestScreenshotVersions(screenshots), groupKey);
  const positionedScreenshots = welcomeScreenshotsForPosition(groupedScreenshots, position);
  const locales = Array.from(new Set(positionedScreenshots.map((image) => image.localeTag)));
  return sortLocalesWithReferenceLocales(locales, referenceLocales);
}

function sortLocalesWithReferenceLocales(locales: string[], referenceLocales: string[]): string[] {
  const localeSet = new Set(locales);
  const orderedReferenceLocales = referenceLocales.filter((locale) => localeSet.has(locale));
  const otherLocales = locales
    .filter((locale) => !referenceLocales.includes(locale))
    .sort((a, b) => a.localeCompare(b));
  return [...orderedReferenceLocales, ...otherLocales];
}

function libraryLocalesForWelcomeLocale(locale: string, referenceLocales: string[]): string[] {
  const firstReferenceLocale = referenceLocales[0] ?? null;
  if (!firstReferenceLocale) {
    return [locale];
  }
  if (locale === firstReferenceLocale) {
    const secondReferenceLocale = referenceLocales.find((candidate) => candidate !== firstReferenceLocale);
    return secondReferenceLocale ? [firstReferenceLocale, secondReferenceLocale] : [firstReferenceLocale];
  }
  return Array.from(new Set([firstReferenceLocale, locale]));
}

function latestScreenshotVersions(images: StoreScreenshot[]): StoreScreenshot[] {
  const latestByStack = new Map<string, StoreScreenshot>();
  for (const image of images) {
    const stackId = screenshotVersionStackId(image);
    const current = latestByStack.get(stackId);
    if (!current || compareScreenshotVersions(current, image) < 0) {
      latestByStack.set(stackId, image);
    }
  }
  return Array.from(latestByStack.values()).sort(compareScreenshotTiles);
}

function latestScreenshotVersionForStack(seed: StoreScreenshot, images: StoreScreenshot[]): StoreScreenshot {
  const stackId = screenshotVersionStackId(seed);
  return images.reduce<StoreScreenshot | null>((latest, image) => {
    if (screenshotVersionStackId(image) !== stackId) {
      return latest;
    }
    return !latest || compareScreenshotVersions(latest, image) < 0 ? image : latest;
  }, null) ?? seed;
}

function screenshotVersionStackId(image: StoreScreenshot): string {
  return image.versionStackId || image.id;
}

function findComparisonScreenshots(
  reviewed: StoreScreenshot,
  screenshots: StoreScreenshot[],
  referenceLocales: string[],
  userLanguages: UserLanguage[],
  dimensionsByImageId: ImageDimensionsById = {}
): StoreScreenshot[] {
  const comparisonLocales = new Set([
    ...referenceLocales,
    ...userLanguages.map((language) => language.languageTag)
  ]);
  if (comparisonLocales.size === 0) {
    return [];
  }

  const reviewGroupKey = screenshotReviewLayoutGroupKey(reviewed, dimensionsByImageId);
  return screenshots
    .filter((image) =>
      image.id !== reviewed.id &&
      image.platform === reviewed.platform &&
      image.imageType === reviewed.imageType &&
      image.position === reviewed.position &&
      image.imageType !== appIconImageType &&
      comparisonLocales.has(image.localeTag) &&
      screenshotReviewLayoutGroupKey(image, dimensionsByImageId) === reviewGroupKey
    )
    .sort((a, b) => {
      const aReference = referenceLocales.includes(a.localeTag);
      const bReference = referenceLocales.includes(b.localeTag);
      if (aReference !== bReference) {
        return aReference ? -1 : 1;
      }
      return a.localeTag.localeCompare(b.localeTag) || compareScreenshotVersions(a, b);
    });
}

function getImageAspectRatio(image: StoreScreenshot): number {
  const dimensions = getImageDimensions(image);
  if (!dimensions || dimensions.height <= 0) {
    return 9 / 16;
  }
  return dimensions.width / dimensions.height;
}

function getScreenshotReviewLayoutAspectRatio(
  seed: StoreScreenshot,
  screenshots: StoreScreenshot[],
  dimensionsByImageId: ImageDimensionsById = {}
): number {
  const reviewGroupKey = screenshotReviewLayoutGroupKey(seed, dimensionsByImageId);
  const groupRatios = screenshots
    .filter((image) =>
      image.imageType !== appIconImageType &&
      image.platform === seed.platform &&
      image.imageType === seed.imageType &&
      screenshotReviewLayoutGroupKey(image, dimensionsByImageId) === reviewGroupKey
    )
    .map((image) => getReviewLayoutAspectRatioCandidate(image, dimensionsByImageId[image.id]))
    .filter((ratio) => Number.isFinite(ratio) && ratio > 0)
    .sort((a, b) => a - b);

  if (!groupRatios.length) {
    return getReviewLayoutAspectRatioCandidate(seed, dimensionsByImageId[seed.id]);
  }

  return groupRatios[Math.floor(groupRatios.length / 2)];
}

function screenshotReviewLayoutGroupKey(image: StoreScreenshot, dimensionsByImageId: ImageDimensionsById = {}): string {
  return screenshotAspectRatioGroupKeyFromRatio(getReviewLayoutAspectRatioCandidate(image, dimensionsByImageId[image.id]));
}

function getReviewLayoutAspectRatioCandidate(image: StoreScreenshot, measuredDimensions?: ImageDimensions | null): number {
  if (measuredDimensions && measuredDimensions.width > 0 && measuredDimensions.height > 0) {
    return measuredDimensions.width / measuredDimensions.height;
  }
  const dimensions = getImageDimensions(image);
  if (dimensions && canUseStoredReviewDimensions(image, dimensions)) {
    return dimensions.width / dimensions.height;
  }
  return getReviewImageAspectRatioFallback(image);
}

function isLandscapeReviewImage(image: StoreScreenshot, measuredDimensions?: ImageDimensions | null): boolean {
  if (measuredDimensions && measuredDimensions.width > 0 && measuredDimensions.height > 0) {
    return measuredDimensions.width / measuredDimensions.height > 1;
  }
  return getReviewLayoutAspectRatioCandidate(image) > 1;
}

function getWelcomePreviewAspectRatioCandidate(image: StoreScreenshot): number {
  const screenshotAspectRatio = getReviewLayoutAspectRatioCandidate(image);
  if (screenshotAspectRatio < 0.95) {
    return Math.min(0.84, Math.max(0.68, screenshotAspectRatio * 1.34));
  }
  return screenshotAspectRatio;
}

function hasWelcomePreviewVerticalCrop(image: StoreScreenshot, frameAspectRatio: number): boolean {
  const dimensions = getDisplayImageDimensions(image);
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    return true;
  }
  return dimensions.width / dimensions.height < frameAspectRatio - 0.01;
}

function getReviewImageAspectRatioFallback(image: StoreScreenshot): number {
  const imageType = image.imageType.toUpperCase();
  if (imageType === "PHONE" || imageType === "IPHONE") {
    return 9 / 16;
  }
  if (imageType === "IPAD") {
    return 3 / 4;
  }
  if (imageType === "LANDSCAPE") {
    return 16 / 9;
  }
  return getImageAspectRatio(image);
}

function canUseStoredReviewDimensions(image: StoreScreenshot, dimensions: ImageDimensions): boolean {
  if (dimensions.width <= 0 || dimensions.height <= 0) {
    return false;
  }
  return !hasUnreliableGooglePlayPhoneDimensions(image, dimensions);
}

function hasUnreliableGooglePlayPhoneDimensions(image: StoreScreenshot, dimensions: ImageDimensions): boolean {
  const imageType = image.imageType.toUpperCase();
  return image.platform === "ANDROID" &&
    image.sourceType === "STORE" &&
    imageType === "PHONE" &&
    image.imageUrl.startsWith("https://play-lh.googleusercontent.com/") &&
    dimensions.width > dimensions.height;
}

function screenshotAspectRatioGroupKey(image: StoreScreenshot): string {
  const aspectRatio = Math.max(getReviewLayoutAspectRatioCandidate(image), 0.01);
  return screenshotAspectRatioGroupKeyFromRatio(aspectRatio);
}

function welcomeGroupKeyForScreenshot(image: StoreScreenshot): string {
  return `${image.platform}:${screenshotAspectRatioGroupKey(image)}`;
}

function parseWelcomeGroupKey(key: string): { platform: Platform; aspectRatioGroupKey: string } | null {
  const [platform, orientation, ratioValue] = key.split(":");
  if ((platform !== "IOS" && platform !== "ANDROID") || !orientation || !ratioValue) {
    return null;
  }
  const ratio = Number(ratioValue);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return null;
  }
  const aspectRatioGroupKey = `${orientation}:${ratioValue}`;
  if (!["portrait", "landscape", "square"].includes(orientation)) {
    return null;
  }
  return { platform, aspectRatioGroupKey };
}

function welcomeGroupLabel(key: string, images: StoreScreenshot[] = []): string {
  const parsed = parseWelcomeGroupKey(key);
  if (!parsed) {
    return "Screenshots";
  }
  const resolution = formatResolutionSummary(images);
  return [
    parsed.platform === "IOS" ? "App Store" : "Google Play",
    aspectRatioGroupLabel(parsed.aspectRatioGroupKey),
    resolution
  ].filter(Boolean).join(" / ");
}

function compareWelcomeGroupKeys(a: string, b: string): number {
  const aGroup = parseWelcomeGroupKey(a);
  const bGroup = parseWelcomeGroupKey(b);
  const aPlatformOrder = aGroup?.platform === "IOS" ? 0 : aGroup?.platform === "ANDROID" ? 1 : 2;
  const bPlatformOrder = bGroup?.platform === "IOS" ? 0 : bGroup?.platform === "ANDROID" ? 1 : 2;
  return aPlatformOrder - bPlatformOrder ||
    compareAspectRatioGroupKeys(aGroup?.aspectRatioGroupKey ?? "", bGroup?.aspectRatioGroupKey ?? "") ||
    a.localeCompare(b);
}

function screenshotAspectRatioGroupKeyFromRatio(aspectRatio: number): string {
  const normalizedAspectRatio = Math.max(aspectRatio, 0.01);
  const orientation = normalizedAspectRatio > 1.05 ? "landscape" : normalizedAspectRatio < 0.95 ? "portrait" : "square";
  if (orientation === "square") {
    return "square:1.00";
  }
  const longToShortRatio = orientation === "landscape" ? normalizedAspectRatio : 1 / normalizedAspectRatio;
  const ratioBucket = Math.max(1, Math.round(longToShortRatio * 20) / 20);
  return `${orientation}:${ratioBucket.toFixed(2)}`;
}

function aspectRatioGroupLabel(key: string): string {
  const [orientation, ratioValue] = key.split(":");
  const ratio = Number(ratioValue);
  if (orientation === "square" || !Number.isFinite(ratio)) {
    return "Square 1:1";
  }
  const formattedRatio = formatAspectRatioNumber(ratio);
  return orientation === "landscape"
    ? `Landscape ${formattedRatio}:1`
    : `Portrait 1:${formattedRatio}`;
}

function compareAspectRatioGroupKeys(a: string, b: string): number {
  const aInfo = parseAspectRatioGroupKey(a);
  const bInfo = parseAspectRatioGroupKey(b);
  return aInfo.order - bInfo.order ||
    aInfo.ratio - bInfo.ratio ||
    a.localeCompare(b);
}

function parseAspectRatioGroupKey(key: string): { order: number; ratio: number } {
  const [orientation, ratioValue] = key.split(":");
  const order = orientation === "portrait" ? 0 : orientation === "landscape" ? 1 : 2;
  const ratio = Number(ratioValue);
  return { order, ratio: Number.isFinite(ratio) ? ratio : 1 };
}

function formatAspectRatioNumber(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.01) {
    return String(Math.round(value));
  }
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function getImageDimensions(image: StoreScreenshot): ImageDimensions | null {
  if (image.width && image.height) {
    return { width: image.width, height: image.height };
  }
  const appleDimensions = image.imageUrl.match(/\/([0-9]+)x([0-9]+)[A-Za-z0-9-]*\.(jpg|jpeg|png|webp)/);
  if (appleDimensions) {
    return { width: Number(appleDimensions[1]), height: Number(appleDimensions[2]) };
  }
  const googleDimensions = image.imageUrl.match(/=w([0-9]+)-h([0-9]+)/);
  if (googleDimensions) {
    return { width: Number(googleDimensions[1]), height: Number(googleDimensions[2]) };
  }
  return null;
}

function formatResolutionSummary(
  images: StoreScreenshot[],
  resolveDimensions: (image: StoreScreenshot) => ImageDimensions | null = getDisplayImageDimensions
): string | undefined {
  const resolutions = Array.from(new Set(images.map((image) => formatImageResolution(image, resolveDimensions)).filter((value): value is string => Boolean(value))));
  if (resolutions.length === 0) {
    return undefined;
  }
  if (resolutions.length === 1) {
    return resolutions[0];
  }
  return `${resolutions[0]} +${resolutions.length - 1}`;
}

function formatImageResolution(
  image: StoreScreenshot,
  resolveDimensions: (image: StoreScreenshot) => ImageDimensions | null = getDisplayImageDimensions
): string | null {
  const dimensions = resolveDimensions(image);
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    return null;
  }
  return `${dimensions.width}x${dimensions.height}`;
}

function storeRefreshFeedbackMessage(summary: StoreScreenshotRefreshSummary): string | null {
  if (summary.foundScreenshotCount > 0 && summary.importedScreenshotCount > 0) {
    return null;
  }

  const platformLabel = formatStoreRefreshPlatforms(summary.refreshedPlatforms);
  const localeLabel = formatStoreRefreshLocales(summary.requestedLocales);

  if (summary.foundScreenshotCount === 0) {
    return `No screenshots found for ${localeLabel} in ${platformLabel}.`;
  }

  if (summary.suppressedScreenshotCount > 0 && summary.importedScreenshotCount === 0) {
    return `All screenshots found for ${localeLabel} in ${platformLabel} were already removed from this workspace.`;
  }

  return null;
}

function formatStoreRefreshPlatforms(platforms: Platform[]): string {
  const labels = platforms.map((platform) => platform === "IOS" ? "App Store" : "Google Play");
  if (labels.length === 0) {
    return "the stores";
  }
  if (labels.length === 1) {
    return labels[0];
  }
  return labels.join(" and ");
}

function formatStoreRefreshLocales(locales: string[]): string {
  if (locales.length === 0) {
    return "the selected locales";
  }
  if (locales.length === 1) {
    return locales[0];
  }
  if (locales.length === 2) {
    return `${locales[0]} and ${locales[1]}`;
  }
  return `${locales[0]} and ${locales.length - 1} other locales`;
}

function matchesPlatformFilter(image: StoreScreenshot, filter: StorePlatformFilter): boolean {
  if (filter === "APP_STORE") {
    return image.platform === "IOS";
  }
  if (filter === "PLAY_STORE") {
    return image.platform === "ANDROID";
  }
  return true;
}

function hasCommentActivity(image: StoreScreenshot): boolean {
  return Boolean(image.lastCommentActivityAt) ||
    image.commentCount > 0 ||
    image.okCount > 0 ||
    image.needsWorkCount > 0;
}

function hasUnresolvedCommentActivity(image: StoreScreenshot): boolean {
  return (image.unresolvedCommentCount ?? image.commentCount) > 0 ||
    (image.unresolvedOkCount ?? 0) > 0 ||
    (image.unresolvedNeedsWorkCount ?? image.needsWorkCount) > 0;
}

function hasCommentActivitySince(image: StoreScreenshot, timestamp: number): boolean {
  if (!image.lastCommentActivityAt) {
    return false;
  }
  const activityTime = Date.parse(image.lastCommentActivityAt);
  return !Number.isNaN(activityTime) && activityTime >= timestamp;
}

function localDateStartTimestamp(value: string): number {
  const date = new Date(`${value}T00:00:00`);
  return date.getTime();
}

function tileImageUrl(image: StoreScreenshot, tileSize: StoreTileSize): string {
  if (shouldUseSizedGooglePlayScreenshotUrl(image)) {
    const width = tileSize === "small" ? googlePlaySmallTileWidth : googlePlayRegularTileWidth;
    return googlePlaySizedImageUrl(image.imageUrl, width);
  }
  const sourceUrl = displayImageUrl(image);
  const dimensions = getDisplayImageDimensions(image);
  if (!dimensions) {
    return sourceUrl;
  }

  const maxLongEdge = tileSize === "small" ? 640 : 900;
  const longerEdge = Math.max(dimensions.width, dimensions.height);
  if (longerEdge <= maxLongEdge) {
    return sourceUrl;
  }

  const scale = maxLongEdge / longerEdge;
  const width = Math.max(1, Math.round(dimensions.width * scale));
  const height = Math.max(1, Math.round(dimensions.height * scale));

  if (sourceUrl.includes("mzstatic.com/image/thumb/")) {
    return resizedAppleImageUrl(sourceUrl, width, height);
  }
  return sourceUrl;
}

function welcomePreviewImageUrl(image: StoreScreenshot): string {
  return displayImageUrl(image);
}

function getDisplayImageDimensions(image: StoreScreenshot): ImageDimensions | null {
  const dimensions = getImageDimensions(image);
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) {
    return null;
  }
  if (canUseStoredReviewDimensions(image, dimensions)) {
    return dimensions;
  }
  const fallbackRatio = getReviewImageAspectRatioFallback(image);
  if (!Number.isFinite(fallbackRatio) || fallbackRatio <= 0) {
    return dimensions;
  }
  const longEdge = Math.max(dimensions.width, dimensions.height);
  if (fallbackRatio >= 1) {
    return { width: longEdge, height: Math.max(1, Math.round(longEdge / fallbackRatio)) };
  }
  return { width: Math.max(1, Math.round(longEdge * fallbackRatio)), height: longEdge };
}

function displayImageUrl(image: StoreScreenshot): string {
  if (shouldUseSizedGooglePlayScreenshotUrl(image)) {
    return googlePlaySizedImageUrl(image.imageUrl, googlePlayDisplayWidth);
  }
  return image.imageUrl;
}

function shouldUseSizedGooglePlayScreenshotUrl(image: StoreScreenshot): boolean {
  return image.sourceType === "STORE" &&
    image.platform === "ANDROID" &&
    image.imageType.toUpperCase() !== appIconImageType &&
    image.imageUrl.startsWith("https://play-lh.googleusercontent.com/");
}

function googlePlaySizedImageUrl(url: string, width: number): string {
  const baseUrl = googlePlayBaseImageUrl(url);
  return `${baseUrl}=w${width}`;
}

function googlePlayBaseImageUrl(url: string): string {
  const sizingIndex = url.indexOf("=");
  if (sizingIndex < 0) {
    return url;
  }
  return url.slice(0, sizingIndex);
}

function resizedAppleImageUrl(url: string, width: number, height: number): string {
  return url.replace(
    /\/([0-9]+)x([0-9]+)([A-Za-z0-9-]*)\.(jpg|jpeg|png|webp)(\?.*)?$/i,
    (_match, _originalWidth, _originalHeight, modifiers: string, extension: string, query: string | undefined) =>
      `/${width}x${height}${modifiers}.${extension}${query ?? ""}`
  );
}

function compareScreenshotVersions(a: StoreScreenshot, b: StoreScreenshot): number {
  return a.versionPosition - b.versionPosition ||
    Date.parse(a.firstRetrievedAt) - Date.parse(b.firstRetrievedAt) ||
    Date.parse(a.createdAt) - Date.parse(b.createdAt) ||
    a.id.localeCompare(b.id);
}

function compareScreenshotTiles(a: StoreScreenshot, b: StoreScreenshot): number {
  return a.position - b.position ||
    a.localeTag.localeCompare(b.localeTag) ||
    a.imageType.localeCompare(b.imageType) ||
    Date.parse(a.firstRetrievedAt) - Date.parse(b.firstRetrievedAt) ||
    a.id.localeCompare(b.id);
}

function compareScreenshotTilesWithReference(a: StoreScreenshot, b: StoreScreenshot, referenceLocales: string[]): number {
  const aReference = referenceLocales.includes(a.localeTag);
  const bReference = referenceLocales.includes(b.localeTag);
  if (aReference !== bReference) {
    return aReference ? -1 : 1;
  }
  return compareScreenshotTiles(a, b);
}

function compareScreenshotGroupKeys(
  a: string,
  b: string,
  groupingMode: StoreGroupingMode | null,
  referenceLocales: string[]
): number {
  if (groupingMode === "position") {
    return Number(a) - Number(b);
  }
  const aReference = referenceLocales.includes(a);
  const bReference = referenceLocales.includes(b);
  if (aReference !== bReference) {
    return aReference ? -1 : 1;
  }
  return a.localeCompare(b);
}

function formatScreenshotTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function formatPlanPrice(price: BillingPlanPrice): string {
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (price.currency || "usd").toUpperCase()
  }).format(price.unitAmount / 100);
  const interval = price.recurringInterval || "month";
  const intervalCount = price.recurringIntervalCount || 1;
  const intervalLabel = intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;
  return `${amount} / ${intervalLabel}`;
}

type BillingPlanMetric = {
  label: string;
  value: string;
  unit?: string;
  muted?: boolean;
  hideValue?: boolean;
};

const billingPlanMetricLabels = ["Owned apps", "Screenshot uploads", "Storage"];

function billingPlanMetrics(plan: BillingPlan): BillingPlanMetric[] {
  return [
    billingPlanAppMetric(plan),
    {
      label: "Screenshot uploads",
      value: plan.uploadEnabled ? "Yes" : "No",
      unit: plan.uploadEnabled ? "direct uploads" : "store only",
      muted: !plan.uploadEnabled
    },
    billingPlanStorageMetric(plan)
  ];
}

function billingPlanAppMetric(plan: BillingPlan): BillingPlanMetric {
  if (plan.appLimit === null || plan.appLimit === undefined) {
    return {
      label: "Owned apps",
      value: "Unlimited",
      unit: "apps"
    };
  }

  return {
    label: "Owned apps",
    value: String(plan.appLimit),
    unit: plan.appLimit === 1 ? "app" : "apps"
  };
}

function billingPlanStorageMetric(plan: BillingPlan): BillingPlanMetric {
  if (!plan.uploadEnabled || plan.storageLimitMb <= 0) {
    return {
      label: "Storage",
      value: "—",
      muted: true
    };
  }

  if (plan.storageLimitMb >= 1024) {
    return {
      label: "Storage",
      value: String(Math.round(plan.storageLimitMb / 1024)),
      unit: "GB"
    };
  }

  return {
    label: "Storage",
    value: String(plan.storageLimitMb),
    unit: "MB"
  };
}

const BILLING_CURRENCY_ORDER = ["usd", "eur", "gbp", "pln"];
const EURO_BILLING_REGION_CODES = new Set([
  "AT",
  "BE",
  "CY",
  "DE",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PT",
  "SI",
  "SK"
]);
const EURO_BILLING_LANGUAGE_CODES = new Set(["de", "es", "fi", "fr", "it", "nl", "pt"]);

function collectAvailableBillingCurrencies(plans: BillingPlan[]): string[] {
  const currencies = new Set<string>();
  plans.forEach((plan) => {
    plan.prices.forEach((price) => {
      const currency = normalizeBillingCurrency(price.currency);
      if (currency) {
        currencies.add(currency);
      }
    });
  });

  return Array.from(currencies).sort(compareBillingCurrencies);
}

function preferredBillingCurrency(currencies: string[]): string | null {
  const available = new Set(currencies.map(normalizeBillingCurrency).filter(Boolean));
  for (const currency of browserBillingCurrencyHints()) {
    if (available.has(currency)) {
      return currency;
    }
  }

  for (const currency of BILLING_CURRENCY_ORDER) {
    if (available.has(currency)) {
      return currency;
    }
  }

  return currencies[0] ?? null;
}

function browserBillingCurrencyHints(): string[] {
  if (typeof navigator === "undefined") {
    return [];
  }

  const locales = navigator.languages?.length ? navigator.languages : [navigator.language].filter(Boolean);
  const hints: string[] = [];
  locales.forEach((locale) => {
    const { language, region } = parseLocaleParts(locale);
    if (region === "GB") {
      pushUnique(hints, "gbp");
    } else if (region === "PL") {
      pushUnique(hints, "pln");
    } else if (region === "US") {
      pushUnique(hints, "usd");
    } else if (region && EURO_BILLING_REGION_CODES.has(region)) {
      pushUnique(hints, "eur");
    } else if (!region && language && EURO_BILLING_LANGUAGE_CODES.has(language)) {
      pushUnique(hints, "eur");
    } else if (!region && language === "pl") {
      pushUnique(hints, "pln");
    }
  });

  return hints;
}

function parseLocaleParts(locale: string): { language: string | null; region: string | null } {
  const parts = locale.replace(/_/g, "-").split("-").filter(Boolean);
  const language = parts[0]?.toLowerCase() ?? null;
  const region = parts.slice(1).find((part) => /^[a-z]{2}$/i.test(part))?.toUpperCase() ?? null;
  return { language, region };
}

function pushUnique(values: string[], value: string) {
  if (!values.includes(value)) {
    values.push(value);
  }
}

function compareBillingCurrencies(a: string, b: string): number {
  const aIndex = BILLING_CURRENCY_ORDER.indexOf(a);
  const bIndex = BILLING_CURRENCY_ORDER.indexOf(b);
  if (aIndex !== -1 || bIndex !== -1) {
    return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
  }

  return a.localeCompare(b);
}

function normalizeBillingCurrency(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

function billingCurrencyLabel(currency: string): string {
  return normalizeBillingCurrency(currency).toUpperCase();
}

function billingTaxLabel(currency: string): string {
  const normalizedCurrency = normalizeBillingCurrency(currency);
  if (normalizedCurrency === "usd" || normalizedCurrency === "cad") {
    return "Plus applicable tax";
  }
  if (normalizedCurrency === "eur" || normalizedCurrency === "gbp" || normalizedCurrency === "pln") {
    return "Tax included";
  }

  return "Tax shown at checkout";
}

function billingSubscriptionCatalogCurrency(plans: BillingPlan[], subscription: BillingSubscription | null): string {
  const stripePriceId = subscription?.stripePriceId;
  if (!stripePriceId) {
    return "";
  }

  for (const plan of plans) {
    const price = plan.prices.find((candidate) => candidate.stripePriceId === stripePriceId);
    const currency = normalizeBillingCurrency(price?.currency);
    if (currency) {
      return currency;
    }
  }

  return "";
}

function formatStorageLimit(value: number): string {
  if (value >= 1024) {
    return `${Math.round(value / 1024)} GB`;
  }
  return `${value} MB`;
}

function formatStorageUsage(account: BillingAccount): string {
  return `${formatBytes(account.storageUsedBytes)} / ${formatStorageLimit(account.storageLimitMb)}`;
}

function shouldShowBillingStorageUsage(account: BillingAccount): boolean {
  return account.uploadEnabled && account.storageLimitMb > 0;
}

function formatPlanAppLimit(value?: number | null): string {
  return value === null || value === undefined ? "Unlimited" : String(value);
}

function formatBytes(value: number): string {
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${value} B`;
}

function formatAdminStripePrice(price: AdminStripePrice): string {
  if (price.unitAmount === null || price.unitAmount === undefined) {
    return price.priceType;
  }
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: price.currency.toUpperCase()
  }).format(price.unitAmount / 100);
  if (!price.recurringInterval) {
    return amount;
  }
  const intervalCount = price.recurringIntervalCount && price.recurringIntervalCount > 1 ? `${price.recurringIntervalCount} ` : "";
  return `${amount} / ${intervalCount}${price.recurringInterval}`;
}

function uniqueImages(images: StoreScreenshot[]): StoreScreenshot[] {
  const seen = new Set<string>();
  return images.filter((image) => {
    const key = `${image.platform}:${image.localeTag}:${image.imageType}:${canonicalImageKey(image.imageUrl)}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function isIconLikeImage(image: StoreScreenshot, appIconKeys: Set<string>): boolean {
  if (appIconKeys.has(canonicalImageKey(image.imageUrl))) {
    return true;
  }
  if (
    image.imageUrl.includes("/AppIcon-") ||
    image.imageUrl.includes("/Features") ||
    image.imageUrl.includes("Placeholder.") ||
    image.imageUrl.includes("/1200x630")
  ) {
    return true;
  }
  if (image.width && image.height) {
    return !isLikelyScreenshotDimensions(image.width, image.height);
  }
  const appleDimensions = image.imageUrl.match(/\/([0-9]+)x([0-9]+)[A-Za-z0-9-]*\.(jpg|jpeg|png|webp)/);
  if (appleDimensions) {
    const width = Number(appleDimensions[1]);
    const height = Number(appleDimensions[2]);
    return !isLikelyScreenshotDimensions(width, height);
  }
  const googleDimensions = image.imageUrl.match(/=w([0-9]+)-h([0-9]+)/);
  if (googleDimensions) {
    const width = Number(googleDimensions[1]);
    const height = Number(googleDimensions[2]);
    return !isLikelyScreenshotDimensions(width, height);
  }
  return false;
}

function canonicalImageKey(url: string): string {
  if (url.startsWith("https://play-lh.googleusercontent.com/")) {
    return url.split("=")[0];
  }
  if (url.includes("mzstatic.com/image/thumb/")) {
    const lastSlash = url.lastIndexOf("/");
    return lastSlash > 0 ? url.slice(0, lastSlash) : url;
  }
  return url.split("?")[0];
}

function isLikelyScreenshotDimensions(width: number, height: number): boolean {
  const shorter = Math.min(width, height);
  const longer = Math.max(width, height);
  if (shorter < 250 || longer < 450) {
    return false;
  }
  const aspectRatio = longer / shorter;
  return aspectRatio >= 1.25 && aspectRatio <= 3;
}

function normalizeLocaleInput(value: string): string | null {
  const parts = value.trim().replace(/_/g, "-").split("-");
  if (parts.length !== 2) {
    return null;
  }
  const language = parts[0].toLowerCase();
  const region = /^[0-9]{3}$/.test(parts[1]) ? parts[1] : parts[1].toUpperCase();
  if (!/^[a-z]{2,3}$/.test(language) || !/^([A-Z]{2}|[0-9]{3})$/.test(region)) {
    return null;
  }
  return `${language}-${region}`;
}

function languageNameForLocale(locale: string | null): string | null {
  const normalizedLocale = locale ? normalizeLocaleInput(locale) : null;
  if (!normalizedLocale) {
    return null;
  }
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    const displayName = displayNames.of(normalizedLocale)?.trim();
    if (!displayName || displayName.toLowerCase() === normalizedLocale.toLowerCase()) {
      return null;
    }
    const baseName = displayNames.of(normalizedLocale.split("-")[0])?.trim();
    const languageCode = normalizedLocale.split("-")[0];
    const shouldKeepRegion = languageCode === "es" || languageCode === "pt";
    const conciseName = shouldKeepRegion
      ? displayName
      : (baseName && displayName.startsWith(`${baseName} (`) ? baseName : displayName).replace(/\s*\([^)]*\)/g, "");
    return conciseName.charAt(0).toUpperCase() + conciseName.slice(1);
  } catch {
    return null;
  }
}

function welcomeLocaleDisplayName(locale: string): string {
  return languageNameForLocale(locale) ?? locale;
}

function buildLocaleSuggestions(): string[] {
  return Array.from(new Set([...commonStoreLocales, ...storeSupportedLocales]));
}

const commonStoreLocales = [
  "en-US",
  "en-GB",
  "en-CA",
  "en-AU",
  "de-DE",
  "fr-FR",
  "es-ES",
  "es-MX",
  "es-US",
  "it-IT",
  "ja-JP",
  "ko-KR",
  "pt-BR",
  "pt-PT",
  "zh-CN",
  "zh-TW",
  "nl-NL",
  "sv-SE",
  "da-DK",
  "fi-FI",
  "no-NO",
  "pl-PL",
  "tr-TR",
  "ru-RU",
  "ar-SA",
  "hi-IN",
  "id-ID",
  "th-TH",
  "vi-VN"
];

const storeSupportedLocales = [
  "af-ZA",
  "am-ET",
  "ar-SA",
  "as-IN",
  "az-AZ",
  "be-BY",
  "bg-BG",
  "bn-BD",
  "bs-BA",
  "ca-ES",
  "cs-CZ",
  "da-DK",
  "de-DE",
  "el-GR",
  "en-AU",
  "en-CA",
  "en-GB",
  "en-IN",
  "en-SG",
  "en-US",
  "en-ZA",
  "es-419",
  "es-ES",
  "es-MX",
  "es-US",
  "et-EE",
  "eu-ES",
  "fa-IR",
  "fi-FI",
  "fil-PH",
  "fr-CA",
  "fr-FR",
  "gl-ES",
  "gu-IN",
  "he-IL",
  "hi-IN",
  "hr-HR",
  "hu-HU",
  "hy-AM",
  "id-ID",
  "is-IS",
  "it-IT",
  "ja-JP",
  "ka-GE",
  "kk-KZ",
  "km-KH",
  "kn-IN",
  "ko-KR",
  "ky-KG",
  "lo-LA",
  "lt-LT",
  "lv-LV",
  "mk-MK",
  "ml-IN",
  "mn-MN",
  "mr-IN",
  "ms-MY",
  "my-MM",
  "ne-NP",
  "nl-NL",
  "no-NO",
  "or-IN",
  "pa-IN",
  "pl-PL",
  "pt-BR",
  "pt-PT",
  "ro-RO",
  "ru-RU",
  "si-LK",
  "sk-SK",
  "sl-SI",
  "sq-AL",
  "sr-RS",
  "sv-SE",
  "sw-TZ",
  "ta-IN",
  "te-IN",
  "th-TH",
  "tr-TR",
  "uk-UA",
  "ur-PK",
  "uz-UZ",
  "vi-VN",
  "zh-CN",
  "zh-HK",
  "zh-TW",
  "zu-ZA"
];

const localeSuggestions = buildLocaleSuggestions();

function SavedAppWorkspace(props: {
  app: AppSummary;
  session: SessionState;
  user: UserProfile | null;
  onUserUpdated: (user: UserProfile) => void;
}) {
  const reviewLinkConfig = useMemo(readReviewLinkConfigFromUrl, []);
  const [screenshots, setScreenshots] = useState<StoreScreenshot[]>([]);
  const [storeLocales, setStoreLocales] = useState<string[]>([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const [storeMessage, setStoreMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingScreenshots(true);
    setStoreMessage(null);
    listStoreScreenshots(props.app.id, props.session)
      .then((nextScreenshots) => {
        if (!cancelled) {
          setScreenshots((current) => preserveReusableScreenshotImageUrls(current, nextScreenshots));
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setStoreMessage(error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingScreenshots(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.app.id, props.session]);

  async function refreshSignedScreenshotUrls(): Promise<StoreScreenshot[]> {
    const nextScreenshots = await listStoreScreenshots(props.app.id, props.session);
    const mergedScreenshots = preserveReusableScreenshotImageUrls(screenshots, nextScreenshots);
    setScreenshots(mergedScreenshots);
    return mergedScreenshots;
  }

  function updateCommentCounts(screenshotId: string, comments: StoreScreenshotComment[]) {
    const commentCounts = storeScreenshotCommentCountPatch(comments);
    setScreenshots((current) =>
      current.map((screenshot) => screenshot.id === screenshotId ? { ...screenshot, ...commentCounts } : screenshot)
    );
  }

  return (
    <section className="workspace-grid owner-grid">
      <StoreScreenshotGrid
        app={props.app}
        session={props.session}
        requestedLocales={storeLocales.length ? storeLocales : reviewLinkConfig.locales}
        referenceLocale={props.app.referenceLocale ?? null}
        screenshots={screenshots}
        userLanguages={props.user?.languages ?? []}
        currentUser={props.user}
        currentUserId={props.user?.id ?? null}
        loading={loadingScreenshots}
        message={storeMessage}
        refreshing={false}
        canRefresh={false}
        canAddLocale={false}
        showReviewerIntro
        reviewLinkConfig={reviewLinkConfig}
        showRefresh={false}
        canEditDescriptions={false}
        onAddLocale={(locale) => {
          setStoreLocales((current) => (current.includes(locale) ? current : [...current, locale].sort()));
        }}
        onCommentsChanged={updateCommentCounts}
        onRefreshSignedScreenshotUrls={refreshSignedScreenshotUrls}
        onUserUpdated={props.onUserUpdated}
      />
    </section>
  );
}

function EmptyWorkspace(props: { onCreateApp: () => void }) {
  return (
    <section className="empty-workspace">
      <p className="eyebrow">First app</p>
      <h1>Create your first app workspace</h1>
      <p>Apps hold store-specific screenshot sets, review links, comments, and API uploads.</p>
      <div className="empty-workspace-actions">
        <button className="primary-button" type="button" onClick={props.onCreateApp}>
          <PlusIcon />
          Create app workspace
        </button>
      </div>
    </section>
  );
}

function AssetPlanningCard() {
  const rows = [
    ["iPhone screenshots", "App Store"],
    ["iPad screenshots", "App Store"],
    ["App Clip cards", "App Store"],
    ["Phone screenshots", "Google Play"],
    ["Feature graphic", "Google Play"]
  ];

  return (
    <div className="planning-card">
      <div>
        <p className="eyebrow">Asset structure</p>
        <h2>Ready for platform-specific artwork</h2>
      </div>
      <div className="planning-list">
        {rows.map(([label, store]) => (
          <div className="planning-row" key={label}>
            <strong>{label}</strong>
            <span>{store}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard(props: { label: string; value: string; detail: string; onOpen?: () => void }) {
  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (!props.onOpen || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }
    event.preventDefault();
    props.onOpen();
  }

  return (
    <article
      className={`metric-card ${props.onOpen ? "metric-card-clickable" : ""}`}
      role={props.onOpen ? "button" : undefined}
      tabIndex={props.onOpen ? 0 : undefined}
      onClick={props.onOpen}
      onKeyDown={handleKeyDown}
    >
      <span>{props.label}</span>
      <strong>{props.value}</strong>
      <p>{props.detail}</p>
      {props.onOpen ? (
        <span className="metric-card-chevron" aria-hidden="true">
          <ChevronRightIcon />
        </span>
      ) : null}
    </article>
  );
}

function LocalesCard(props: {
  value: string;
  existingLocales: string[];
  disabled: boolean;
  onAddLocale: (locale: string) => Promise<void>;
}) {
  return (
    <article className="metric-card locales-card">
      <span>Locales</span>
      <strong>{props.value}</strong>
      <div className="locales-card-footer">
        <p>Locales added to this workspace.</p>
        <AddLocaleDropdown
          disabled={props.disabled}
          existingLocales={props.existingLocales}
          onAddLocale={props.onAddLocale}
        />
      </div>
    </article>
  );
}

function AddLocaleDropdown(props: {
  disabled: boolean;
  existingLocales: string[];
  onAddLocale: (locale: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState<number | null>(null);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const [addingLocale, setAddingLocale] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const existingLocaleSet = useMemo(() => new Set(props.existingLocales), [props.existingLocales.join("|")]);
  const normalizedQuery = normalizeLocaleInput(query);
  const trimmedQuery = query.toLowerCase().trim();
  const canAddTypedLocale = Boolean(normalizedQuery && !existingLocaleSet.has(normalizedQuery));
  const suggestedLocales = localeSuggestions
    .filter((locale) => !existingLocaleSet.has(locale))
    .filter((locale) => locale !== normalizedQuery || !canAddTypedLocale)
    .filter((locale) => !trimmedQuery || welcomeLocaleDisplayName(locale).toLowerCase().includes(trimmedQuery) || locale.toLowerCase().includes(trimmedQuery));
  const options = [
    ...(canAddTypedLocale && normalizedQuery ? [normalizedQuery] : []),
    ...suggestedLocales
  ].map((locale) => ({ label: welcomeLocaleDisplayName(locale), value: locale }));
  const optionsSignature = options.map((option) => option.value).join("|");
  const activeOptionIndex = hoveredOptionIndex ?? highlightedOptionIndex;

  useEffect(() => {
    setHighlightedOptionIndex(options.length === 1 ? 0 : null);
    setHoveredOptionIndex(null);
  }, [open, options.length, optionsSignature, query]);

  useEffect(() => {
    if (open) {
      focusDropdownSearchInput(searchInputRef.current);
    }
  }, [open]);

  async function addLocale(locale: string) {
    setOpen(false);
    setQuery("");
    setHighlightedOptionIndex(null);
    setHoveredOptionIndex(null);
    setAddingLocale(locale);
    try {
      await props.onAddLocale(locale);
    } finally {
      setAddingLocale(null);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!options.length) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? 0 : (current + 1) % options.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!options.length) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? options.length - 1 : (current - 1 + options.length) % options.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const activeOption = activeOptionIndex === null ? undefined : options[activeOptionIndex];
      if (activeOption) {
        void addLocale(activeOption.value);
      }
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="add-locale-dropdown" ref={ref}>
      <button
        className="primary-button compact"
        type="button"
        disabled={props.disabled || Boolean(addingLocale)}
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (next) {
              setQuery("");
              setHighlightedOptionIndex(null);
              setHoveredOptionIndex(null);
            }
            return next;
          });
        }}
      >
        <PlusIcon />
        {addingLocale ? "Adding..." : "Add locale"}
      </button>
      {open ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="locale-dropdown-menu add-locale-menu"
          minWidth={260}
          onDismiss={() => setOpen(false)}
          onMouseLeave={() => setHoveredOptionIndex(null)}
          open={open}
        >
          <input
            className="locale-dropdown-search"
            ref={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search locale"
          />
          <div className="locale-dropdown-options">
            {options.map((option, index) => {
              const highlighted = activeOptionIndex === index;
              return (
                <button
                  className={highlighted ? "highlighted" : ""}
                  key={option.value}
                  type="button"
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  onMouseEnter={() => setHoveredOptionIndex(index)}
                  onClick={() => void addLocale(option.value)}
                >
                  <span>{option.label}</span>
                  <span>{option.value}</span>
                </button>
              );
            })}
          </div>
          {options.length === 0 ? <div className="menu-empty">No matching locales.</div> : null}
        </DropdownPortal>
      ) : null}
    </div>
  );
}

function ReviewLinksCard(props: {
  value: string;
  onCreate: () => void;
  onOpen: () => void;
}) {
  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    if (event.target instanceof HTMLElement && event.target.closest("button")) {
      return;
    }
    event.preventDefault();
    props.onOpen();
  }

  return (
    <article
      className="metric-card review-links-card metric-card-clickable"
      role="button"
      tabIndex={0}
      onClick={props.onOpen}
      onKeyDown={handleKeyDown}
    >
      <div className="review-links-card-header">
        <span>Review links</span>
        <span className="metric-card-chevron review-links-card-chevron" aria-hidden="true">
          <ChevronRightIcon />
        </span>
      </div>
      <strong>{props.value}</strong>
      <div className="review-links-card-footer">
        <p>Generated links and usage analytics.</p>
        <button
          className="primary-button compact"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            props.onCreate();
          }}
        >
          <LinkIcon />
          Create link
        </button>
      </div>
    </article>
  );
}

function ReviewerIntroCard(props: {
  appName: string;
  loading: boolean;
  title: string;
  referenceImage: StoreScreenshot | null;
  reviewImage: StoreScreenshot | null;
  localeOptions: string[];
  selectedLocale: string | null;
  localePlaceholder?: string;
  cropFocus?: ReviewLinkCropFocus;
  preview?: boolean;
  collapsed?: boolean;
  collapsible?: boolean;
  canStart: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onLocaleChange: (locale: string) => void;
  onStartReview: () => void;
}) {
  const collapsed = Boolean(props.collapsed);
  const title = collapsed ? "How it works?" : props.title;

  return (
    <section className={`reviewer-intro-card ${props.preview ? "preview" : ""} ${props.collapsible ? "collapsible" : ""} ${collapsed ? "collapsed" : ""}`}>
      {props.collapsible ? (
        <button
          className="reviewer-intro-collapse-button"
          type="button"
          aria-label={collapsed ? "Expand welcome section" : "Collapse welcome section"}
          aria-expanded={!collapsed}
          onClick={() => props.onCollapsedChange?.(!collapsed)}
        >
          {collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
        </button>
      ) : null}
      <div className="reviewer-intro-main">
        <h1>{title}</h1>
        {!collapsed ? (
          <div className="reviewer-preview-row" aria-label="Review flow preview">
            <ReviewerPreviewShot label="Reference" image={props.referenceImage} cropFocus={props.cropFocus} />
            <span className="reviewer-preview-chevron" aria-hidden="true">
              <ChevronRightIcon />
            </span>
            <ReviewerPreviewShot label="Under review" image={props.reviewImage} cropFocus={props.cropFocus} />
          </div>
        ) : null}
      </div>
      {!collapsed ? (
        <div className="reviewer-intro-actions">
          <div className="reviewer-start-copy">
            <span>
              Open{" "}
              <span className="welcome-locale-picker-suffix">
                <WelcomeLocalePicker
                  locales={props.localeOptions}
                  value={props.selectedLocale}
                  placeholder={props.localePlaceholder}
                  onChange={props.onLocaleChange}
                />
                screenshot
              </span>
            </span>
            <span>and mark what needs work.</span>
          </div>
          <button className="primary-button" type="button" disabled={!props.canStart || props.loading} onClick={props.onStartReview}>
            {props.loading ? "Loading..." : "Start review"}
            <ChevronRightIcon />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function WelcomeLocalePicker(props: {
  locales: string[];
  value: string | null;
  placeholder?: string;
  onChange: (locale: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState<number | null>(null);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const ref = useRef<HTMLSpanElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedQuery = query.toLowerCase().trim();
  const options = props.locales
    .map((locale) => ({ label: welcomeLocaleDisplayName(locale), value: locale }))
    .filter((option) =>
      !trimmedQuery ||
      option.label.toLowerCase().includes(trimmedQuery) ||
      option.value.toLowerCase().includes(trimmedQuery)
    );
  const optionSignature = options.map((option) => option.value).join("|");
  const activeOptionIndex = hoveredOptionIndex ?? highlightedOptionIndex;
  const selectedLabel = props.value
    ? welcomeLocaleDisplayName(props.value)
    : props.placeholder ?? (props.locales[0] ? welcomeLocaleDisplayName(props.locales[0]) : "Locale");

  useEffect(() => {
    setHighlightedOptionIndex(options.length === 1 ? 0 : null);
    setHoveredOptionIndex(null);
  }, [open, optionSignature, options.length, query]);

  useEffect(() => {
    if (!open || hoveredOptionIndex !== null || highlightedOptionIndex === null || options.length === 0) {
      return;
    }
    window.requestAnimationFrame(() => optionRefs.current[highlightedOptionIndex]?.scrollIntoView({ block: "nearest" }));
  }, [highlightedOptionIndex, hoveredOptionIndex, open, options.length]);

  useEffect(() => {
    if (open) {
      focusDropdownSearchInput(searchInputRef.current);
    }
  }, [open]);

  function selectLocale(locale: string) {
    if (locale !== props.value) {
      props.onChange(locale);
    }
    setOpen(false);
    setQuery("");
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (options.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? 0 : (current + 1) % options.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length === 0) {
        return;
      }
      setHoveredOptionIndex(null);
      setHighlightedOptionIndex((current) => current === null ? options.length - 1 : (current - 1 + options.length) % options.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const activeOption = activeOptionIndex === null ? undefined : options[activeOptionIndex];
      if (activeOption) {
        selectLocale(activeOption.value);
      }
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <span className="welcome-locale-picker" ref={ref}>
      <button
        className={`welcome-locale-picker-trigger ${open ? "open" : ""}`}
        type="button"
        disabled={props.locales.length === 0}
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (next) {
              setQuery("");
              setHighlightedOptionIndex(null);
              setHoveredOptionIndex(null);
            }
            return next;
          });
        }}
      >
        <span>{selectedLabel}</span>
        <ChevronDownIcon />
      </button>
      {open ? (
        <DropdownPortal
          align="start"
          anchorRef={ref}
          className="locale-dropdown-menu welcome-locale-menu"
          minWidth={240}
          onDismiss={() => setOpen(false)}
          onMouseLeave={() => setHoveredOptionIndex(null)}
          open={open}
        >
          <input
            className="locale-dropdown-search"
            ref={searchInputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search locale"
          />
          <div className="locale-dropdown-options">
            {options.map((option, index) => {
              const selected = option.value === props.value;
              const highlighted = activeOptionIndex === index;
              return (
                <button
                  className={`${selected ? "active" : ""} ${highlighted ? "highlighted" : ""}`.trim()}
                  key={option.value}
                  type="button"
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  onMouseEnter={() => setHoveredOptionIndex(index)}
                  onClick={() => selectLocale(option.value)}
                >
                  <span>{option.label}</span>
                  <small>{option.value}</small>
                </button>
              );
            })}
          </div>
          {options.length === 0 ? <div className="menu-empty">No matching locales.</div> : null}
        </DropdownPortal>
      ) : null}
    </span>
  );
}

function ReviewerPreviewShot(props: {
  label: string;
  image: StoreScreenshot | null;
  cropFocus?: ReviewLinkCropFocus;
}) {
  const aspectRatio = props.image ? getWelcomePreviewAspectRatioCandidate(props.image) : 3 / 4;
  const cropFocus = normalizeReviewLinkCropFocus(props.cropFocus ?? defaultReviewLinkCropFocus);
  const hasVerticalCrop = props.image ? hasWelcomePreviewVerticalCrop(props.image, aspectRatio) : false;
  const topCropped = hasVerticalCrop && cropFocus > 2;
  const bottomCropped = hasVerticalCrop && cropFocus < 98;
  const topMaskStop = topCropped ? "18%" : "0%";
  const bottomMaskStop = bottomCropped ? "82%" : "100%";
  const cropMask = `linear-gradient(to bottom, ${topCropped ? "transparent" : "#000"} 0%, #000 ${topMaskStop}, #000 ${bottomMaskStop}, ${bottomCropped ? "transparent" : "#000"} 100%)`;
  const cropStyle = {
    aspectRatio,
    "--welcome-crop-focus": `${cropFocus}%`,
    "--welcome-crop-offset": `${-cropFocus}%`,
    "--welcome-crop-mask": cropMask,
    "--welcome-preview-top-border-width": topCropped ? "0px" : "1px",
    "--welcome-preview-bottom-border-width": bottomCropped ? "0px" : "1px",
    "--welcome-preview-top-border-color": topCropped ? "transparent" : "var(--line)",
    "--welcome-preview-bottom-border-color": bottomCropped ? "transparent" : "var(--line)",
    "--welcome-preview-top-radius": topCropped ? "0px" : "var(--radius-md)",
    "--welcome-preview-bottom-radius": bottomCropped ? "0px" : "var(--radius-md)"
  } as CSSProperties & Record<
    "--welcome-crop-focus" | "--welcome-crop-offset" | "--welcome-crop-mask" | "--welcome-preview-top-border-width" | "--welcome-preview-bottom-border-width" | "--welcome-preview-top-border-color" | "--welcome-preview-bottom-border-color" | "--welcome-preview-top-radius" | "--welcome-preview-bottom-radius",
    string
  >;
  return (
    <figure className="reviewer-preview-shot">
      <figcaption>{props.label}</figcaption>
      <div
        className={`reviewer-preview-image ${props.image ? "has-image" : ""}`}
        style={cropStyle}
      >
        {props.image ? (
          <div className="reviewer-preview-image-crop">
            <img
              alt={`${props.label}: ${props.image.localeTag} ${props.image.imageType}`}
              src={welcomePreviewImageUrl(props.image)}
              referrerPolicy="no-referrer"
            />
          </div>
        ) : (
          <span className="reviewer-preview-empty">No screenshot</span>
        )}
      </div>
    </figure>
  );
}

function DropdownCopy(props: { eyebrow: string; value: string }) {
  return (
    <span className="menu-trigger-copy">
      <span className="menu-trigger-eyebrow">{props.eyebrow}</span>
      <strong>{props.value}</strong>
    </span>
  );
}

function ScreenshotReviewLogo() {
  return (
    <svg aria-hidden="true" viewBox="0 0 512 512" focusable="false">
      <path fill="currentColor" d="M144,480C152.831,480 160,487.169 160,496C160,504.831 152.831,512 144,512L48,512C21.508,512 0,490.492 0,464L0,368C0,359.169 7.169,352 16,352C24.831,352 32,359.169 32,368L32,464C32,472.831 39.169,480 48,480L144,480Z" />
      <path fill="currentColor" d="M480,368C480,359.169 487.169,352 496,352C504.831,352 512,359.169 512,368L512,464C512,490.492 490.492,512 464,512L368,512C359.169,512 352,504.831 352,496C352,487.169 359.169,480 368,480L464,480C472.831,480 480,472.831 480,464L480,368Z" />
      <path fill="currentColor" d="M368,32C359.169,32 352,24.831 352,16C352,7.169 359.169,0 368,0L464,0C490.492,0 512,21.508 512,48L512,144C512,152.831 504.831,160 496,160C487.169,160 480,152.831 480,144L480,48C480,39.169 472.831,32 464,32L368,32Z" />
      <path fill="currentColor" d="M32,144C32,152.831 24.831,160 16,160C7.169,160 0,152.831 0,144L0,48C0,21.508 21.508,0 48,0L144,0C152.831,0 160,7.169 160,16C160,24.831 152.831,32 144,32L48,32C39.169,32 32,39.169 32,48L32,144Z" />
      <path fill="currentColor" stroke="currentColor" strokeWidth="2.1" d="M224.247,405.827C239.866,405.827 251.992,400.277 260.213,388.152L407.779,170.296C413.534,161.87 416,153.033 416,145.223C416,123.026 398.325,106.173 375.306,106.173C359.892,106.173 349.822,111.722 340.368,126.52L223.63,307.997L166.289,243.463C158.273,234.42 148.82,230.104 136.077,230.104C113.059,230.104 96,246.957 96,269.359C96,279.635 98.672,287.651 107.509,297.516L190.13,391.029C199.173,401.305 210.271,405.827 224.247,405.827Z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="m6 15 6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="m15 18-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="m9 18 6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 11a5 5 0 0 0-7.1 0l-2 2a5 5 0 0 0 7.1 7.1l1.1-1.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 7l1 14h10l1-14M9 7V4h6v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StorefrontIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M4 10h16L18.7 4H5.3L4 10Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M5 10v10h14V10" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M4 10c0 1.3 1 2.3 2.3 2.3S8.7 11.3 8.7 10c0 1.3 1 2.3 2.3 2.3s2.3-1 2.3-2.3c0 1.3 1 2.3 2.3 2.3S20 11.3 20 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M12 15V5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path d="m7 9 5-5 5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 20h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function CommentIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M7 18.5 3.5 21V6.5A3.5 3.5 0 0 1 7 3h10a3.5 3.5 0 0 1 3.5 3.5V15A3.5 3.5 0 0 1 17 18.5H7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="m5 12 4 4 10-10" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M12 8v5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M12 17h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export default App;
